/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, no-use-before-define */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { faPenToSquare, faCheck, faArrowLeft, faCamera, faTrash, faUndo, faRedo, faTimes, faPlus, faSync, faArrowUp, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Check } from 'lucide-react';
import { StandardSearch } from '../styles/standardStyles';
import { applyIngredientNameChangeToDrafts, clearRecipeReviewDraft, getRecipeReviewDraft, hydrateRecipeReviewDrafts, mergeRecipesWithLocalReviewDrafts, saveRecipeReviewDraft } from '../utils/recipeReviewDrafts';
import { sortBySearchPriority } from '../utils/searchPriority';
import { addedFromSalesLabel, isPendingAddedFromSales } from '../utils/addedFromSales';
import UnitMappingService from '../services/unitMappingService';
import RecipesService from '../services/recipesService';
import MenuItemSelector from './MenuItemSelector';
import InventoryService from '../services/inventoryService';
import RecipeLibraryService from '../services/recipeLibraryService';
import UnitChangeConfirmationModal from './UnitChangeConfirmationModal';
import SmartUnitDropdown from './SmartUnitDropdown';
import CrossFamilyConversionModal from './CrossFamilyConversionModal';
import InventoryItemEditMenu from './InventoryItemEditMenu';
import { normalizeToBase, getUnitFamily, isCrossFamilyChange, formatQuantity, toBaseUnitOrKeep, toBaseUnitSymbol, toCanonicalUnit, convert, equivalentIn } from '../utils/unitDisplay';
import { mergeImportRows, addPerPlateOntoRow } from '../utils/recipeImportMerge';
import { buildPerPlatePayload } from '../utils/recipeSavePayload';

import FeaturesService from '../services/FeaturesService';
import ComplimentaryItemsPage from './comps';
import CustomImageUploadModal from './CustomImageUploadModal';
import PreparationCreatorModal from './PreparationCreatorModal';
import MenuOrganization from './menuorganization';
import http from '../services/httpClient';
import { scrollToTop } from '../utils/scroll';
import './itemmap.css';
import './comps.css';
import './NCItemsPage.css';
import { FaChevronDown, FaTimes } from 'react-icons/fa';

// SP9 (B4): render-path logging is a no-op outside development — each of the
// hundreds of former console.log calls cost real time on low-end devices.
const debugLog = process.env.NODE_ENV === 'development' ? console.log.bind(console) : () => {};


// Helpers: name normalization and Levenshtein distance (module scope so they are always defined)
function normalizeName(s) {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(th|dh|bh)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshtein(a, b) {
  a = normalizeName(a); b = normalizeName(b);
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[m][n];
}

// Normalize complimentary items to always return array of strings
function normalizeComplimentaryItems(items) {
  if (!items || !Array.isArray(items)) return [];
  return items.map(item => {
    if (typeof item === 'string') return item;
    if (typeof item === 'object' && item.name) return item.name;
    return String(item);
  });
}

// Normalize add-ons to always return array of strings
function normalizeAddOns(items) {
  if (!items || !Array.isArray(items)) return [];
  return items.map(item => {
    if (typeof item === 'string') return item;
    if (typeof item === 'object' && item.name) return item.name;
    return String(item);
  });
}

function getUnitCategory(unit) {
  if (!unit) return 'unknown';
  const unitLower = String(unit).toLowerCase().trim();

  const weightUnits = ['g', 'kg', 'mg', 'oz', 'lb', 'pound', 'gram', 'kilogram', 'ton', 'tonne'];
  if (weightUnits.includes(unitLower)) return 'weight';

  const volumeUnits = [
    'ml', 'l', 'cl', 'dl', 'liter', 'litre', 'milliliter', 'millilitre',
    'cup', 'tbsp', 'tsp', 'tablespoon', 'teaspoon', 'fl oz', 'fluid ounce',
    'gallon', 'gal', 'quart', 'qt', 'pint', 'pt', 'bowl', 'glass'
  ];
  if (volumeUnits.includes(unitLower)) return 'volume';

  const countUnits = ['pcs', 'pieces', 'unit', 'units', 'count', 'nos', 'number'];
  if (countUnits.includes(unitLower)) return 'count';

  return 'unknown';
}

// Normalize recipe objects so we know whether a real stored image exists.
// "image_url" comes from DB (may be null) while "image" can be a fallback placeholder.
const withImageMeta = (recipe) => {
  if (!recipe) return null;
  const storedImage = typeof recipe.image_url === 'string' && recipe.image_url.trim().length > 0
    ? recipe.image_url.trim()
    : null;
  const fallbackImage = recipe.image || recipe.fallbackImage || null;
  return {
    ...recipe,
    image: storedImage,
    fallbackImage
  };
};

const DUPLICATE_OVERRIDE_OPTIONS = [
  { value: 'Different Brand', label: 'Different Brand' },
  { value: 'Different Quality', label: 'Different Quality' },
  { value: 'Intentional Variant', label: 'Intentional Variant' },
  { value: 'Other', label: 'Other' }
];

// Custom, fully-stylable select component
const NiceSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  disabled = false,
  fullWidth = false,
  style,
  onAddClick,
  className = '',
  openAbove = false,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(() => Math.max(0, options.findIndex(o => o.value === value)));
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    const idx = options.findIndex(o => o.value === value);
    if (idx >= 0) setActiveIndex(idx);
  }, [value, options]);

  const selectByIndex = (idx) => {
    const opt = options[idx];
    if (!opt) return;
    if (opt.value === 'add') {
      onAddClick && onAddClick();
    } else {
      onChange && onChange(opt.value);
    }
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActiveIndex(i => Math.min(options.length - 1, (i ?? -1) + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setActiveIndex(i => Math.max(0, (i ?? options.length) - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); selectByIndex(activeIndex >= 0 ? activeIndex : 0); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const selected = options.find(o => o.value === value);

  return (
    <div
      ref={wrapRef}
      className={`nice-select-custom${open ? ' open' : ''}${disabled ? ' disabled' : ''} ${className}`}
      style={{ width: fullWidth ? '100%' : undefined, ...style }}
    >
      <div
        className="ns-control"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={onKeyDown}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.label ?? selected?.value ?? placeholder}
        </span>
        <span className="ns-arrow" />
      </div>
      {open && !disabled && (
        <div className={`ns-menu${openAbove ? ' open-above' : ''}`} role="listbox">
          {options.map((opt, idx) => (
            <div
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`ns-option${opt.value === value ? ' selected' : ''}${idx === activeIndex ? ' active' : ''}`}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={(e) => { e.preventDefault(); }}
              onClick={(e) => {
                if (opt.value === 'add') {
                  e.stopPropagation();
                  onAddClick && onAddClick();
                } else {
                  selectByIndex(idx);
                }
              }}
            >
              {opt.label ?? opt.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Unit Family Helpers ───────────────────────────────────────────────────
const UNIT_FAMILIES = {
  weight: ['g', 'kg'],
  volume: ['ml', 'L'],
  count: ['pcs'],
};
const getUnitFamilyMembers = (unit) => {
  const normalized = String(unit || '').toLowerCase().trim();
  for (const [, members] of Object.entries(UNIT_FAMILIES)) {
    if (members.map(m => m.toLowerCase()).includes(normalized)) return members;
  }
  return null;
};
const ALL_BASE_UNITS = ['g', 'kg', 'ml', 'L', 'pcs'];

/**
 * Build smart unit options for an ingredient dropdown.
 * - New item (no existing unit or 'New Ingredient'): show all 5 units
 * - Existing item: show same-family units + '+ Other Units' separator + remaining cross-family units (initially hidden)
 * @param {string} currentUnit - The item's current unit
 * @param {boolean} isNewItem - Whether the item is new (not yet in inventory)
 * @param {boolean} showCrossFamily - Whether cross-family units should be shown
 * @param {Array} extraKitchenTools - Extra ingredient-specific kitchen tool units
 * @returns {Array} options for SearchableSelect
 */
const getSmartUnitOptions = (currentUnit, isNewItem, showCrossFamily = false, extraKitchenTools = []) => {
  // New item: show all 5 units
  if (isNewItem || !currentUnit) {
    return ALL_BASE_UNITS.map(u => ({ value: u, label: u }));
  }

  // Existing item: show same-family units
  const familyMembers = getUnitFamilyMembers(currentUnit);
  const sameFamilyOpts = (familyMembers || [currentUnit]).map(u => ({ value: u, label: u }));

  // Add any kitchen tool extras
  const seen = new Set(sameFamilyOpts.map(o => o.value.toLowerCase()));
  for (const tool of extraKitchenTools) {
    const v = (tool.value || '').toLowerCase();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    sameFamilyOpts.push(tool);
  }

  // Show "+ Other Units" button (no cross-family options in this dropdown)
  sameFamilyOpts.push({ value: '__show_other__', label: '+ Other Units' });

  return sameFamilyOpts;
};

// Searchable dropdown (with inline filter input) used for unit selection
const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Search unit...',
  disabled = false,
  style,
  // Show fewer rows by default to reduce dropdown height
  visibleCount = 3,
  onAddClick,
  isOpen,
  onToggle,
  openUpward,
}) => {
  const [open, setOpen] = useState(isOpen || false);

  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(isOpen);
    }
  }, [isOpen]);

  const handleToggle = (newOpen) => {
    setOpen(newOpen);
    if (onToggle) {
      onToggle(newOpen);
    }
  };
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const controlRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState(undefined);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) handleToggle(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [handleToggle]);

  const selected = options.find(o => o.value === value);
  const specialValues = new Set(['__show_other__', '__divider__', 'add']);
  const filtered = query.trim()
    ? [
        ...sortBySearchPriority(
          options.filter(o => !specialValues.has(o.value)),
          query,
          (option) => option.label || option.value || ''
        ),
        // Always show special options at the end when searching
        ...options.filter(o => specialValues.has(o.value))
      ]
    : options;

  // When opening or when options/filter change, measure widest option and size menu accordingly
  useEffect(() => {
    if (!open) return;
    const ctrl = controlRef.current;
    if (!ctrl) return;
    const computeWidth = () => {
      const testEl = document.createElement('div');
      const cs = window.getComputedStyle(ctrl);
      testEl.style.position = 'fixed';
      testEl.style.left = '-9999px';
      testEl.style.top = '-9999px';
      testEl.style.visibility = 'hidden';
      testEl.style.whiteSpace = 'nowrap';
      testEl.style.fontFamily = cs.fontFamily || 'Inter, Arial, sans-serif';
      testEl.style.fontSize = '16px';
      testEl.style.fontWeight = '400';
      document.body.appendChild(testEl);
      let max = 0;
      const source = filtered.length ? filtered : options;
      for (const opt of source) {
        testEl.textContent = String(opt.label ?? opt.value ?? '');
        max = Math.max(max, testEl.clientWidth);
      }
      document.body.removeChild(testEl);
      const triggerW = ctrl.getBoundingClientRect().width;
      // Add generous padding and scrollbar allowance
      let needed = max + 60; // text + padding
      needed = Math.max(needed, triggerW);
      const maxViewport = Math.max(340, Math.floor(window.innerWidth * 0.9));
      setMenuWidth(Math.min(needed, maxViewport));
    };
    computeWidth();
    // Recompute on window resize while open
    const onResize = () => computeWidth();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, options, filtered]);

  const CONTROL_HEIGHT = 30; // compact control height
  const MENU_GAP = 6; // spacing between control and menu
  const optionHeight = 36; // px per option row for height math
  const listMaxHeight = Math.max(1, visibleCount) * optionHeight;

  return (
    <div
      ref={wrapRef}
      className={`itemmap-unit-select${open ? ' itemmap-unit-select--open' : ''}`}
      style={{ position: 'relative', width: '100%', zIndex: open ? 5000 : 'auto', ...(style || {}) }}
    >
      <div
        ref={controlRef}
        onClick={() => !disabled && handleToggle(!open)}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%',
          height: CONTROL_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #d1d5db',
          borderRadius: 8,
          background: disabled ? '#f5f5f5' : '#fff',
          padding: '0 10px',
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.label ?? selected?.value ?? 'g'}
        </span>
        <span style={{ border: 'solid #111', borderWidth: '0 2px 2px 0', display: 'inline-block', padding: 3, transform: 'rotate(45deg)' }} />
      </div>
      {open && !disabled && (
        <div
          className="itemmap-unit-menu"
          role="listbox"
          style={{
            position: 'absolute',
            top: openUpward ? 'auto' : CONTROL_HEIGHT + MENU_GAP,
            bottom: openUpward ? CONTROL_HEIGHT + MENU_GAP : 'auto',
            left: 'auto',
            right: 0,
            zIndex: 3000,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
            padding: 8,
            width: menuWidth || 'max-content',
            minWidth: menuWidth || 'auto',
            maxWidth: '90vw',
          }}
        >
          <div style={{ maxHeight: listMaxHeight, overflowY: 'auto', overflowX: 'hidden' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 12, color: '#777', fontSize: 13 }}>No matches</div>
            ) : (
              filtered.map(opt => {
                // Divider row (non-clickable)
                if (opt.value === '__divider__') {
                  return (
                    <div key="__divider__" style={{
                      padding: '6px 10px', fontSize: 11, color: '#9ca3af',
                      textAlign: 'center', userSelect: 'none', fontWeight: 600,
                      borderTop: '1px solid #e5e7eb', marginTop: 4,
                    }}>
                      {opt.label}
                    </div>
                  );
                }
                // "Show other units" button
                if (opt.value === '__show_other__') {
                  return (
                    <div
                      key="__show_other__"
                      onClick={() => {
                        onChange && onChange('__show_other__');
                      }}
                      style={{
                        padding: '8px 10px', borderRadius: 8,
                        background: '#ffffff', color: '#000000',
                        cursor: 'pointer', textAlign: 'center',
                        fontWeight: 600, fontSize: 13, marginTop: 4,
                        border: '1px dashed #d1d5db',
                      }}
                    >
                      {opt.label}
                    </div>
                  );
                }
                // Disabled option
                if (opt.disabled) {
                  return (
                    <div key={opt.value} style={{
                      padding: '6px 10px', color: '#9ca3af', fontSize: 12,
                      userSelect: 'none', fontStyle: 'italic',
                    }}>
                      {opt.label}
                    </div>
                  );
                }
                return (
                  <div
                    key={opt.value}
                    role="option"
                    aria-selected={opt.value === value}
                    onClick={() => {
                      if (opt.value === 'add') {
                        onAddClick && onAddClick();
                      } else {
                        onChange && onChange(opt.value);
                      }
                      handleToggle(false);
                      setQuery('');
                    }}
                    style={{
                      padding: opt.value === 'add' ? '10px 12px' : '8px 10px',
                      borderRadius: 8,
                      background: opt.value === 'add' ? '#000' : (opt.isCrossFamily ? '#fffbeb' : (opt.value === value ? '#f3f4f6' : 'transparent')),
                      color: opt.value === 'add' ? '#fff' : (opt.isCrossFamily ? '#92400e' : '#111'),
                      cursor: 'pointer',
                      textAlign: opt.value === 'add' ? 'center' : 'left',
                      fontWeight: opt.value === 'add' ? 700 : 400,
                      marginTop: opt.value === 'add' ? 8 : 0,
                      whiteSpace: 'nowrap',
                      wordBreak: 'keep-all',
                      borderLeft: opt.isCrossFamily ? '3px solid #f59e0b' : 'none',
                    }}
                  >
                    {opt.value === 'add' ? (opt.label || '+ Add') : (opt.label ?? opt.value)}
                    {opt.isCrossFamily && <span style={{ fontSize: 10, marginLeft: 4, color: '#d97706' }}>⚠</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RecipeListScreen = ({ goTo, previousScreen, isOnboarding = false, onBack }) => {
  const [recipes, setRecipes] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const categoryChipsRef = useRef(null);
  const categoryMeasureRef = useRef(null);
  const [showAllCategoryChips, setShowAllCategoryChips] = useState(false);
  const [hasCategoryChipOverflow, setHasCategoryChipOverflow] = useState(false);
  const [collapsedCategoryCount, setCollapsedCategoryCount] = useState(0);
  const [showUnitChangeModal, setShowUnitChangeModal] = useState(false);
  const [unitChangePreview, setUnitChangePreview] = useState(null);
  const [isApplyingUnitChange, setIsApplyingUnitChange] = useState(false);
  const [pendingUnitChange, setPendingUnitChange] = useState(null);
  const [savingRecipeId, setSavingRecipeId] = useState(null);
  const [openUnitIndex, setOpenUnitIndex] = useState(null);

  // Track which ingredient indexes have "Other Units" expanded
  const [showOtherUnitsFor, setShowOtherUnitsFor] = useState(new Set());
  
  // New CrossFamilyConversionModal state
  const [showNewConversionModal, setShowNewConversionModal] = useState(false);
  const [newConversionModalProps, setNewConversionModalProps] = useState(null);
  const [externalEditItemTrigger, setExternalEditItemTrigger] = useState(null);

  // SP9 "your number is sacred", Rule 1: changing the unit NEVER changes the number and
  // NEVER interrupts. The old cross-family conversion popup is gone from recipe rows —
  // the strict "this item is measured by weight" rule lives on the inventory item (pencil
  // edit), and the save gate still refuses a family-vs-stock mismatch. The row instead
  // shows an inline warning with a one-tap fix (see rowFamilyMismatch below).
  const handleUnitChangeWithConfirmation = async (itemName, currentUnit, newUnit, callback) => {
    if (currentUnit === newUnit) return;
    if (newUnit === '__show_other__') return; // handled in the dropdown's onChange
    callback(newUnit);
  };

  // Inline family note: the row's unit family vs the item's stock family. Returns null
  // when they agree (or the item/units are unknown), else { stockFamily, fixUnit }.
  const rowFamilyMismatch = (name, unit) => {
    const std = inventoryStdUnitMap[(name || '').trim().toLowerCase()];
    if (!std || !unit) return null;
    const rowFamily = UnitMappingService.getUnitFamily(unit);
    const stockFamily = UnitMappingService.getUnitFamily(std);
    if (!rowFamily || !stockFamily || rowFamily === stockFamily) return null;
    if (!UnitMappingService.isBaseUnit(unit)) return null; // kitchen tools convert at deduction
    const fixUnit = stockFamily === 'volume' ? 'ml' : 'g';
    return { stockFamily, fixUnit };
  };

  const handleConfirmUnitChange = async (conversionFactor) => {
    if (!pendingUnitChange) return;
    
    setIsApplyingUnitChange(true);
    try {
      const { itemName, currentUnit, newUnit, callback, conversionFactor: storedFactor } = pendingUnitChange;
      // Use the stored factor from the conversion modal if the confirmation modal doesn't provide one
      const finalFactor = conversionFactor || storedFactor;
      const result = await UnitMappingService.confirmUnitChange(
        itemName, 
        currentUnit, 
        newUnit, 
        finalFactor,
        'Changed via Recipe Editor'
      );
      
      if (result.success) {
        showToast('Unit changed successfully. Quantities updated.', 'success');
        setShowUnitChangeModal(false);
        setPendingUnitChange(null);
        setUnitChangePreview(null);
        callback(newUnit);
        // Refresh data to reflect the changes across all recipes
        loadData();
      } else {
        showToast(result.error || 'Failed to apply unit change', 'error');
      }
    } catch (err) {
      console.error('Apply error:', err);
      showToast(err.message || 'Failed to apply unit change', 'error');
    } finally {
      setIsApplyingUnitChange(false);
    }
  };
  const editNameInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  const addIngredientButtonRef = useRef(null);
  const unitDropdownRef = useRef(null);
  const ingredientEditorRef = useRef(null);
  const swipeStateRef = useRef({});
  const [swipeOffsets, setSwipeOffsets] = useState({});
  const [swipingIndex, setSwipingIndex] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [ingredientList, setIngredientList] = useState([]);
  // Portions that were set by a leakage calibration rather than typed when the
  // recipe was written (LEAKAGE_SIGNATURE_SPEC §10.4). Keyed by lower-cased
  // ingredient name, because a recipe row here carries a name, not an item_id.
  const [calibratedPortions, setCalibratedPortions] = useState(new Map());
  const [pendingAutoAddAfterUnitSelect, setPendingAutoAddAfterUnitSelect] = useState(false);

  /**
   * A quantity the kitchen argued for and the owner accepted is not the same
   * fact as a quantity someone typed when the recipe was written, and six
   * months later nothing on this screen could tell them apart. §10.4 requires
   * the portion to carry that history wherever it is shown.
   *
   * Failure is silent on purpose: leakage may be switched off for a deployment,
   * or the user may not hold the reports permission, and neither is a reason
   * the recipe editor should show an error or stop working.
   */
  useEffect(() => {
    const menuItemId = selectedRecipe?.menu_item_id || selectedRecipe?.id;
    if (!menuItemId) { setCalibratedPortions(new Map()); return undefined; }
    let alive = true;
    http.get(`/leakage/calibrations?menu_item_id=${encodeURIComponent(menuItemId)}`)
      .then((rows) => {
        if (!alive || !Array.isArray(rows)) return;
        const byName = new Map();
        for (const row of rows) {
          const key = String(row.ingredient_name || '').trim().toLowerCase();
          if (key) byName.set(key, row);
        }
        setCalibratedPortions(byName);
      })
      .catch(() => { if (alive) setCalibratedPortions(new Map()); });
    return () => { alive = false; };
  }, [selectedRecipe?.menu_item_id, selectedRecipe?.id]);

  const triggerAddIngredientFromKeyboard = useCallback(() => {
    // Reuse the existing button handler so keyboard and click behavior stay identical.
    const btn = addIngredientButtonRef.current;
    if (btn) btn.click();
  }, []);

  const [localInputValues, setLocalInputValues] = useState({});
  const [creationErrors, setCreationErrors] = useState({});
  const [showRoundOffModal, setShowRoundOffModal] = useState(false);
  const originalIngredientSnapshotRef = useRef([]);
  const editSessionSnapshotRef = useRef(null);
  const editingOriginalsRef = useRef({});
  const [plateStep, setPlateStep] = useState(1);
  const [showPlateMenu, setShowPlateMenu] = useState(false);
  const [customPlateStep, setCustomPlateStep] = useState('');
  const [showCustomPlateInput, setShowCustomPlateInput] = useState(false);
  const plateMenuRef = useRef(null);
  const [autoSelectAttempted, setAutoSelectAttempted] = useState(false);
  const [cameFromTSR, setCameFromTSR] = useState(false);
  const [cameFromWastage, setCameFromWastage] = useState(false);
  const [cameFromMenuManagement, setCameFromMenuManagement] = useState(false);



  const PLATE_STEP_STORAGE_KEY = 'itemmapPlateStep';
  const PLATE_STEP_CUSTOM_STORAGE_KEY = 'itemmapCustomPlateStep';

  const getPersistedPlateStep = () => {
    const raw = localStorage.getItem(PLATE_STEP_STORAGE_KEY);
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const syncPlateStepFromStorage = () => {
    const persisted = getPersistedPlateStep();
    setPlateStep(persisted);
    const storedCustom = localStorage.getItem(PLATE_STEP_CUSTOM_STORAGE_KEY);
    if (storedCustom) {
      setCustomPlateStep(String(storedCustom));
    }
  };

  const [complimentaryItems, setComplimentaryItems] = useState([]);
  const [showComplimentaryDropdown, setShowComplimentaryDropdown] = useState(false);
  const [showComplimentaryItemPage, setShowComplimentaryItemPage] = useState(false);
  const [selectedComplimentaryItem, setSelectedComplimentaryItem] = useState(null);
  const [complimentaryLinkModal, setComplimentaryLinkModal] = useState({
    visible: false,
    item: null,
    currentlyLinked: false,
    error: '',
    isSubmitting: false
  });
  const complimentaryDropdownRef = useRef(null);
  const [showAddComplimentaryModal, setShowAddComplimentaryModal] = useState(false);
  const [newComplimentaryForm, setNewComplimentaryForm] = useState({ name: '' });
  const [showDeleteComplimentaryModal, setShowDeleteComplimentaryModal] = useState(false);
  const [complimentaryItemToDelete, setComplimentaryItemToDelete] = useState(null);
  const [restoreComplimentaryDropdownAfterDelete, setRestoreComplimentaryDropdownAfterDelete] = useState(false);
  const [complimentaryRecipesLibrary, setComplimentaryRecipesLibrary] = useState([]);
  const [linkedComplimentaryItems, setLinkedComplimentaryItems] = useState([]);
  const [loadingComplimentaryLibrary, setLoadingComplimentaryLibrary] = useState(false);
  const [selectedComplimentaryItems, setSelectedComplimentaryItems] = useState([]);

  const [showSaveConfirmationModal, setShowSaveConfirmationModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyModalMessage, setVerifyModalMessage] = useState('');
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [summaryOpenDropdown, setSummaryOpenDropdown] = useState(null);
  const [savedPreparations, setSavedPreparations] = useState([]);
  const [showCategoryGate, setShowCategoryGate] = useState(false);
  
  // Track if component is still mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    const pendingTSRData = localStorage.getItem('pendingTSRData');
    const pendingWastageData = localStorage.getItem('pendingWastageData');
    const pendingMenuManagementData = localStorage.getItem('pendingMenuManagementData');
    setCameFromTSR(!!pendingTSRData);
    setCameFromWastage(!!pendingWastageData);
    setCameFromMenuManagement(!!pendingMenuManagementData);
  }, []);
  
  // Cleanup on unmount - close modal and mark as unmounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Force close modal if component unmounts (safety cleanup)
      // This should NOT happen in normal flow since we block unmounting while modal is open
      if (showNewConversionModal) {
        console.warn('⚠️ Modal was open during unmount - forcing close');
      }
      setShowNewConversionModal(false);
      setNewConversionModalProps(null);
    };
  }, [showNewConversionModal]);

  useEffect(() => {
    const handleScroll = () => {
      const appMain = document.querySelector('.app-main');
      const scrollTop = Math.max(
        window.scrollY,
        document.documentElement.scrollTop,
        document.body.scrollTop,
        appMain ? appMain.scrollTop : 0
      );
      setShowScrollTop(scrollTop > 400);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  useEffect(() => {
    setPlateStep(1);
    setCustomPlateStep('');
  }, [isOnboarding]);

  const loadSavedPreparations = useCallback(async () => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      if (!businessId) return;

      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';
      const response = await fetch(`${apiUrl}/medium-features/decomposition/templates?businessId=${businessId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      if (!response.ok) return;

      const result = await response.json();
      setSavedPreparations(Array.isArray(result?.personalized) ? result.personalized : []);
    } catch (error) {
      console.warn('Failed to load saved preparations for summary:', error);
    }
  }, []);

  useEffect(() => {
    loadSavedPreparations();
  }, [loadSavedPreparations]);

  useEffect(() => {
    if (showSaveConfirmationModal) {
      loadSavedPreparations();
    }
  }, [showSaveConfirmationModal, loadSavedPreparations]);

  const [showKitchenUnitModal, setShowKitchenUnitModal] = useState(false);
  const [kitchenUnitForm, setKitchenUnitForm] = useState({
    item: '',
    kitchenTool: 'Tablespoon',
    quantity: '15',
    unit: 'ml'
  });
  // Kitchen-unit popup local dropdown states (to mirror Kitchen Units screen UI)
  const [modalToolOpen, setModalToolOpen] = useState(false);
  const [modalUnitOpen, setModalUnitOpen] = useState(false);
  const [modalItemOpen, setModalItemOpen] = useState(false);
  const [modalItemSearch, setModalItemSearch] = useState('');
  const [modalToolSearch, setModalToolSearch] = useState('');
  const [modalUnitSearch, setModalUnitSearch] = useState('');
  const [modalKitchenTools, setModalKitchenTools] = useState(['Tablespoon', 'Teaspoon', 'Cup', 'Bowl']);
  const [modalShowAddTool, setModalShowAddTool] = useState(false);
  const [modalNewToolName, setModalNewToolName] = useState('');
  const baseUnitOptions = [
    { value: 'ml', label: 'ml' },
    { value: 'L', label: 'L' },
    { value: 'g', label: 'g' },
    { value: 'kg', label: 'kg' },
  ];

  const normalizeKitchenTool = (tool) => {
    const raw = String(tool || '').trim();
    const lower = raw.toLowerCase();
    if (!lower) return { symbol: '', label: '' };
    const map = {
      tablespoon: 'tbsp',
      tbsp: 'tbsp',
      teaspoon: 'tsp',
      tsp: 'tsp'
    };
    const symbol = map[lower] || lower;
    const labelMap = {
      tbsp: 'Tablespoon',
      tsp: 'Teaspoon'
    };
    return { symbol, label: labelMap[symbol] || raw };
  };

  const handleSaveKitchenUnit = async () => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      
      // Validate required fields
      if (!kitchenUnitForm.kitchenTool || !kitchenUnitForm.quantity || !kitchenUnitForm.unit) {
        showToast('Please fill all fields');
        return;
      }
      
      //  PRODUCTION VALIDATION: Check for cross-category conversion
      const ingredientName = kitchenUnitForm.item || formData.name;
      const ingredientNameKey = (ingredientName || '').trim().toLowerCase();
      const standardUnit = inventoryStdUnitMap[ingredientNameKey];
      const conversionUnit = kitchenUnitForm.unit;
      
      if (standardUnit) {
        // Ingredient exists - validate unit categories
        const standardCategory = getUnitCategory(standardUnit);
        const conversionCategory = getUnitCategory(conversionUnit);
        
        // Allow if both are unknown or if either is 'count' (pieces, units)
        if (standardCategory !== 'unknown' && 
            conversionCategory !== 'unknown' &&
            standardCategory !== 'count' &&
            conversionCategory !== 'count' &&
            standardCategory !== conversionCategory) {
          
          // ❌ CROSS-CATEGORY MISMATCH DETECTED
          showToast(
            `❌ Cannot create this conversion!\n\n` +
            `"${ingredientName}" uses ${standardUnit} (${standardCategory}), ` +
            `but you're converting to ${conversionUnit} (${conversionCategory}).\n\n` +
            `Please use ${standardCategory} units for this ingredient.\n\n` +
            `Cannot convert between weight and volume without density data.`,
            'error'
          );
          console.warn(`🚫 Blocked cross-category conversion: ${ingredientName} (${standardUnit}/${standardCategory}) → ${conversionUnit}/${conversionCategory}`);
          return; // ❌ BLOCK SAVE
        }
        
        debugLog(` Validation passed: ${ingredientName} (${standardUnit}/${standardCategory}) → ${conversionUnit}/${conversionCategory}`);
      } else {
        // New ingredient - no standard unit yet, allow conversion
        debugLog(`⚠️ New ingredient "${ingredientName}" - no standard unit found, conversion will set it`);
      }
      
      //  VALIDATION PASSED - Proceed with save
      const result = await UnitMappingService.saveKitchenUnit({
        businessId,
        item: ingredientName,
        kitchenTool: kitchenUnitForm.kitchenTool,
        quantity: Number(kitchenUnitForm.quantity),
        unit: kitchenUnitForm.unit,
        type: 'kitchen'  // itemmap uses kitchen type
      });

      // Update ingredient-specific maps for immediate UI consistency
      const itemKey = (kitchenUnitForm.item || formData.name || '').trim().toLowerCase();
      if (itemKey) {
        setIngredientToolUnitsMap(prev => {
          const next = { ...prev };
          const list = next[itemKey] ? [...next[itemKey]] : [];
          const { symbol, label } = normalizeKitchenTool(kitchenUnitForm.kitchenTool);
          if (symbol && !list.find(x => x.value === symbol)) {
            list.push({ value: symbol, label });
          }
          next[itemKey] = list;
          return next;
        });
        setIngredientToolConversionsMap(prev => {
          const next = { ...prev };
          const list = next[itemKey] ? [...next[itemKey]] : [];
          list.push({
            tool: normalizeKitchenTool(kitchenUnitForm.kitchenTool).symbol,
            unit: kitchenUnitForm.unit,
            quantity: Number(kitchenUnitForm.quantity)
          });
          next[itemKey] = list;
          return next;
        });

        // If user is editing this ingredient, switch to the new tool immediately
        if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
          const tool = normalizeKitchenTool(kitchenUnitForm.kitchenTool).symbol;
          const outUnit = String(kitchenUnitForm.unit || '').trim();
          const factor = Number(kitchenUnitForm.quantity) || 0;

          if (tool && outUnit && factor > 0) {
            // Update formData
            if ((formData.name || '').trim().toLowerCase() === itemKey) {
              let newQty = formData.quantity;
              if (String(formData.unit || '').trim().toLowerCase() === outUnit.toLowerCase()) {
                newQty = Number((Number(formData.quantity || 0) / factor).toFixed(4));
              }
              setFormData(prev => ({ ...prev, unit: tool, quantity: newQty }));
            }

            // Update ingredient list for the active row
            setIngredientList(prev => {
              const updated = [...prev];
              const row = updated[editingIngredientIndex];
              if (!row) return updated;
              if ((row.name || '').trim().toLowerCase() !== itemKey) return updated;
              const currentUnit = String(row.unit || '').trim().toLowerCase();
              if (currentUnit === outUnit.toLowerCase()) {
                // Convert displayed qty by dividing by kitchen tool factor, then recalc all plate sizes
                const displayQty = getDisplayQty(row);
                const newDisplayQty = Number((displayQty / factor).toFixed(4));
                const withQty = setQtyForPlateStep(row, newDisplayQty);
                row.qty10 = withQty.qty10;
                row.qty100 = withQty.qty100;
              }
              row.unit = tool;
              updated[editingIngredientIndex] = { ...row };
              return updated;
            });
          }
        }
      }

      // Update unit options to include the new kitchen unit
      const currentOptions = [...unitOptions];
      const normalizedTool = normalizeKitchenTool(kitchenUnitForm.kitchenTool);
      if (!currentOptions.find(opt => opt.value === normalizedTool.symbol)) {
        currentOptions.push({
          value: normalizedTool.symbol,
          label: normalizedTool.label
        });
        setUnitOptions(currentOptions);
      }

      setShowKitchenUnitModal(false);
      showToast('Kitchen unit saved successfully!');
      debugLog(' Kitchen unit saved:', result);

      //  AUTO-REFRESH: Reload kitchen conversions and update display
      await refreshKitchenConversionsAndDisplay();
    } catch (error) {
      console.error('❌ Failed to save kitchen unit:', error);
      const errorMsg = error?.response?.data?.error || error?.message || 'Failed to save kitchen unit';
      showToast(errorMsg);
    }
  };
  const [editingIngredientIndex, setEditingIngredientIndex] = useState(null);
  const [formData, setFormData] = useState({ name: '', quantity: 0, unit: '' });
  const [duplicateRecipeError, setDuplicateRecipeError] = useState(null);
  // Same item name already exists: offer to create it as a VARIANT (distinct name,
  // linked through RecipeVariants, parent mapping × multiplier). Shape:
  // { items: [{ name, parentId, parentName, existingPrices, newPrice, label, multiplier }] }
  const [variantConfirm, setVariantConfirm] = useState(null);
  // Rows the server refused on validate, keyed by lowercase ingredient name.
  const [ingredientBlockers, setIngredientBlockers] = useState({});
  const [unitOptions, setUnitOptions] = useState([]);

  // Close display mode unit selector when entering edit mode
  useEffect(() => {
    if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
      setOpenUnitIndex(null);
    }
  }, [editingIngredientIndex]);

  useEffect(() => {
    if (editingIngredientIndex === null || editingIngredientIndex === undefined) return;
    const timer = setTimeout(() => {
      if (editNameInputRef.current) {
        editNameInputRef.current.focus();
        const len = editNameInputRef.current.value.length;
        editNameInputRef.current.setSelectionRange(len, len);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [editingIngredientIndex]);
  
  // Undo/Redo history for ingredient deletions
  const [ingredientHistory, setIngredientHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const cloneIngredientList = (list) => list.map(item => ({ ...item }));
  const buildIngredientSnapshot = (list = []) => (Array.isArray(list) ? list : []).map(item => ({
    name: String(item?.name || '').trim(),
    unit: String(item?.unit || '').trim(),
    qty10: Number(Number(item?.qty10 || 0).toFixed(4)),
    qty100: Number(Number(item?.qty100 || 0).toFixed(4))
  }));
  // SP9 (B6): history is capped and no-op pushes are skipped — long editing sessions no
  // longer accumulate unbounded full-list snapshots. (Snapshots stay full-list on purpose:
  // a diff-based restore is regression bait for a 40-row list of small objects.)
  const INGREDIENT_HISTORY_CAP = 50;
  const pushIngredientHistory = (action, previousList, newList) => {
    if (JSON.stringify(previousList) === JSON.stringify(newList)) return;
    let nextHistory = ingredientHistory.slice(0, historyIndex + 1);
    nextHistory.push({ action, previousList, newList });
    if (nextHistory.length > INGREDIENT_HISTORY_CAP) {
      nextHistory = nextHistory.slice(nextHistory.length - INGREDIENT_HISTORY_CAP);
    }
    setIngredientHistory(nextHistory);
    setHistoryIndex(nextHistory.length - 1);
  };

  const [ingredientToolUnitsMap, setIngredientToolUnitsMap] = useState({}); // name -> [{symbol,label}]

  // SP9 (B): the fuzzy tool-units lookup used to run a Levenshtein sweep over the whole
  // map INSIDE every row render. Same logic, cached per name until the map changes.
  const toolExtrasForName = React.useMemo(() => {
    const cache = new Map();
    return (rawNameInput) => {
      const rawName = String(rawNameInput || '').trim();
      const nameKey = rawName.toLowerCase();
      if (cache.has(nameKey)) return cache.get(nameKey);
      let extras = ingredientToolUnitsMap[nameKey] || [];
      if (!extras.length && rawName) {
        const normRaw = normalizeName(rawName);
        let best = { key: null, dist: Infinity };
        for (const k of Object.keys(ingredientToolUnitsMap || {})) {
          const d = levenshtein(normRaw, normalizeName(k));
          if (d < best.dist) best = { key: k, dist: d };
        }
        if (best.key && (best.dist <= 2 || normalizeName(best.key).includes(normRaw) || normRaw.includes(normalizeName(best.key)))) {
          extras = ingredientToolUnitsMap[best.key] || [];
        }
      }
      cache.set(nameKey, extras);
      return extras;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredientToolUnitsMap]);
  const [ingredientToolConversionsMap, setIngredientToolConversionsMap] = useState({}); // name -> [{tool, unit, quantity}]
  const [showNewRecipeForm, setShowNewRecipeForm] = useState(false);
  // const [newRecipeData, setNewRecipeData] = useState({ name: '', price: '', servings: '' });
  const [recipeCards, setRecipeCards] = useState([{ id: Date.now(), name: '', price: '', servings: '' }]); // Array of recipe cards
  const [capturedImage, setCapturedImage] = useState(null);
  // Refs for keyboard navigation between card fields
  const cardFieldRefs = useRef({}); // { [cardId]: { name, price, servings } }
  const newCardFocusIdRef = useRef(null); // id of newly added card to focus after render
  
  const [showMenuOptionsModal, setShowMenuOptionsModal] = useState(false);
  // Menu-scan OCR: backend + UI removed (SP7). setShowOCRModal(true) exists nowhere, so
  // the states and handlers below are unreachable plumbing kept only until a
  // render-tested cleanup can excise them from the shared variant-multiplier code.
  const [showOCRModal, setShowOCRModal] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrResults, setOcrResults] = useState([]);
  const ocrMenuInputRef = useRef(null);
  const [showMenuSelector, setShowMenuSelector] = useState(false);
  const [useMasterCategories, setUseMasterCategories] = useState(() => {
    const saved = localStorage.getItem('useMasterCategories');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('useMasterCategories', JSON.stringify(useMasterCategories));
  }, [useMasterCategories]);

  const categories = React.useMemo(() => {
    const key = useMasterCategories ? 'master_category' : 'category';
    const unique = Array.from(new Set(recipes.map(r => r[key] || 'Uncategorized').filter(Boolean)));
    return unique.sort();
  }, [recipes, useMasterCategories]);

  useEffect(() => {
    setFilter('All');
  }, [useMasterCategories]);

  useEffect(() => {
    scrollToTop();
  }, [search, filter, useMasterCategories]);

  useEffect(() => {
    setLocalInputValues({});
  }, [selectedRecipe?.id]);

  useEffect(() => {
    if (!showNewRecipeForm) {
      setCreationErrors({});
    }
  }, [showNewRecipeForm]);
  // const [availableCategories, setAvailableCategories] = useState([]); // Menu categories from DB
  const [selectedCategory, setSelectedCategory] = useState(null); // Selected category for recipes
  const [savingIngredients, setSavingIngredients] = useState(false);
  const [showAddMasterCategoryInput, setShowAddMasterCategoryInput] = useState(false);
  const [newMasterCategoryName, setNewMasterCategoryName] = useState('');
  const savingIngredientsRef = useRef(false); // imperative guard — avoids stale React state blocking retries
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateConflictData, setDuplicateConflictData] = useState(null);
  const [duplicateOverrides, setDuplicateOverrides] = useState({});
  const duplicateOverridesRef = useRef({}); // imperative ref — ensures retries use the latest overrides
  const [duplicateForceReason, setDuplicateForceReason] = useState('Intentional Variant');
  // Batch duplicate resolution — all detected duplicates surfaced together in one popup
  const [duplicateConflicts, setDuplicateConflicts] = useState([]); // array from backend
  const [duplicateDecisions, setDuplicateDecisions] = useState({}); // { attempted_name: { action, reason } }
  const pendingConfirmListRef = useRef(null);
  const pendingSaveListRef = useRef(null);
  const pendingSavePlateCountRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const ingredientListInitRef = useRef(true); // skip auto-save on initial load
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // 'saving' | 'saved' | null

  // Menu scan job tracking
  const [menuScanJob, setMenuScanJob] = useState(null);
  const [menuScanProgress, setMenuScanProgress] = useState(0);
  const [scannedMenuItems, setScannedMenuItems] = useState([]);
  const pollIntervalRef = useRef(null);

  // Multi-image support for menu scanning
  const [selectedMenuImages, setSelectedMenuImages] = useState([]);
  const [menuProcessingProgress, setMenuProcessingProgress] = useState({ current: 0, total: 0 });

  // Inventory items map for standard units (name -> unit symbol)
  const [inventoryStdUnitMap, setInventoryStdUnitMap] = useState({});
  const [inventoryItemNames, setInventoryItemNames] = useState([]); // for modal Item dropdown
  // item_id -> name. Needed to recognise a CONVERTED item: a preparation links to the item it
  // drives by id, and the two names are routinely different ("Garam Masala" made by "Garam
  // Masala Indian"), so nothing name-based can spot the pair.
  const [inventoryNameById, setInventoryNameById] = useState({});

  const isCurrentEditingIngredientNew = useCallback(() => {
    const rawName = (formData?.name || '').trim();
    const nameKey = rawName.toLowerCase();
    return !rawName || rawName === 'new ingredient' || !inventoryStdUnitMap[nameKey];
  }, [formData?.name, inventoryStdUnitMap]);

  // === Sync from Recipe Library ===
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncMatches, setSyncMatches] = useState([]);
  const [syncSelected, setSyncSelected] = useState({});
  const [syncLoading, setSyncLoading] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [syncConflictItems, setSyncConflictItems] = useState([]);
  const [syncProcessing, setSyncProcessing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, message: '' });

  // Handler: Open sync modal and fetch matches
  const handleSyncFromLibrary = async () => {
    try {
      setSyncLoading(true);
      setShowSyncModal(true);
      setSyncMatches([]);
      setSyncSelected({});
      const businessId = UnitMappingService.getBusinessId();
      const result = await RecipeLibraryService.matchMenuItems(businessId);
      if (result.matches && result.matches.length > 0) {
        setSyncMatches(result.matches);
      } else {
        showToast('No matches found in recipe library', 'warning');
        setShowSyncModal(false);
      }
    } catch (err) {
      console.error('Failed to match menu items:', err);
      showToast('Failed to check recipe library', 'error');
      setShowSyncModal(false);
    } finally {
      setSyncLoading(false);
    }
  };

  // Handler: Toggle select all
  const handleSyncSelectAll = (checked) => {
    const next = {};
    if (checked) {
      syncMatches.forEach(m => { next[m.menuItemId] = true; });
    }
    setSyncSelected(next);
  };

  // Handler: Proceed with sync (check for conflicts)
  const handleSyncProceed = () => {
    const selectedIds = Object.keys(syncSelected).filter(k => syncSelected[k]).map(Number);
    if (selectedIds.length === 0) {
      showToast('Select at least one item', 'warning');
      return;
    }
    const selected = syncMatches.filter(m => selectedIds.includes(m.menuItemId));
    const conflicting = selected.filter(m => m.hasExistingIngredients);

    if (conflicting.length > 0) {
      setSyncConflictItems(conflicting);
      setShowConflictModal(true);
    } else {
      executeBulkSync(selected, 'replace');
    }
  };

  // Handler: Execute the bulk sync
  const executeBulkSync = async (items, strategy) => {
    setShowSyncModal(false);
    setShowConflictModal(false);
    setSyncProcessing(true);
    setSyncProgress({ current: 0, total: items.length, message: 'Starting sync...' });

    try {
      const businessId = UnitMappingService.getBusinessId();
      const selectedItems = items.map(m => ({
        menuItemId: m.menuItemId,
        menuItemName: m.menuItemName
      }));

      setSyncProgress({ current: 1, total: items.length, message: 'Syncing ingredients from library...' });

      const results = await RecipeLibraryService.bulkSync(businessId, selectedItems, strategy);

      setSyncProgress({ current: items.length, total: items.length, message: 'Complete!' });

      // Refresh the recipes list
      const recipesData = await RecipesService.getRecipes(businessId);
      setRecipes(applyLocalReviewDrafts(recipesData));

      const parts = [];
      if (results.synced > 0) parts.push(`${results.synced} new`);
      if (results.replaced > 0) parts.push(`${results.replaced} replaced`);
      if (results.merged > 0) parts.push(`${results.merged} merged`);
      showToast(`✅ Synced: ${parts.join(', ')}`, 'success');
    } catch (err) {
      console.error('Bulk sync failed:', err);
      showToast(`❌ Sync failed: ${err.message}`, 'error');
    } finally {
      setSyncProcessing(false);
    }
  };

  // Simple message modal that auto-closes after a short duration
  const MESSAGE_MODAL_DURATION_MS = 1500;
  const [toastModal, setToastModal] = useState({
    visible: false,
    message: '',
    type: 'info'
  });
  const toastTimerRef = useRef(null);
  const closeToast = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastModal((prev) => ({ ...prev, visible: false }));
  };
  const showToast = (msg, type = 'info') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastModal({
      visible: true,
      message: msg,
      type: type || 'info'
    });
    toastTimerRef.current = setTimeout(() => closeToast(), MESSAGE_MODAL_DURATION_MS);
  };

  const getComplimentaryItemName = (item) => (
    typeof item === 'string' ? item : item?.name || 'this complimentary item'
  );

  const closeComplimentaryLinkModal = () => {
    setComplimentaryLinkModal({
      visible: false,
      item: null,
      currentlyLinked: false,
      error: '',
      isSubmitting: false
    });
  };

  const requestComplimentaryLinkToggle = (item, currentlyLinked, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setShowComplimentaryDropdown(false);
    setComplimentaryLinkModal({
      visible: true,
      item,
      currentlyLinked,
      error: '',
      isSubmitting: false
    });
  };

  const applyLocalReviewDrafts = (nextRecipes) => (
    mergeRecipesWithLocalReviewDrafts(nextRecipes, UnitMappingService.getBusinessId())
  );

  // SP7: pull the server's drafts once per mount so the "Under Review" badge is truthful
  // across devices; a dead network degrades to local-only drafts.
  useEffect(() => {
    hydrateRecipeReviewDrafts(UnitMappingService.getBusinessId())
      .then((applied) => {
        if (applied > 0) setRecipes((prev) => applyLocalReviewDrafts(prev));
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistLocalReviewDraft = (draftIngredientList = ingredientList) => {
    if (!selectedRecipe?.id) return null;

    return saveRecipeReviewDraft({
      recipe: selectedRecipe,
      businessId: UnitMappingService.getBusinessId(),
      draft: {
        status: 'draft',
        source: 'itemmap',
        ingredientList: Array.isArray(draftIngredientList) ? draftIngredientList : [],
        selectedComplimentaryItems: Array.isArray(selectedComplimentaryItems) ? selectedComplimentaryItems : []
      }
    });
  };
  
  
  // Preparation Creator Modal (New standalone feature)
  const [showPreparationCreator, setShowPreparationCreator] = useState(false);
  
  // 🆕 Portion Multiplier Customization
  const [showMultiplierEditor, setShowMultiplierEditor] = useState(false);
  const [pendingVariantGroups, setPendingVariantGroups] = useState([]);
  const [customMultipliers, setCustomMultipliers] = useState({}); // businessId-specific multipliers
  const [editableMultipliers, setEditableMultipliers] = useState({}); // Current editing state
  const [isCreatingRecipes, setIsCreatingRecipes] = useState(false);
  const [recipeCreationProgress, setRecipeCreationProgress] = useState({ current: 0, total: 0, message: '' });
  // Add loading state for the save operation
  const [isSavingMultipliers, setIsSavingMultipliers] = useState(false);
  
  // Delete success modal state
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);
  const [deleteModalMessage, setDeleteModalMessage] = useState('');
  
  // Delete confirmation modal state
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  
  
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

  // Close complimentary dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (complimentaryDropdownRef.current && !complimentaryDropdownRef.current.contains(e.target)) {
        setShowComplimentaryDropdown(false);
      }
    };
    if (showComplimentaryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showComplimentaryDropdown]);




  // Load recipes, unit options, and inventory std units from API
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const businessId = UnitMappingService.getBusinessId();

      // SP9 (A1): fire every opening request AT ONCE — six requests of ~300 ms are one
      // round trip in parallel, not a 1.8 s chain. Consumption below stays in order.
      const pRecipes = RecipesService.getRecipes(businessId);
      // O-1: the route needs the login token (it answered 401 without it, and the list stayed on its fallback).
      const categoriesToken = localStorage.getItem('token');
      const pCategories = fetch(`${process.env.REACT_APP_API_URL}/menu/categories?businessId=${businessId}`, {
        headers: categoriesToken ? { Authorization: `Bearer ${categoriesToken}` } : undefined,
      }).catch(() => null);
      const pUnits = UnitMappingService.getUnitOptions();
      const pKitchen = UnitMappingService.getIngredientKitchenUnits(businessId, 'kitchen');
      const pInventory = InventoryService.getInventoryItems(businessId, 500);
      const pCompLibrary = loadComplimentaryRecipesLibrary();
      const pRecipeLibrary = RecipeLibraryService.list();
      // Mark handled so a fast failure can't raise an unhandled-rejection warning while a
      // slower request is still being awaited; each await below still throws normally.
      [pRecipes, pUnits, pKitchen, pInventory, pCompLibrary, pRecipeLibrary].forEach((p) => p.catch(() => {}));

      const recipesData = await pRecipes;
      const normalized = recipesData.map(r => ({ ...r })); // Normalize recipes data
      setRecipes(applyLocalReviewDrafts(normalized));
      // SP9 (A3): the list IS the screen — paint it now; the lookups keep loading behind it.
      setLoading(false);
      // Dynamic categories are computed automatically from recipes state and toggle selection

      // Fetch menu categories from DB
      try {
        const response = await pCategories;
        if (response && response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            // setAvailableCategories removed - unused state
            debugLog('📂 Loaded categories:', data.data.map(cat => cat.name).join(', '));
          }
        }
      } catch (e) {
        console.warn('Failed to load menu categories:', e);
        // Fallback categories removed - unused
      }

      const unitOptionsData = await pUnits;
      // Build a minimal, global base list only (do NOT include custom Count units)
      // Keep mapping dropdown clean; ingredient-specific tools are merged later per item.
      // Base units (global for all)
      const allowedBase = ['g', 'kg', 'ml', 'L', 'pcs'];
      const bySymbol = new Map();
      for (const group of ['kitchen', 'supplier', 'container']) {
        for (const u of unitOptionsData[group] || []) {
          const sym = (u.unit_symbol || '').trim();
          if (!sym) continue;
          if (!bySymbol.has(sym)) bySymbol.set(sym, u);
        }
      }
      const baseUnits = allowedBase
        .filter(sym => sym && (bySymbol.has(sym) || true))
        .map(sym => ({ value: sym, label: sym }));
      setUnitOptions(baseUnits);

      // Fetch ingredient-scoped kitchen conversions and build a lookup for per-ingredient extra options
      try {
        const convs = await pKitchen;
        // convs: [{ item, kitchen_tool, unit, quantity }]
        const byItem = {};
        const byItemConversions = {};
        for (const r of convs || []) {
          const itemName = (r.item || '').trim();
          const toolSym = (r.kitchen_tool || '').trim();
          const outUnit = (r.unit || '').trim();
          const qty = Number(r.quantity);
          if (!itemName || !toolSym || !outUnit) continue;
          // Represent tool symbol itself as a selectable unit in mapping UI
          const key = itemName.toLowerCase();
          const normalizedTool = normalizeKitchenTool(toolSym);
          if (!normalizedTool.symbol) continue;
          if (!byItem[key]) byItem[key] = new Map();
          // Avoid duplicates; prefer lowercase label
          if (!byItem[key].has(normalizedTool.symbol)) {
            byItem[key].set(normalizedTool.symbol, { value: normalizedTool.symbol, label: normalizedTool.label });
          }
          if (!byItemConversions[key]) byItemConversions[key] = [];
          if (Number.isFinite(qty) && qty > 0) {
            byItemConversions[key].push({
              tool: normalizedTool.symbol,
              unit: outUnit,
              quantity: qty
            });
          }
        }
        // Convert maps to arrays
        const finalized = {};
        Object.keys(byItem).forEach(k => finalized[k] = Array.from(byItem[k].values()));
        setIngredientToolUnitsMap(finalized);
        setIngredientToolConversionsMap(byItemConversions);
      } catch (e) {
        console.warn('Failed to load ingredient kitchen units (optional):', e);
      }

      // Fetch inventory items to know each item's standard unit (e.g., ml/L for liquids)
      try {
        const inv = await pInventory;
        const map = {};
        const names = [];
        const byId = {};
        for (const it of inv.data || []) {
          if (it?.name) {
            map[it.name.trim().toLowerCase()] = it.standardUnit || null;
            names.push(it.name.trim());
            const id = it.item_id ?? it.id;
            if (id !== undefined && id !== null) byId[String(id)] = it.name.trim();
          }
        }
        setInventoryStdUnitMap(map);
        setInventoryItemNames(names);
        setInventoryNameById(byId);
      } catch (e) {
        console.warn('Failed to fetch inventory items for standard units', e);
      }
      
      // 🆕 Load complimentary recipes library (advanced system) — started up front
      await pCompLibrary;

      // Load recipe library for import suggestions
      try {
        const libList = await pRecipeLibrary;
        setRecipeLibraryList(libList || []);
      } catch (e) {
        console.warn('Failed to load recipe library list:', e);
      }
      
    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Failed to load recipes. Please try again.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Reacts to an inventory rename / merge / unit change made from the pencil menu.
   * Shared by the desktop and mobile mounts so both behave identically.
   *
   * loadData() refreshes the recipe list and the lookup maps, but it never rebuilds
   * `ingredientList` — those are the OPEN recipe's editable rows, produced by the
   * fetch effect keyed on selectedRecipe.id. That is why these edits used to show up
   * only after leaving the screen and reopening it. Here the on-screen rows, the
   * ingredient form, the unit map, and the SAVED DRAFTS are all patched directly.
   *
   * Drafts matter as much as the visible rows: the fetch effect prefers a saved draft
   * over the server, so a draft written before the change would otherwise put the old
   * name straight back on the next open.
   */
  const handleInventoryItemChanged = useCallback((changeInfo) => {
    loadData();
    loadSavedPreparations();

    const mode = changeInfo?.mode;
    const sourceName = String(changeInfo?.itemName || changeInfo?.sourceName || '').trim();
    const sourceKey = sourceName.toLowerCase();
    if (!mode || !sourceKey) return;

    const businessId = UnitMappingService.getBusinessId();
    const nameOf = (row) => String(row?.name || '').trim().toLowerCase();

    // Row transform per mode, applied to BOTH the live list and the dirty-state
    // snapshot so a system-applied change is never mistaken for a user edit.
    let transformRows;

    if (mode === 'unit') {
      const newUnit = changeInfo.newUnit;
      if (!newUnit) return;
      transformRows = (rows) => rows.map((row) => (nameOf(row) === sourceKey ? { ...row, unit: newUnit } : row));

      setFormData((prev) => (nameOf(prev) === sourceKey ? { ...prev, unit: newUnit } : prev));
      setInventoryStdUnitMap((prev) => ({ ...prev, [sourceKey]: newUnit }));
      applyIngredientNameChangeToDrafts(businessId, { from: sourceName, to: sourceName, mode: 'unit', newUnit });

    } else if (mode === 'rename') {
      const newName = String(changeInfo.newName || '').trim();
      if (!newName) return;
      transformRows = (rows) => rows.map((row) => (nameOf(row) === sourceKey ? { ...row, name: newName } : row));

      setFormData((prev) => (nameOf(prev) === sourceKey ? { ...prev, name: newName } : prev));
      setInventoryStdUnitMap((prev) => {
        if (!(sourceKey in prev)) return prev;
        const next = { ...prev };
        next[newName.toLowerCase()] = next[sourceKey];
        delete next[sourceKey];
        return next;
      });
      applyIngredientNameChangeToDrafts(businessId, { from: sourceName, to: newName, mode: 'rename' });

    } else if (mode === 'merge') {
      const targetName = String(changeInfo.targetName || '').trim();
      if (!targetName) return;
      const targetKey = targetName.toLowerCase();
      // Mirrors the backend merge: when the recipe already uses the target, the
      // source's row is deleted; otherwise the row is re-pointed (keeping its
      // quantity and unit), which reads as a rename here.
      transformRows = (rows) => {
        const hasTarget = rows.some((row) => nameOf(row) === targetKey);
        return rows.reduce((acc, row) => {
          if (nameOf(row) !== sourceKey) { acc.push(row); return acc; }
          if (hasTarget) return acc;
          acc.push({ ...row, name: targetName });
          return acc;
        }, []);
      };

      setFormData((prev) => (nameOf(prev) === sourceKey ? { ...prev, name: targetName } : prev));
      setInventoryStdUnitMap((prev) => {
        if (!(sourceKey in prev)) return prev;
        const next = { ...prev };
        delete next[sourceKey];
        return next;
      });
      applyIngredientNameChangeToDrafts(businessId, { from: sourceName, to: targetName, mode: 'merge' });

    } else {
      return;
    }

    setIngredientList((prev) => (Array.isArray(prev) ? transformRows(prev) : prev));
    originalIngredientSnapshotRef.current = transformRows(originalIngredientSnapshotRef.current || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData, loadSavedPreparations]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-select recipe if menuItemId is stored (e.g., from TSR navigation)
  useEffect(() => {
    if (!autoSelectAttempted && recipes.length > 0) {
      const autoSelectId = localStorage.getItem('autoSelectRecipeId');
      const autoSelectName = localStorage.getItem('autoSelectRecipeName');
      
      if (autoSelectId || autoSelectName) {
        debugLog('📋 All recipe names:', recipes.map(r => r.name).join(', '));
        debugLog('📋 First 5 recipes with IDs:', recipes.slice(0, 5).map(r => ({ 
          id: r.id, 
          name: r.name 
        })));
        
        // Try to find by ID first
        let recipeToSelect = autoSelectId ? recipes.find(r => 
          String(r.id) === String(autoSelectId) || 
          String(r.menu_item_id) === String(autoSelectId) ||
          String(r.menuItemId) === String(autoSelectId) ||
          String(r.recipe_id) === String(autoSelectId)
        ) : null;
        
        // Fallback: try to find by name if ID matching failed (case-insensitive, trimmed)
        if (!recipeToSelect && autoSelectName) {
          debugLog('🔍 ID match failed, trying name match...');
          const searchName = autoSelectName.toLowerCase().trim();
          recipeToSelect = recipes.find(r => 
            r.name && r.name.toLowerCase().trim() === searchName
          );
          if (recipeToSelect) {
            debugLog(' Found recipe by name:', recipeToSelect.name);
          } else {
            // Try partial match as last resort
            debugLog('🔍 Exact name match failed, trying partial match...');
            recipeToSelect = recipes.find(r => 
              r.name && (
                r.name.toLowerCase().includes(searchName) ||
                searchName.includes(r.name.toLowerCase())
              )
            );
            if (recipeToSelect) {
              debugLog(' Found recipe by partial name match:', recipeToSelect.name);
            }
          }
        }
        
        if (recipeToSelect) {
          debugLog('🎯 Auto-selecting recipe:', recipeToSelect.name, '(ID:', recipeToSelect.id, ')');
          setSelectedRecipe({
            ...recipeToSelect,
            image: recipeToSelect.image_url || recipeToSelect.image
          });
        } else {
          console.warn('⚠️ No recipe found matching ID:', autoSelectId, 'or name:', autoSelectName);
          debugLog('Available recipe names:', recipes.map(r => `"${r.name}"`).slice(0, 10).join(', '));
        }
        
        // Clear the localStorage flags
        localStorage.removeItem('autoSelectRecipeId');
        localStorage.removeItem('autoSelectRecipeName');
      }
      setAutoSelectAttempted(true);
    }
  }, [recipes, autoSelectAttempted]);

  // Load ingredients for selected recipe
  const loadRecipeIngredients = async (recipeId) => {
    try {
      const ingredients = await RecipesService.getRecipeIngredients(recipeId, UnitMappingService.getBusinessId());
      return ingredients;
    } catch (error) {
      console.error('Failed to load recipe ingredients:', error);
      return [];
    }
  };

  // 🆕 Fetch complimentary items - MenuItems first, then RecipeLibrary fallback
  useEffect(() => {
    const fetchComplimentaryData = async () => {
      if (!selectedRecipe || !selectedRecipe.name) {
        return;
      }
      
      // Reset selections when switching recipes
      setSelectedComplimentaryItems([]);
      
      try {
        const businessId = UnitMappingService.getBusinessId();
        const localReviewDraft = getRecipeReviewDraft(selectedRecipe, businessId);
        const menuItemId = selectedRecipe.menu_item_id || selectedRecipe.id;
        // 🆕 LOAD ADVANCED COMPLIMENTARY SYSTEM DATA
        await loadLinkedComplimentaryItems(menuItemId);
        
        // STEP 1: Try to fetch from MenuItems (business-specific)
        const menuItemData = await RecipesService.getRecipe(menuItemId, businessId);
        
        let complimentaryData = [];
        let needsTemplateData = false;
        
        // Check if MenuItems has data
        if (menuItemData.data) {
          const item = menuItemData.data;
          
          if (item.complimentary_items && Array.isArray(item.complimentary_items) && item.complimentary_items.length > 0) {
            complimentaryData = normalizeComplimentaryItems(item.complimentary_items);
            debugLog(` Using MenuItems complimentary_items (${complimentaryData.length} items):`, complimentaryData);
          } else {
            needsTemplateData = true;
          }
        } else {
          needsTemplateData = true;
        }
        
        // STEP 2: If MenuItems doesn't have data, fallback to RecipeLibrary template
        if (needsTemplateData) {
          debugLog('📚 Fetching from RecipeLibrary template...');
          // Most dishes (e.g. "Bellam Coffee 2") are not in the library — a 404 here just
          // means "no template", not a failure of the whole load.
          let templateData = null;
          try {
            templateData = await RecipeLibraryService.getRecipe(selectedRecipe.name, businessId);
          } catch (libError) {
            debugLog(`📚 No RecipeLibrary template for "${selectedRecipe.name}": ${libError.message}`);
          }

          if (templateData) {
            if (!complimentaryData.length && templateData.complimentary_items && Array.isArray(templateData.complimentary_items)) {
              complimentaryData = normalizeComplimentaryItems(templateData.complimentary_items);
              debugLog(` Using RecipeLibrary template complimentary_items (${complimentaryData.length} items)`);
            }
            
            // STEP 3: Copy template data to MenuItems for future use
            if (complimentaryData.length > 0) {
              debugLog('💾 Copying template data to MenuItems for business-specific storage...');
              try {
                await RecipesService.updateRecipeCustomizations(menuItemId, {
                  complimentary_items: complimentaryData
                }, businessId);
                debugLog(' Template data copied to MenuItems');
              } catch (copyError) {
                console.warn('⚠️ Failed to copy template to MenuItems:', copyError);
              }
            }
          }
        }
        
        // Set state
        setComplimentaryItems(complimentaryData);
        setSelectedComplimentaryItems(
          Array.isArray(localReviewDraft?.selectedComplimentaryItems)
            ? localReviewDraft.selectedComplimentaryItems
            : complimentaryData
        );
        debugLog(` Final state: ${complimentaryData.length} complimentary items`);
        
      } catch (error) {
        console.error('❌ Failed to fetch complimentary data:', error);
        console.error('Error details:', error.message, error.stack);
        // Set empty arrays as fallback
        setComplimentaryItems([]);
      }
    };
    
    fetchComplimentaryData();
  }, [selectedRecipe?.id, selectedRecipe?.menu_item_id]);

  // Reactively fetch ingredients whenever the selected recipe changes
  useEffect(() => {
    const fetchIngredients = async () => {
      if (selectedRecipe && selectedRecipe.id) {
        debugLog(`🔄 Fetching ingredients for selected recipe: ${selectedRecipe.name} (ID: ${selectedRecipe.id})`);
        try {
          const businessId = UnitMappingService.getBusinessId();
          const localReviewDraft = getRecipeReviewDraft(selectedRecipe, businessId);

          if (Array.isArray(localReviewDraft?.ingredientList)) {
            const draftList = localReviewDraft.ingredientList.length > 0
              ? localReviewDraft.ingredientList
              : [{ name: 'New Ingredient', unit: '', qty10: 0, qty100: 0 }];

            setIngredientList(JSON.parse(JSON.stringify(draftList)));
            originalIngredientSnapshotRef.current = buildIngredientSnapshot(draftList);
            setPlateStep(1);
            setCustomPlateStep('');
            return;
          }

          const ingredients = await RecipesService.getRecipeIngredients(selectedRecipe.id, businessId);

          debugLog(` Loaded ${ingredients.length} ingredients from backend:`, ingredients);

          const list = (ingredients.length > 0
            ? ingredients.map(ing => {
              const ingredientName = ing.ingredient_name || ing.name || '';
              const nameKey = ingredientName.trim().toLowerCase();
              const std = inventoryStdUnitMap[nameKey];
              let unitSym = ing.unit || std || 'g';
              let perPlateQty = ing.quantity; // backend per-plate (now standard)

              //  FIX: Multi-hop reverse conversion for kitchen units
              // Try to find ingredient-specific kitchen conversion for display
              const convs = ingredientToolConversionsMap[nameKey] || [];
              
              // First, try direct match (backend unit = conversion output unit)
              let matchedConv = convs.find(c => {
                const outUnit = (c.unit || '').trim();
                return outUnit && outUnit.toLowerCase() === String(unitSym || '').trim().toLowerCase();
              });
              
              // If no direct match, try multi-hop conversion (L → ml, kg → g)
              if (!matchedConv && convs.length > 0) {
                debugLog(`🔄 [Display] Multi-hop reverse conversion for ${ingredientName}: ${unitSym}`);
                
                // Try converting backend unit to intermediate unit
                for (const conv of convs) {
                  const convOutUnit = (conv.unit || '').trim().toLowerCase();
                  
                  // Check if we can convert backend unit to conversion's output unit
                  // Example: Backend has 'L', conversion has 'ml' → convert L to ml first
                  if (convOutUnit !== unitSym.toLowerCase()) {
                    // Try standard conversions (L→ml, kg→g, ml→L, g→kg)
                    let intermediateQty = perPlateQty;
                    let canConvert = false;
                    
                    // L → ml
                    if (unitSym.toLowerCase() === 'l' && convOutUnit === 'ml') {
                      intermediateQty = perPlateQty * 1000;
                      canConvert = true;
                    }
                    // ml → L
                    else if (unitSym.toLowerCase() === 'ml' && convOutUnit === 'l') {
                      intermediateQty = perPlateQty / 1000;
                      canConvert = true;
                    }
                    // kg → g
                    else if (unitSym.toLowerCase() === 'kg' && convOutUnit === 'g') {
                      intermediateQty = perPlateQty * 1000;
                      canConvert = true;
                    }
                    // g → kg
                    else if (unitSym.toLowerCase() === 'g' && convOutUnit === 'kg') {
                      intermediateQty = perPlateQty / 1000;
                      canConvert = true;
                    }
                    
                    if (canConvert && conv.quantity > 0) {
                      debugLog(` [Display] Found multi-hop: ${unitSym} → ${conv.unit} → ${conv.tool}`);
                      debugLog(`   Quantity: ${perPlateQty} ${unitSym} → ${intermediateQty} ${conv.unit} → ${intermediateQty / conv.quantity} ${conv.tool}`);
                      matchedConv = conv;
                      perPlateQty = intermediateQty;
                      break;
                    }
                  }
                }
              }
              
              if (matchedConv && matchedConv.quantity > 0) {
                unitSym = matchedConv.tool;
                perPlateQty = Number((perPlateQty / matchedConv.quantity).toFixed(4));
                debugLog(` [Display] Using kitchen unit: ${perPlateQty} ${unitSym} for ${ingredientName}`);
              } else {
                if ((ing.unit === 'g' || ing.unit === 'kg') && (std === 'ml' || std === 'L')) {
                  unitSym = std;
                  perPlateQty = convertQuantity(ing.quantity, ing.unit || 'g', std);
                }
                if ((ing.unit === 'ml' || ing.unit === 'L') && (std === 'g' || std === 'kg')) {
                  unitSym = std;
                  perPlateQty = convertQuantity(ing.quantity, ing.unit || 'ml', std);
                }
              }

              // SP9 sacred numbers: what was saved is what you see — no auto-scaling on load.
              const normPerPlate = { value: perPlateQty, unit: unitSym };
              const norm = { value: Number((normPerPlate.value * 10).toFixed(4)), unit: normPerPlate.unit };

              return {
                name: ingredientName,
                unit: norm.unit,
                qty10: norm.value,
                qty100: 0,
                inventoryId: ing.inventory_id || null,  // Fixed: backend returns inventory_id not inventoryId
                id: ing.id || null  // Fixed: backend returns id (recipe_ingredient_id)
              };
            })
            : [{ name: 'New Ingredient', unit: '', qty10: 0, qty100: 0 }]);

          // Sort ingredients alphabetically by name (A–Z, case-insensitive)
          const sortedList = [...list].sort((a, b) =>
            (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
          );

          setIngredientList(sortedList);
          originalIngredientSnapshotRef.current = buildIngredientSnapshot(sortedList);

          setPlateStep(1);
          setCustomPlateStep('');
        } catch (error) {
          console.error(`Failed to load ingredients for recipe ${selectedRecipe.id}:`, error);
          showToast('Error loading ingredients');
          setIngredientList([]);
          originalIngredientSnapshotRef.current = [];
        }
      } else {
        setIngredientList([]); // Clear ingredients if no recipe is selected
        originalIngredientSnapshotRef.current = [];
      }
    };

    fetchIngredients();
  }, [selectedRecipe?.id]);

  // 🔄 Auto-save ingredients locally on change (debounced 1.5s, no DB write)
  useEffect(() => {
    // Skip auto-save on initial load / recipe switch
    if (ingredientListInitRef.current) {
      ingredientListInitRef.current = false;
      return;
    }
    if (!selectedRecipe?.id) return;
    // Only auto-save if there are valid ingredients
    const valid = ingredientList.filter(i => (i.name || '').trim() && i.name !== 'New Ingredient' && (i.qty10 > 0 || i.qty100 > 0));
    if (valid.length === 0) return;
    // Debounce — saves even while editing (live sync keeps ingredientList up-to-date)
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveIngredients();
    }, 1500);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [ingredientList]);

  // Reset init ref when recipe changes so first load of new recipe doesn't auto-save
  useEffect(() => {
    ingredientListInitRef.current = true;
  }, [selectedRecipe?.id]);

  // 🔄 Keep selected recipe image in sync when recipes list updates
  useEffect(() => {
    if (!selectedRecipe?.id || !Array.isArray(recipes) || recipes.length === 0) return;
    const latest = recipes.find(r => (r.menu_item_id || r.id) === selectedRecipe.id);
    if (!latest) return;

    const latestImageUrl = (latest.image_url || '').trim();
    const currentImageUrl = (selectedRecipe.image_url || '').trim();
    if (latestImageUrl && latestImageUrl !== currentImageUrl) {
      setSelectedRecipe(withImageMeta({
        ...selectedRecipe,
        ...latest,
        id: latest.menu_item_id || latest.id
      }));
    }
  }, [recipes, selectedRecipe?.id]);

  // 🆕 Load custom multipliers for this business from DATABASE
  useEffect(() => {
    const loadCustomMultipliers = async () => {
      try {
        const businessId = UnitMappingService.getBusinessId();
        const token = localStorage.getItem('token');
        
        // Robust URL construction
        const baseUrl = process.env.REACT_APP_API_URL;
        const endpoint = baseUrl.endsWith('/api') ? '/portion-multipliers' : '/api/portion-multipliers';
        const fullUrl = `${baseUrl.replace(/\/api$/, '')}${baseUrl.endsWith('/api') ? '' : ''}${endpoint}`;
        // Actually simpler: 
        // If baseUrl ends in /api, use it directly + /portion-multipliers
        // If not, use baseUrl + /api/portion-multipliers
        const apiUrl = baseUrl.endsWith('/api') 
          ? `${baseUrl}/portion-multipliers` 
          : `${baseUrl}/api/portion-multipliers`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'x-business-id': businessId
          }
        });
        if (!response.ok) throw new Error(`Failed to load multipliers (${response.status})`);
        
        const data = await response.json();
        
        if (data.success && data.multipliers) {
          setCustomMultipliers(data.multipliers);
        }
      } catch (error) {
        console.error('Failed to load custom multipliers from DB:', error);
        // Fallback to localStorage if DB fails
        try {
          const businessId = UnitMappingService.getBusinessId();
          const storageKey = `customMultipliers_${businessId}`;
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            const parsed = JSON.parse(stored);
            setCustomMultipliers(parsed);
            debugLog(` Loaded custom multipliers from localStorage as fallback:`, parsed);
          }
        } catch (localError) {
          console.error('Fallback to localStorage also failed:', localError);
        }
      }
    };
    
    loadCustomMultipliers();
  }, []);

  // 🆕 Save custom multipliers to DATABASE
  const saveCustomMultipliers = async (multipliers) => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      const token = localStorage.getItem('token');
      
      const baseUrl = process.env.REACT_APP_API_URL;
      const apiUrl = baseUrl.endsWith('/api') 
          ? `${baseUrl}/portion-multipliers` 
          : `${baseUrl}/api/portion-multipliers`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        },
        body: JSON.stringify({ multipliers })
      });
      if (!response.ok) throw new Error(`Failed to save multipliers (${response.status})`);
      
      const data = await response.json();
      
      if (data.success) {
        setCustomMultipliers(multipliers);
        debugLog(`💾 Saved ${data.count} custom multipliers to DB for business ${businessId}:`, multipliers);
        showToast(' Multipliers saved to database successfully!');
        
        // Also save to localStorage as backup
        const storageKey = `customMultipliers_${businessId}`;
        localStorage.setItem(storageKey, JSON.stringify(multipliers));
      } else {
        throw new Error(data.error || 'Failed to save multipliers');
      }
    } catch (error) {
      console.error('Failed to save custom multipliers to DB:', error);
      showToast('❌ Failed to save multipliers to database');
      throw error;
    }
  };

  // Delete recipe handler
  const deleteRecipe = async (id) => {
    setRecipeToDelete(id);
    setShowDeleteConfirmModal(true);
  };
  
  const handleConfirmDelete = async () => {
    const id = recipeToDelete;
    setShowDeleteConfirmModal(false);
    setRecipeToDelete(null);
    
    if (!id) return;
    
    try {
      const businessId = UnitMappingService.getBusinessId();
      const result = await RecipesService.deleteRecipe(id, businessId);
      clearRecipeReviewDraft({ id }, businessId);
      
      // Refresh recipe list from server to get updated data
      debugLog('🔄 Refreshing recipe list...');
      const refreshedRecipes = await RecipesService.getRecipes(businessId);
      setRecipes(applyLocalReviewDrafts(refreshedRecipes));
      
      if (result.softDelete) {
        setDeleteModalMessage('Item Removed Successfully');
        setShowDeleteSuccessModal(true);
      } else {
        setDeleteModalMessage('Item Deleted Successfully');
        setShowDeleteSuccessModal(true);
      }
    } catch (error) {
      console.error('❌ Failed to delete recipe:', error);
      
      try {
        const businessId = UnitMappingService.getBusinessId();
        const refreshedRecipes = await RecipesService.getRecipes(businessId);
        setRecipes(applyLocalReviewDrafts(refreshedRecipes));
      } catch (refreshError) {
        console.error('Failed to refresh recipe list:', refreshError);
      }
      
      if (error.response?.data?.constraint) {
        setDeleteModalMessage('Item Removed Successfully');
        setShowDeleteSuccessModal(true);
      } else {
        setDeleteModalMessage('Failed To Delete Recipe');
        setShowDeleteSuccessModal(true);
      }
    }
  };

  // 🆕 Get multiplier for a portion keyword (custom or default)
  const getMultiplierForKeyword = (keyword) => {
    const businessId = UnitMappingService.getBusinessId();
    const keyLower = keyword.toLowerCase();
    
    // Check if business has custom multiplier for this keyword
    if (customMultipliers[keyLower] !== undefined) {
      return customMultipliers[keyLower];
    }
    
    // Fall back to default multipliers
    return calculatePortionMultiplier(keyword);
  };

  // SP9 (A4): incremental card rendering — ~30 cards up front, more as the sentinel
  // scrolls into view. Grid-safe, no dependency, cheap phones stop building 200 cards.
  const CARD_BATCH = 30;
  const [visibleCardCount, setVisibleCardCount] = useState(CARD_BATCH);
  const cardSentinelRef = useRef(null);
  useEffect(() => {
    setVisibleCardCount(CARD_BATCH);
  }, [filter, search, useMasterCategories]);
  useEffect(() => {
    const node = cardSentinelRef.current;
    if (!node || typeof window === 'undefined' || !window.IntersectionObserver) return undefined;
    const observer = new window.IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisibleCardCount((prev) => prev + CARD_BATCH);
      }
    }, { rootMargin: '600px' });
    observer.observe(node);
    return () => observer.disconnect();
  });

  const filteredRecipes = sortBySearchPriority(
    recipes.filter((recipe) => {
      const orig = editingOriginalsRef.current[recipe.id];
      const status = orig ? orig.status : recipe.status;
      const key = useMasterCategories ? 'master_category' : 'category';
      const category = orig ? orig[key] : recipe[key];

      if (filter === 'All') return true;
      if (filter === 'Pending') return status !== 'confirmed';
      if (filter === 'Under Review') return status === 'draft';
      return (category || 'Uncategorized') === filter;
    }),
    search,
    (recipe) => {
      const orig = editingOriginalsRef.current[recipe.id];
      return orig ? orig.name : (recipe?.name || '');
    },
    { sortWhenEmpty: true }
  );

  const categoryFilterOptions = ['All', ...categories, 'Under Review', 'Pending'];
  const isCategoryCollapsed = hasCategoryChipOverflow && !showAllCategoryChips;
  const visibleCategoryOptions = isCategoryCollapsed
    ? categoryFilterOptions.slice(0, collapsedCategoryCount || categoryFilterOptions.length)
    : categoryFilterOptions;

  useEffect(() => {
    setShowAllCategoryChips(false);
  }, [categories]);

  useEffect(() => {
    const measureCategoryRows = () => {
      const measureContainer = categoryMeasureRef.current;
      if (!measureContainer) return;

      const chips = Array.from(measureContainer.querySelectorAll('[data-measure-chip="true"]'));
      const viewAllChip = measureContainer.querySelector('[data-measure-view-all="true"]');

      if (!chips.length || !viewAllChip) {
        setHasCategoryChipOverflow(false);
        setCollapsedCategoryCount(categoryFilterOptions.length);
        return;
      }

      const getRowCount = (elements) => {
        const rowTops = [];
        elements.forEach((el) => {
          if (el.style.display === 'none') return;
          const top = Math.round(el.offsetTop);
          if (!rowTops.some((existingTop) => Math.abs(existingTop - top) <= 2)) {
            rowTops.push(top);
          }
        });
        return rowTops.length;
      };

      chips.forEach((chip) => { chip.style.display = ''; });
      viewAllChip.style.display = 'none';

      const contentOverflowsTwoRows = getRowCount(chips) > 2;
      setHasCategoryChipOverflow((prev) => (prev === contentOverflowsTwoRows ? prev : contentOverflowsTwoRows));

      if (!contentOverflowsTwoRows) {
        setCollapsedCategoryCount((prev) => (prev === chips.length ? prev : chips.length));
        return;
      }

      let maxVisibleCount = 0;
      for (let count = chips.length; count >= 0; count -= 1) {
        chips.forEach((chip, idx) => {
          chip.style.display = idx < count ? '' : 'none';
        });
        viewAllChip.style.display = '';

        const rowsWithToggle = getRowCount([...chips, viewAllChip]);
        if (rowsWithToggle <= 2) {
          maxVisibleCount = count;
          break;
        }
      }

      setCollapsedCategoryCount((prev) => (prev === maxVisibleCount ? prev : maxVisibleCount));

      chips.forEach((chip) => { chip.style.display = ''; });
      viewAllChip.style.display = 'none';
    };

    const frameId = window.requestAnimationFrame(measureCategoryRows);
    const timeoutId = window.setTimeout(measureCategoryRows, 80);
    window.addEventListener('resize', measureCategoryRows);
    let resizeObserver;
    if (window.ResizeObserver && categoryMeasureRef.current) {
      resizeObserver = new window.ResizeObserver(measureCategoryRows);
      resizeObserver.observe(categoryMeasureRef.current);
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', measureCategoryRows);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [categoryFilterOptions.join('|')]);

  useEffect(() => {
    if (showAllCategoryChips || !hasCategoryChipOverflow) return;

    const frameId = window.requestAnimationFrame(() => {
      const visibleContainer = categoryChipsRef.current;
      if (!visibleContainer) return;

      const toggleChip = visibleContainer.querySelector('[data-category-toggle="true"]');
      if (!toggleChip) return;

      const visibleElements = Array.from(
        visibleContainer.querySelectorAll('[data-visible-category-chip="true"], [data-category-toggle="true"]')
      );

      const rowTops = [];
      visibleElements.forEach((el) => {
        const top = Math.round(el.offsetTop);
        if (!rowTops.some((existingTop) => Math.abs(existingTop - top) <= 2)) {
          rowTops.push(top);
        }
      });

      rowTops.sort((a, b) => a - b);

      const toggleTop = Math.round(toggleChip.offsetTop);
      const toggleRowIndex = rowTops.findIndex((rowTop) => Math.abs(rowTop - toggleTop) <= 2);
      const toggleRow = toggleRowIndex >= 0 ? toggleRowIndex + 1 : 1;

      if (toggleRow > 2 && collapsedCategoryCount > 0) {
        setCollapsedCategoryCount((prev) => Math.max(prev - 1, 0));
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [visibleCategoryOptions.length, hasCategoryChipOverflow, showAllCategoryChips, collapsedCategoryCount]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target.result);
        setShowNewRecipeForm(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const createNewRecipes = async () => {
    // Check for duplicate names in the new batch or against existing recipes
    const namesInNewBatch = recipeCards.map(c => c.name ? c.name.trim().toLowerCase() : '');
    const existingNames = recipes.map(r => r.name.trim().toLowerCase());
    const nonMockNames = namesInNewBatch.filter(Boolean);
    
    const hasDuplicatesInBatch = nonMockNames.some((val, idx) => nonMockNames.indexOf(val) !== idx);
    const hasExistingDuplicates = nonMockNames.some(name => existingNames.includes(name));
    
    if (hasDuplicatesInBatch || hasExistingDuplicates) {
      setShowDuplicateModal(true);
      return;
    }

    const errors = {};
    let hasError = false;
    recipeCards.forEach(card => {
      const nameInvalid = !card.name || !card.name.trim();
      const priceInvalid = card.price === '' || card.price === undefined || card.price === null || isNaN(parseFloat(card.price));
      const servingsInvalid = card.servings === '' || card.servings === undefined || card.servings === null || isNaN(parseInt(card.servings));
      
      if (nameInvalid || priceInvalid || servingsInvalid) {
        hasError = true;
        errors[card.id] = {
          name: nameInvalid,
          price: priceInvalid,
          servings: servingsInvalid
        };
      }
    });

    if (hasError) {
      setCreationErrors(errors);
      showToast('All fields (Item Name, Price, and Servings) are required for all items.', 'warning');
      return;
    }

    const validRecipes = recipeCards;

    try {
      setIsCreatingRecipes(true);
      setRecipeCreationProgress({ current: 0, total: 6, message: 'Starting recipe creation...' });
      
      // If only one recipe, navigate directly to ingredient mapping with multilingual support
      if (validRecipes.length === 1) {
        const card = validRecipes[0];
        const originalName = card.name; // Keep original language name for display
        
        debugLog(`📝 Creating recipe: "${originalName}"`);
        
        setRecipeCreationProgress({ current: 1, total: 6, message: `Translating recipe name...` });
        
        // Detect language and translate to English for DB storage and image generation
        let englishName = originalName;
        let sourceLanguage = 'en';
        
        try {
          const apiUrl = process.env.REACT_APP_API_URL;
          const translateResponse = await fetch(`${apiUrl}/medium-priority/translate-to-english`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: originalName })
          });
          
          if (translateResponse.ok) {
            const translateData = await translateResponse.json();
            if (translateData.success) {
              englishName = translateData.translatedText;
              sourceLanguage = translateData.sourceLanguage;
              debugLog(`🌍 Translation: "${originalName}" (${sourceLanguage}) → "${englishName}" (en)`);
            }
          }
        } catch (translateError) {
          console.warn('Translation failed, using original name:', translateError);
        }
        
        // Create recipe with English name in DB
        const recipeData = {
          name: englishName, // Store English name in DB
          displayName: originalName, // Store original name for display
          sourceLanguage: sourceLanguage, // Store detected language
          price: parseFloat(card.price) || 0,
          servings: parseInt(card.servings) || 1,
          ingredients: [],
          source: 'manual' // Flag for backend to generate image
        };
        
        debugLog('📝 Creating recipe with data:', recipeData);
        debugLog('🏢 Business ID:', UnitMappingService.getBusinessId());
        
        setRecipeCreationProgress({ current: 2, total: 6, message: `Item name: ${originalName}` });
        
        // Create the recipe with duplicate check
        let newRecipe;
        try {
          newRecipe = await RecipesService.createRecipe(recipeData, UnitMappingService.getBusinessId());
          debugLog(` Created recipe: ${englishName} (ID: ${newRecipe.id})`);
        } catch (createError) {
          const status = createError?.response?.status || createError?.status;
          // Handle invalid dish name error
          if (status === 400) {
            const errorData = createError?.response?.data || createError?.data;
            setIsCreatingRecipes(false);
            setRecipeCreationProgress({ current: 0, total: 0, message: '' });
            showToast(`❌ Invalid dish name: ${errorData?.details || 'Please enter a proper dish name.'}`, 'error');
            return;
          }
          // Handle duplicate item error
          if (status === 409) {
            const errorData = createError?.response?.data || createError?.data;
            setIsCreatingRecipes(false);
            setRecipeCreationProgress({ current: 0, total: 0, message: '' });
            showToast(`❌ Duplicate Item: An item named "${errorData?.existingItem?.name || englishName}" already exists.`, 'warning');
            return;
          }
          throw createError;
        }
        
        const businessId = UnitMappingService.getBusinessId();
        
        setRecipeCreationProgress({ current: 3, total: 6, message: 'Generating dish image...' });
        
        //  Image generation happens automatically in backend (fire-and-forget)
        debugLog(`🎨 Backend will generate dish image for "${englishName}" in background...`);
        
        // 🆕 AUTO-GENERATE INGREDIENTS (exactly like OCR scan flow)
        debugLog(`🤖 Generating complete recipe data for "${englishName}" using AI...`);
        setRecipeCreationProgress({ current: 4, total: 6, message: '' });
        
        let generatedIngredients = [];
        let generatedComplimentaryItems = [];
        let generatedDescription = '';
        try {
          const aiResponse = await fetch(`${process.env.REACT_APP_API_URL}/ai/generate-ingredients`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'x-business-id': businessId 
            },
            body: JSON.stringify({ 
              dishName: englishName, 
              plateCount: 10 
            })
          });
          if (!aiResponse.ok) throw new Error(`AI generation failed (${aiResponse.status})`);
          
          const aiData = await aiResponse.json();
          if (aiData.success && aiData.ingredients && aiData.ingredients.length > 0) {
            debugLog(` Generated complete recipe from ${aiData.source}:`);
            debugLog(`   - ${aiData.ingredients.length} ingredients`);
            debugLog(`   - ${aiData.complimentary_items?.length || 0} complimentary items`);
            
            // AI returns zero quantities with correct units — user fills in manually
            generatedIngredients = aiData.ingredients.map(ing => {
              // Extract unit from quantity_standard (e.g., "0 kg" → "kg")
              const unitMatch = (ing.quantity_standard || '').match(/[a-zA-Z]+/);
              const unit = unitMatch ? unitMatch[0] : (ing.unit || 'g');
              return {
                name: ing.ingredient || ing.name,
                quantity: 0,
                unit: unit
              };
            });
            
            // Store complimentary items and description
            generatedComplimentaryItems = aiData.complimentary_items || [];
            generatedDescription = aiData.description || '';
            
            // 🆕 UPDATE RECIPE WITH DESCRIPTION (before saving ingredients)
            if (generatedDescription) {
              debugLog(`💾 Updating recipe with description...`);
              try {
                await RecipesService.updateRecipe(newRecipe.id, {
                  description: generatedDescription
                }, businessId);
                debugLog(` Description saved to database`);
              } catch (descError) {
                console.warn('⚠️ Failed to save description:', descError);
              }
            }
            
            // 🆕 SAVE INGREDIENTS TO DATABASE (exactly like OCR scan flow)
            debugLog(`💾 Saving ${generatedIngredients.length} ingredients to database...`);
            await RecipesService.updateRecipeIngredients(newRecipe.id, generatedIngredients, businessId);
            debugLog(` Ingredients saved to database for recipe ID: ${newRecipe.id}`);
            
            // Update local state for complimentary items (no RecipeLibrary update)
            if (generatedComplimentaryItems.length > 0) {
              setComplimentaryItems(generatedComplimentaryItems);
            }
            
          } else {
            console.warn(`⚠️ No recipe data generated for ${englishName}`);
            showToast(`AI couldn't generate ingredients for "${englishName}". Add them manually.`, 'warning');
          }
        } catch (aiError) {
          console.error('AI recipe generation failed:', aiError);
          console.warn('Continuing with empty recipe data');
        }
        
        // 🆕 REFRESH RECIPE LIST (to show newly created recipe with image)
        setRecipeCreationProgress({ current: 5, total: 6, message: 'Finalizing recipe...' });
        const refreshedRecipes = await RecipesService.getRecipes(businessId);
        setRecipes(applyLocalReviewDrafts(refreshedRecipes));

        // Reset form
        setShowNewRecipeForm(false);
        setRecipeCards([{ id: Date.now(), name: '', price: '', servings: '' }]);
        setCapturedImage(null);

        // 🆕 AUTO-SELECT THE NEW RECIPE (exactly like OCR scan flow)
        // Find the recipe in the refreshed list - use menu_item_id for matching
        const createdRecipeWithImage = refreshedRecipes.find(r => r.menu_item_id === newRecipe.id || r.id === newRecipe.id);
        if (createdRecipeWithImage) {
          // Set the recipe with proper structure so useEffect loads ingredients
          setSelectedRecipe(withImageMeta({
            ...createdRecipeWithImage,
            id: createdRecipeWithImage.menu_item_id || createdRecipeWithImage.id,
          }));
          debugLog(` Auto-selected recipe: ${createdRecipeWithImage.name} (ID: ${createdRecipeWithImage.menu_item_id})`);
        } else {
          // Fallback: if not found in list, create minimal structure
          setSelectedRecipe(withImageMeta({
            id: newRecipe.id,
            menu_item_id: newRecipe.id,
            name: englishName,
            displayName: originalName,
            sourceLanguage: sourceLanguage,
            image_url: newRecipe.image_url || null,
            image: newRecipe.image_url || null,
          }));
          debugLog(`⚠️ Recipe not found in refreshed list, using created recipe data`);
        }
        
        // Dismiss loading
        setRecipeCreationProgress({ current: 6, total: 6, message: 'Complete!' });
        setTimeout(() => {
          setIsCreatingRecipes(false);
          setRecipeCreationProgress({ current: 0, total: 0, message: '' });
        }, 500);
        
        showToast(' Recipe created successfully!');
        
      } else {
        // Multiple recipes - process each one with COMPLETE OCR flow
        debugLog(`📝 Creating ${validRecipes.length} recipes with complete OCR flow...`);
        const businessId = UnitMappingService.getBusinessId();
        const createdRecipes = [];
        
        for (const card of validRecipes) {
          const originalName = card.name;
          let englishName = originalName;
          let sourceLanguage = 'en';
          
          // Translate to English if needed
          try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const translateResponse = await fetch(`${apiUrl}/medium-priority/translate-to-english`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text: originalName })
            });
            
            if (translateResponse.ok) {
              const translateData = await translateResponse.json();
              if (translateData.success) {
                englishName = translateData.translatedText;
                sourceLanguage = translateData.sourceLanguage;
              }
            }
          } catch (translateError) {
            console.warn('Translation failed for', originalName);
          }
          
          const recipeData = {
            name: englishName,
            displayName: originalName,
            sourceLanguage: sourceLanguage,
            price: parseFloat(card.price) || 0,
            servings: parseInt(card.servings) || 1,
            ingredients: [],
            source: 'manual'
          };
          
          // Create recipe with duplicate check
          let newRecipe;
          try {
            newRecipe = await RecipesService.createRecipe(recipeData, businessId);
            debugLog(` Created recipe: ${englishName} (ID: ${newRecipe.id})`);
            debugLog(`🎨 Backend generating image for "${englishName}" in background...`);
          } catch (createError) {
            const status = createError?.response?.status || createError?.status;
            // Handle invalid dish name error
            if (status === 400) {
              const errorData = createError?.response?.data || createError?.data;
              setIsCreatingRecipes(false);
              setRecipeCreationProgress({ current: 0, total: 0, message: '' });
              showToast(`❌ Invalid dish name: ${errorData?.details || 'Please enter a proper dish name.'}`, 'error');
              return;
            }
            // Handle duplicate item error
            if (status === 409) {
              console.warn(`⚠️ Skipping duplicate: ${englishName} already exists`);
              showToast(`⚠️ Skipped "${originalName}" - already exists in menu`, 'warning');
              continue;
            }
            throw createError;
          }
          
          // Generate ingredients with AI (like OCR)
          try {
            debugLog(`🤖 Generating complete recipe data for "${englishName}"...`);
            const aiResponse = await fetch(`${process.env.REACT_APP_API_URL}/ai/generate-ingredients`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'x-business-id': businessId 
              },
              body: JSON.stringify({ 
                dishName: englishName, 
                plateCount: 10 
              })
            });
            if (!aiResponse.ok) throw new Error(`AI generation failed (${aiResponse.status})`);
            
            const aiData = await aiResponse.json();
            if (aiData.success && aiData.ingredients && aiData.ingredients.length > 0) {
              debugLog(` Generated complete recipe from ${aiData.source}:`);
              debugLog(`   - ${aiData.ingredients.length} ingredients`);
              debugLog(`   - ${aiData.complimentary_items?.length || 0} complimentary items`);
              
              // AI returns zero quantities with correct units — user fills in manually
              const ingredients = aiData.ingredients.map(ing => {
                const unitMatch = (ing.quantity_standard || '').match(/[a-zA-Z]+/);
                const unit = unitMatch ? unitMatch[0] : (ing.unit || 'g');
                return {
                  name: ing.ingredient || ing.name,
                  quantity: 0,
                  unit: unit
                };
              });
              
              // Save ingredients to database (like OCR)
              debugLog(`💾 Saving ${ingredients.length} ingredients to database...`);
              await RecipesService.updateRecipeIngredients(newRecipe.id, ingredients, businessId);
              debugLog(` Ingredients saved for "${englishName}"`);
              
              // Save complimentary items to Recipe Library
              if (aiData.complimentary_items?.length > 0) {
                try {
                  await RecipeLibraryService.updateRecipe(englishName, {
                    complimentary_items: aiData.complimentary_items || []
                  }, businessId);
                  debugLog(` Saved complimentary items to Recipe Library`);
                } catch (saveError) {
                  console.warn('⚠️ Failed to save to Recipe Library:', saveError);
                }
              }
            } else {
              console.warn(`⚠️ No recipe data generated for ${englishName}`);
            }
          } catch (aiError) {
            console.error(`Failed to generate recipe data for ${englishName}:`, aiError);
          }
          
          createdRecipes.push({ original: originalName, english: englishName, id: newRecipe.id });
        }

        // Refresh recipe list to show all new recipes with images
        const refreshedRecipes = await RecipesService.getRecipes(businessId);
        setRecipes(applyLocalReviewDrafts(refreshedRecipes));

        // Reset form
        setShowNewRecipeForm(false);
        setRecipeCards([{ id: Date.now(), name: '', price: '', servings: '' }]);
        setCapturedImage(null);
        
        // Auto-select first created recipe with proper structure
        if (createdRecipes.length > 0 && refreshedRecipes.length > 0) {
          const firstCreated = refreshedRecipes.find(r => r.menu_item_id === createdRecipes[0].id || r.id === createdRecipes[0].id);
          if (firstCreated) {
            setSelectedRecipe(withImageMeta({
              ...firstCreated,
              id: firstCreated.menu_item_id || firstCreated.id,
            }));
            debugLog(` Auto-selected first recipe: ${firstCreated.name} (ID: ${firstCreated.menu_item_id || firstCreated.id})`);
          }
        }
        
        showToast(` Created ${validRecipes.length} recipes with AI ingredients!`);
      }
    } catch (error) {
      console.error('Failed to create recipes:', error);
      const errorMessage = error.message || error.toString();
      showToast(`❌ Failed to create recipes: ${errorMessage}`, 'error');
    } finally {
      setIsCreatingRecipes(false);
      setRecipeCreationProgress({ current: 0, total: 0, message: '' });
    }
  };

  // Use a stable incremental id to avoid potential Date.now() collisions on rapid clicks
  const nextRecipeIdRef = useRef(Date.now());
  const addMoreRecipeCard = () => {
    nextRecipeIdRef.current += 1;
    const newId = nextRecipeIdRef.current;
    newCardFocusIdRef.current = newId;
    setRecipeCards(prev => [
      ...prev,
      { id: newId, name: '', price: '', servings: '' }
    ]);
  };

  // Auto-focus the name field of a newly added card
  useEffect(() => {
    if (newCardFocusIdRef.current) {
      const refs = cardFieldRefs.current[newCardFocusIdRef.current];
      if (refs?.name) {
        refs.name.focus();
        newCardFocusIdRef.current = null;
      }
    }
  });

  const removeRecipeCard = (id) => {
    if (recipeCards.length > 1) {
      setRecipeCards(recipeCards.filter(card => card.id !== id));
    }
  };

  const updateRecipeCard = (id, field, value) => {
    setRecipeCards(recipeCards.map(card =>
      card.id === id ? { ...card, [field]: value } : card
    ));
    if (creationErrors[id] && creationErrors[id][field]) {
      setCreationErrors(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: false
        }
      }));
    }
  };

  const createRecipesWithoutIngredients = async ({ variantChoices = null } = {}) => {
    const errors = {};
    let hasError = false;
    recipeCards.forEach(card => {
      const nameInvalid = !card.name || !card.name.trim();
      const priceInvalid = card.price === '' || card.price === undefined || card.price === null || isNaN(parseFloat(card.price));
      const servingsInvalid = card.servings === '' || card.servings === undefined || card.servings === null || isNaN(parseInt(card.servings));
      
      if (nameInvalid || priceInvalid || servingsInvalid) {
        hasError = true;
        errors[card.id] = {
          name: nameInvalid,
          price: priceInvalid,
          servings: servingsInvalid
        };
      }
    });

    if (hasError) {
      setCreationErrors(errors);
      showToast('All fields (Item Name, Price, and Servings) are required for all items.', 'warning');
      return;
    }

    // Duplicate name check (case-insensitive), against existing recipes and within the
    // batch being created. Names stay unique — the DB's idx_unique_menu_name guarantees
    // it — so "same dish at another price/size" is created as a VARIANT: a distinctly
    // named item ("X (Half)") linked through RecipeVariants, carrying the existing
    // mapping × multiplier. Two same-name cards in one batch are always blocked.
    if (!variantChoices) {
      const toPrice = (value) => Math.round((parseFloat(value) || 0) * 100) / 100;
      const variantCandidates = [];
      const seenInBatch = new Map();
      let blockedName = null;

      for (const card of recipeCards) {
        const displayName = String(card.name || '').trim();
        const cardName = displayName.toLowerCase();
        const cardPrice = toPrice(card.price);

        if (seenInBatch.has(cardName)) { blockedName = displayName; break; }
        seenInBatch.set(cardName, cardPrice);

        const existing = recipes.filter((r) => String(r.name || '').trim().toLowerCase() === cardName);
        if (existing.length === 0) continue;

        const existingPrices = Array.from(new Set(existing.map((r) => toPrice(r.price))));
        if (existingPrices.includes(cardPrice)) { blockedName = displayName; break; }

        const parent = existing[0];
        variantCandidates.push({
          name: displayName,
          parentId: parent.menu_item_id || parent.id,
          parentName: parent.name,
          existingPrices,
          newPrice: cardPrice,
          label: `₹${cardPrice}`,
          multiplier: 1,
        });
      }

      if (blockedName) {
        setDuplicateRecipeError({ name: blockedName });
        return;
      }
      if (variantCandidates.length > 0) {
        setVariantConfirm({ items: variantCandidates });
        return;
      }
    }

    setVariantConfirm(null);
    const validRecipes = recipeCards;

    try {
      setIsCreatingRecipes(true);
      setRecipeCreationProgress({
        current: 0,
        total: validRecipes.length,
        message: 'Creating recipes without ingredients...'
      });

      const businessId = UnitMappingService.getBusinessId();
      const createdRecipes = [];
      const skippedDuplicates = [];

      for (let index = 0; index < validRecipes.length; index += 1) {
        const card = validRecipes[index];
        const itemName = String(card.name || '').trim();

        setRecipeCreationProgress({
          current: index + 1,
          total: validRecipes.length,
          message: `Creating ${itemName}...`
        });

        const recipeData = {
          name: itemName,
          price: parseFloat(card.price) || 0,
          servings: parseInt(card.servings) || 1,
          ingredients: [],
          source: 'manual_quick',
          ...(variantChoices && variantChoices[card.name.trim().toLowerCase()]
            ? {
                variantOf: variantChoices[card.name.trim().toLowerCase()].parentId,
                variantLabel: variantChoices[card.name.trim().toLowerCase()].label,
                portionMultiplier: variantChoices[card.name.trim().toLowerCase()].multiplier,
              }
            : {})
        };

        try {
          const newRecipe = await RecipesService.createRecipe(recipeData, businessId);
          createdRecipes.push(newRecipe);
        } catch (createError) {
          const status = createError?.response?.status || createError?.status;
          if (status === 409) {
            skippedDuplicates.push(itemName);
            continue;
          }
          throw createError;
        }
      }

      const refreshedRecipes = await RecipesService.getRecipes(businessId);
      setRecipes(applyLocalReviewDrafts(refreshedRecipes));

      if (createdRecipes.length > 0) {
        const firstCreated = createdRecipes[0];
        const selectedCreatedRecipe = refreshedRecipes.find((recipe) => (
          Number(recipe?.menu_item_id || recipe?.id) === Number(firstCreated?.id)
        ));

        if (selectedCreatedRecipe) {
          setSelectedRecipe(withImageMeta({
            ...selectedCreatedRecipe,
            id: selectedCreatedRecipe.menu_item_id || selectedCreatedRecipe.id,
          }));
        }
      }

      setShowNewRecipeForm(false);
      setRecipeCards([{ id: Date.now(), name: '', price: '', servings: '' }]);
      setCapturedImage(null);

      if (createdRecipes.length === 0 && skippedDuplicates.length > 0) {
        showToast('All recipe names already exist. Please use different names.', 'warning');
      } else if (skippedDuplicates.length > 0) {
        showToast(`Created ${createdRecipes.length} recipe(s). Skipped ${skippedDuplicates.length} duplicate name(s).`, 'warning');
      } else {
        showToast(`Created ${createdRecipes.length} recipe(s) without ingredients.`, 'success');
      }
    } catch (error) {
      console.error('Failed to create recipes without ingredients:', error);
      const errorMessage = error?.message || 'Failed to create recipes without ingredients';
      showToast(errorMessage, 'error');
    } finally {
      setIsCreatingRecipes(false);
      setRecipeCreationProgress({ current: 0, total: 0, message: '' });
    }
  };

  // Keep units as entered; no auto-conversion for clarity in 10/100 step entry
  const normalizeQty = (v) => {
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) {
      return Math.round(n * 100) / 100;
    }
    return 0;
  };

  // Unit conversion helper: supports g<->kg, ml<->L, and weight<->volume with 1:1 density fallback
  // Delegates to THE frontend converter (unitDisplay.convert). The old body assumed a
  // 1:1 density across g↔ml; cross-family now keeps the number unchanged and warns —
  // the server-side gate refuses any cross-family save, so the number never persists
  // in the wrong family.
  const convertQuantity = (value, fromUnit, toUnit) => {
    const numeric = Number(value) || 0;
    if (!fromUnit || !toUnit || fromUnit === toUnit) return numeric;
    const converted = convert(numeric, fromUnit, toUnit);
    if (converted === null) {
      console.warn(`⚠️ [UNITS] Cannot convert ${numeric} ${fromUnit} → ${toUnit} (different families); keeping the number unchanged`);
      return numeric;
    }
    return converted;
  };

  // SP9: the auto-prettify (normalizeToPreferredUnit — 1500 g → 1.5 kg) is DELETED from
  // every edit and display path. Your number is sacred: it changes only when you type it,
  // tap an equivalence hint, or switch the plate view.

  /**
   * Base unit for the IMPORT path: kg -> g, L -> ml, everything else untouched.
   *
   * Deliberately NOT normalizeToPreferredUnit, which auto-scales in both directions — it
   * pulls 0.07 kg down to 70 g, but it also pushes 1200 g up to 1.2 kg. That is right for
   * display and wrong for an import, because it makes the stored unit depend on how large
   * the number happens to be: the same recipe lands in g or kg according to portion size.
   *
   * It keeps 4 decimals rather than the 2 used for display — an import writes the stored
   * value, and a spice at 0.0125 g per plate must not be rounded to 0.01 on the way in.
   */
  const forceBaseUnit = (value, unit) => toBaseUnitOrKeep(value, unit);

  // Helper: get the display quantity for an ingredient based on current plateStep
  // Derives missing values from whichever qty is available (qty10 is the primary stored value)
  const getDisplayQty = (item) => {
    const q10 = item.qty10 || 0;
    const q100 = item.qty100 || 0;
    const perPlate = q10 > 0 ? (q10 / 10) : (q100 > 0 ? (q100 / 100) : 0);
    
    let val = 0;
    if (plateStep === 1) val = perPlate;
    else if (plateStep === 10) val = perPlate * 10;
    else if (plateStep === 100) val = perPlate * 100;
    else if (perPlate > 0) val = perPlate * plateStep;
    
    return Number(val.toFixed(2));
  };

  // Helper: given a value entered for current plateStep, compute both qty10 and qty100
  const setQtyForPlateStep = (item, value) => {
    const v = Number(value) || 0;
    const step = Number(plateStep) > 0 ? Number(plateStep) : 1;
    if (step === 1) {
      return { ...item, qty10: Number((v * 10).toFixed(4)), qty100: Number((v * 100).toFixed(4)) };
    }
    if (step === 10) {
      return { ...item, qty10: v, qty100: Number((v * 10).toFixed(4)) };
    }
    if (step === 100) {
      return { ...item, qty100: v, qty10: Number((v / 10).toFixed(4)) };
    }
    
    // CUSTOM PLATE LOGIC:
    // 'v' is always what the user typed against `step` plates, and getDisplayQty renders
    // a row back as perPlate * step. This is that function's inverse, so it must divide
    // by step for every step — exactly as the 1 / 10 / 100 branches above already do.
    // Assigning perPlate = v for step > 1 broke the round trip: 500 g typed against 5
    // plates came straight back as 2500 g and persisted 5x the true per-plate quantity.
    const perPlate = v / step;
    return { ...item, qty10: Number((perPlate * 10).toFixed(4)), qty100: Number((perPlate * 100).toFixed(4)) };
  };

  const presetPlateSteps = [1, 10, 100];
  const isCustomPlateStep = !presetPlateSteps.includes(Number(plateStep));

  const applyPlateStepChange = (nextStep) => {
    const normalized = Number(nextStep);
    if (!Number.isFinite(normalized) || normalized <= 0) {
      showToast('Enter a valid plate count');
      return;
    }

    setIngredientList(prev => prev.map(item => {
      const q10 = item.qty10 || 0;
      const q100 = item.qty100 || 0;
      const effQ10 = q10 > 0 ? q10 : (q100 > 0 ? Number((q100 / 10).toFixed(4)) : 0);
      const effQ100 = q100 > 0 ? q100 : (q10 > 0 ? Number((q10 * 10).toFixed(4)) : 0);
      // SP9 sacred numbers: a plate-view switch scales the DISPLAY from the stored
      // per-plate value — it never flips the row's unit.
      return { ...item, qty10: effQ10, qty100: effQ100 };
    }));

    setPlateStep(normalized);
    localStorage.setItem(PLATE_STEP_STORAGE_KEY, String(normalized));
    if (!presetPlateSteps.includes(normalized)) {
      localStorage.setItem(PLATE_STEP_CUSTOM_STORAGE_KEY, String(normalized));
      setCustomPlateStep(String(normalized));
    }
    setShowCustomPlateInput(false);
    setShowPlateMenu(false);
    showToast(`Showing for ${normalized} ${normalized === 1 ? 'plate' : 'plates'}`);
  };

  const applyCustomPlateStep = () => {
    const parsed = Number(customPlateStep);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showToast('Enter a valid custom plate count');
      return;
    }
    applyPlateStepChange(parsed);
  };

  //  AUTO-REFRESH: Reload kitchen conversions and update display immediately
  const refreshKitchenConversionsAndDisplay = async () => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      debugLog('🔄 Refreshing kitchen conversions and display...');

      // 1. Reload kitchen conversions from backend
      const convs = await UnitMappingService.getIngredientKitchenUnits(businessId, 'kitchen');
      
      // 2. Rebuild ingredient-specific maps
      const byItem = {};
      const byItemConversions = {};
      for (const r of convs || []) {
        const itemName = (r.item || '').trim();
        const toolSym = (r.kitchen_tool || '').trim();
        const outUnit = (r.unit || '').trim();
        const qty = Number(r.quantity);
        if (!itemName || !toolSym || !outUnit) continue;
        
        const key = itemName.toLowerCase();
        const normalizedTool = normalizeKitchenTool(toolSym);
        if (!normalizedTool.symbol) continue;
        
        if (!byItem[key]) byItem[key] = new Map();
        if (!byItem[key].has(normalizedTool.symbol)) {
          byItem[key].set(normalizedTool.symbol, { value: normalizedTool.symbol, label: normalizedTool.label });
        }
        
        if (!byItemConversions[key]) byItemConversions[key] = [];
        if (Number.isFinite(qty) && qty > 0) {
          byItemConversions[key].push({
            tool: normalizedTool.symbol,
            unit: outUnit,
            quantity: qty
          });
        }
      }
      
      // Convert maps to arrays
      const finalized = {};
      Object.keys(byItem).forEach(k => finalized[k] = Array.from(byItem[k].values()));
      setIngredientToolUnitsMap(finalized);
      setIngredientToolConversionsMap(byItemConversions);

      // 3. If a recipe is selected, reload its ingredients to show updated conversions
      if (selectedRecipe && selectedRecipe.id) {
        debugLog('🔄 Reloading ingredients for recipe:', selectedRecipe.name);
        const ingredients = await RecipesService.getRecipeIngredients(selectedRecipe.id, businessId);
        
        const list = (ingredients.length > 0
          ? ingredients.map(ing => {
            const ingredientName = ing.ingredient_name || ing.name || '';
            const nameKey = ingredientName.trim().toLowerCase();
            const std = inventoryStdUnitMap[nameKey];
            let unitSym = ing.unit || std || 'g';
            let perPlateQty = ing.quantity;

            // Check for kitchen conversion
            const convs = byItemConversions[nameKey] || [];
            let matchedConv = convs.find(c => {
              const outUnit = (c.unit || '').trim();
              return outUnit && outUnit.toLowerCase() === String(unitSym || '').trim().toLowerCase();
            });
            
            // Try multi-hop conversion
            if (!matchedConv && convs.length > 0) {
              for (const conv of convs) {
                const convOutUnit = (conv.unit || '').trim().toLowerCase();
                if (convOutUnit !== unitSym.toLowerCase()) {
                  let intermediateQty = perPlateQty;
                  let canConvert = false;
                  
                  if (unitSym.toLowerCase() === 'l' && convOutUnit === 'ml') {
                    intermediateQty = perPlateQty * 1000;
                    canConvert = true;
                  } else if (unitSym.toLowerCase() === 'ml' && convOutUnit === 'l') {
                    intermediateQty = perPlateQty / 1000;
                    canConvert = true;
                  } else if (unitSym.toLowerCase() === 'kg' && convOutUnit === 'g') {
                    intermediateQty = perPlateQty * 1000;
                    canConvert = true;
                  } else if (unitSym.toLowerCase() === 'g' && convOutUnit === 'kg') {
                    intermediateQty = perPlateQty / 1000;
                    canConvert = true;
                  }
                  
                  if (canConvert && conv.quantity > 0) {
                    matchedConv = conv;
                    perPlateQty = intermediateQty;
                    break;
                  }
                }
              }
            }
            
            if (matchedConv && matchedConv.quantity > 0) {
              unitSym = matchedConv.tool;
              perPlateQty = Number((perPlateQty / matchedConv.quantity).toFixed(4));
              debugLog(` [Refresh] Using kitchen unit: ${perPlateQty} ${unitSym} for ${ingredientName}`);
            }

            // SP9 sacred numbers: refresh must not rescale what the user saved.
            const normPerPlate = { value: perPlateQty, unit: unitSym };
            const norm = { value: Number((normPerPlate.value * 10).toFixed(4)), unit: normPerPlate.unit };

            return {
              name: ingredientName,
              unit: norm.unit,
              qty10: norm.value,
              qty100: 0,
              inventoryId: ing.inventory_id || null,
              id: ing.id || null
            };
          })
          : []
        );

        setIngredientList(list);
        
        // If we are currently editing an ingredient row, sync formData with the new server values
        if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
          const updatedIng = list[editingIngredientIndex];
          if (updatedIng) {
            const qty = getDisplayQty(updatedIng);
            setFormData(prev => ({
              ...prev,
              name: updatedIng.name === 'New Ingredient' ? '' : (updatedIng.name || ''),
              quantity: qty ?? 0,
              unit: updatedIng.unit || 'g'
            }));
          }
        }

        originalIngredientSnapshotRef.current = buildIngredientSnapshot(list);
        debugLog(' Ingredient display refreshed with new kitchen conversions');
      }

      debugLog(' Kitchen conversions refreshed successfully');
    } catch (error) {
      console.error('❌ Failed to refresh kitchen conversions:', error);
    }
  };

  const updateQuantity = (index, value, recordHistory = false) => {
    const prevList = cloneIngredientList(ingredientList);
    const updated = [...ingredientList];
    const qty = normalizeQty(value);
    
    // Check if per plate quantity is rounded off to zero
    const step = Number(plateStep) > 0 ? Number(plateStep) : 1;
    const perPlateVal = qty / step;
    if (qty > 0 && perPlateVal <= 0.0001 && recordHistory) {
      setShowRoundOffModal(true);
      showToast("Round off to zero. Costing cannot be applied.", "warning");
    }

    // SP9 sacred numbers: the typed value is stored as typed — no auto-scaling, no unit
    // flip under the user's fingers. 1500 g stays 1500 g.
    const withQty = setQtyForPlateStep(updated[index], qty);
    updated[index] = { ...updated[index], qty10: withQty.qty10, qty100: withQty.qty100 };
    setIngredientList(updated);
    if (recordHistory) {
      pushIngredientHistory('update', prevList, cloneIngredientList(updated));
    }
  };

  const handleRowTouchStart = (index, event) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    const target = event.target;
    const isControl = target.closest(
      '.controls-inline, .number-input, .text-input, .square-btn, [role="button"], input, select, button, .suggestions'
    );
    if (isControl) return;
    const touch = event.touches[0];
    setSwipingIndex(index);
    swipeStateRef.current = {
      index,
      startX: touch.clientX,
      startY: touch.clientY,
      endX: touch.clientX,
      endY: touch.clientY
    };
  };

  const handleRowTouchMove = (index, event) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    if (swipeStateRef.current.index !== index) return;
    const touch = event.touches[0];
    swipeStateRef.current.endX = touch.clientX;
    swipeStateRef.current.endY = touch.clientY;
    const deltaX = touch.clientX - swipeStateRef.current.startX;
    const bounded = Math.max(-120, Math.min(120, deltaX));
    setSwipeOffsets(prev => ({ ...prev, [index]: bounded }));
  };

  const handleRowTouchEnd = (index) => {
    if (typeof window !== 'undefined' && window.innerWidth > 640) return;
    const swipe = swipeStateRef.current;
    if (swipe.index !== index) return;
    const deltaX = swipe.endX - swipe.startX;
    const deltaY = swipe.endY - swipe.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const shouldDelete = absX > 60 && absX > absY * 1.5;
    if (shouldDelete && editingIngredientIndex !== index) {
      const exitOffset = deltaX > 0 ? 160 : -160;
      setSwipeOffsets(prev => ({ ...prev, [index]: exitOffset }));
      setTimeout(() => {
        deleteIngredient(index);
        setSwipeOffsets(prev => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
      }, 120);
    } else {
      setSwipeOffsets(prev => ({ ...prev, [index]: 0 }));
    }
    setSwipingIndex(null);
    swipeStateRef.current = {};
  };

  const startEditingIngredient = async (index) => {
    if (editingIngredientIndex !== null && editingIngredientIndex !== undefined && editingIngredientIndex !== index) {
      const hasName = formData.name && formData.name.trim() && formData.name.trim() !== 'New Ingredient';
      if (hasName) {
        try { await saveIngredient({ silent: true }); } catch (e) { console.warn('Auto-save failed:', e.message); }
      } else {
        cancelEditing();
      }
    }
    const ingredient = ingredientList[index];
    if (!ingredient) return;
    // Close any open unit selector dropdown from display mode
    setOpenUnitIndex(null);
    editSessionSnapshotRef.current = cloneIngredientList(ingredientList);
    setEditingIngredientIndex(index);
    const qty = getDisplayQty(ingredient);
    const ingredientName = ingredient.name === 'New Ingredient' ? '' : (ingredient.name || '');
    setFormData({ name: ingredientName, quantity: qty ?? 0, unit: ingredient.unit || 'g' });
    // Open suggestions immediately so typed names always get dropdown matches.
    setShowNameSuggestions(true);
  };

  // Autocomplete suggestions for inventory items, saved preparations, and menu items
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [preparationSuggestions, setPreparationSuggestions] = useState([]);
  const [menuSuggestions, setMenuSuggestions] = useState([]);
  const [librarySuggestions, setLibrarySuggestions] = useState([]);
  const [recipeLibraryList, setRecipeLibraryList] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showImportConfirmModal, setShowImportConfirmModal] = useState(false);
  const [selectedMenuItemForImport, setSelectedMenuItemForImport] = useState(null);
  // Library Match & Import modal (sync button in the Ingredients toolbar)
  const [showLibraryMatchModal, setShowLibraryMatchModal] = useState(false);
  const [libraryMatchQuery, setLibraryMatchQuery] = useState('');
  const [libraryMatchResults, setLibraryMatchResults] = useState([]);
  const [libraryMatchLoading, setLibraryMatchLoading] = useState(false);
  const [libraryMatchSelected, setLibraryMatchSelected] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  
  // SP9 (B2): the suggestion INDEX is built once per data change, not per keystroke.
  const suggestionIndex = React.useMemo(() => {
    const inventory = (inventoryItemNames || []).map((name) => {
      const key = String(name || '').trim().toLowerCase();
      return { id: `local-${key}`, name, standardUnit: inventoryStdUnitMap[key] || null };
    });
    const preparations = (savedPreparations || []).map((prep) => {
      const name = prep.composite_name || prep.name || '';
      return {
        id: prep.id ? `prep-${prep.id}` : `prep-${name}`,
        name,
        standardUnit: prep.total_weight_unit_symbol || prep.total_weight_unit_name || 'g',
      };
    });
    return { inventory, preparations };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryItemNames, inventoryStdUnitMap, savedPreparations]);

  // SP9 (B3): letters render instantly (the input is plain state); the SEARCH waits for a
  // 150 ms typing pause and runs against the prepared index. The old per-keystroke
  // InventoryService network search is gone — inventoryItemNames already holds the full
  // inventory; the API is consulted only as a fallback when that local list is empty.
  useEffect(() => {
    if (!editingIngredientIndex && editingIngredientIndex !== 0) return undefined;
    let active = true;

    const timer = setTimeout(async () => {
      const rawTerm = formData.name?.trim();
      const term = (!rawTerm || rawTerm === 'New Ingredient') ? '' : rawTerm;
      try {
        let localPool = suggestionIndex.inventory;
        if (localPool.length === 0 && UnitMappingService.getBusinessId()) {
          const items = await InventoryService.searchInventoryItems(term, UnitMappingService.getBusinessId(), 50);
          localPool = items || [];
        }
        const mergedItems = sortBySearchPriority(localPool, term, (item) => item?.name || '', { limit: 50 });
        const prepItems = sortBySearchPriority(suggestionIndex.preparations, term, (prep) => prep?.name || '', { limit: 50 });
        const menuItems = sortBySearchPriority(recipes, term, (recipe) => recipe?.name || '', { limit: 50 });
        const menuNameSet = new Set(menuItems.map((m) => (m.name || '').toLowerCase().trim()));
        const currentDishName = (selectedRecipe?.name || '').toLowerCase().trim();
        const filteredLib = recipeLibraryList.filter((r) => {
          const n = (r.item_name || r.name || '').toLowerCase().trim();
          return n && n !== currentDishName && !menuNameSet.has(n);
        });
        const libItems = sortBySearchPriority(filteredLib, term, (r) => r.item_name || r.name || '', { limit: 50 });

        if (active) {
          setNameSuggestions(mergedItems);
          setPreparationSuggestions(prepItems);
          setMenuSuggestions(menuItems);
          setLibrarySuggestions(libItems);
        }
      } catch (e) {
        console.warn('Failed to load suggestions', e);
      }
    }, 150);

    return () => { active = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.name, editingIngredientIndex, recipes, suggestionIndex, recipeLibraryList, selectedRecipe?.name]);

  const pickNameSuggestion = (s) => {
    const newName = s.name;
    // Recipe rows are entered and stored in BASE units, so an ingredient held in kg seeds
    // the row as g and one held in L seeds it as ml. Seeding the row with the high unit is
    // what produces the recipes that read "50000 ml of oil per plate": the reader sees the
    // number they meant in one unit sitting next to a label naming another.
    const newUnit = s.standardUnit ? toBaseUnitSymbol(s.standardUnit) : (formData.unit || 'g');

    setFormData(prev => {
      if (!s.standardUnit) return { ...prev, name: newName };
      // Re-picking a name on a row that ALREADY has a quantity must not silently reinterpret
      // it — "2" beside kg and "2" beside g are different amounts. Same family, so the value
      // is converted to keep the physical quantity identical. Across families (a weight
      // ingredient landing on a row typed in ml) there is no honest conversion, so only the
      // unit moves and the number stands as the user typed it.
      const sameFamily = getUnitFamily(toCanonicalUnit(prev.unit)) === getUnitFamily(newUnit);
      const hasQty = prev.quantity !== '' && prev.quantity !== null && prev.quantity !== undefined
        && Number.isFinite(Number(prev.quantity));
      const quantity = (sameFamily && hasQty)
        ? toBaseUnitOrKeep(prev.quantity, prev.unit).value
        : prev.quantity;
      return { ...prev, name: newName, unit: newUnit, quantity };
    });

    setShowNameSuggestions(false);
    // Live-sync into ingredientList so auto-save picks it up immediately
    if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
      setIngredientList(prev => {
        const updated = [...prev];
        const row = updated[editingIngredientIndex];
        if (row) {
          // Mirrors the formData branch above, against the row's own unit and quantity.
          const sameFamily = getUnitFamily(toCanonicalUnit(row.unit)) === getUnitFamily(newUnit);
          const hasQty = row.quantity !== '' && row.quantity !== null && row.quantity !== undefined
            && Number.isFinite(Number(row.quantity));
          updated[editingIngredientIndex] = {
            ...row,
            name: newName,
            unit: s.standardUnit ? newUnit : row.unit,
            quantity: (s.standardUnit && sameFamily && hasQty)
              ? toBaseUnitOrKeep(row.quantity, row.unit).value
              : row.quantity,
          };
        }
        return updated;
      });
    }
    // Focus quantity input after selection
    setTimeout(() => {
      if (quantityInputRef.current) {
        quantityInputRef.current.focus();
        quantityInputRef.current.select();
      }
    }, 100);
  };
  
  // Every import from the suggestion list asks "how many plates?" first — the first
  // import as well as repeats.
  const pickMenuItemSuggestion = async (menuItem) => {
    openRepeatImportModal(menuItem, { first: !isRepeatImport(menuItem) });
  };

  const pickLibraryRecipeSuggestion = (recipe) => {
    const target = { name: recipe.item_name || recipe.name, source: 'library' };
    openRepeatImportModal(target, { first: !isRepeatImport(target) });
  };
  
  const confirmDeleteAllIngredients = () => {
    if (ingredientList.length === 0) {
      showToast('No ingredients to delete', 'warning');
      setShowDeleteAllModal(false);
      return;
    }

    // Save current state to history before deleting all
    const newHistory = ingredientHistory.slice(0, historyIndex + 1);
    newHistory.push({ 
      action: 'deleteAll', 
      previousList: [...ingredientList]
    });
    setIngredientHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);

    // Clear all ingredients
    setIngredientList([]);
    showToast(' All ingredients deleted', 'success');
    setShowDeleteAllModal(false);
  };
  
  // Fetches a source's ingredients (menu item or Recipe Library dish) and maps them into
  // ingredientList rows: base units, qty10 = 10-plate quantity. Returns null (after a
  // warning toast) when the source has no ingredients. Shared by the first import and
  // the repeat-import-by-plates path so both read the source identically.
  const loadImportRows = async (target) => {
      const businessId = UnitMappingService.getBusinessId();

      let importedList;

      if (target.source === 'library') {
        // Fetch from Recipe Library (has real weights). If the caller already loaded the
        // library row (e.g. the Library Match modal), use its ingredients directly and
        // skip the fuzzy name re-fetch so the exact dish row is always used.
        let libIngredients;
        if (Array.isArray(target.ingredients)) {
          libIngredients = target.ingredients;
        } else {
          const recipe = await RecipeLibraryService.getRecipe(target.name, businessId);
          libIngredients = recipe?.ingredients || [];
        }

        if (!libIngredients.length) {
          showToast('No ingredients found in recipe library', 'warning');
          return null;
        }

        debugLog(`📥 Importing ${libIngredients.length} ingredients from library: ${target.name}`);

        importedList = libIngredients.map(ing => {
          const ingredientName = ing.name || '';
          const nameKey = ingredientName.trim().toLowerCase();
          const std = inventoryStdUnitMap[nameKey];
          let unitSym = ing.unit || std || 'g';
          let perPlateQty = Number(ing.quantity) || 0;

          if ((ing.unit === 'g' || ing.unit === 'kg') && (std === 'ml' || std === 'L')) {
            unitSym = std;
            perPlateQty = convertQuantity(perPlateQty, ing.unit || 'g', std);
          }
          if ((ing.unit === 'ml' || ing.unit === 'L') && (std === 'g' || std === 'kg')) {
            unitSym = std;
            perPlateQty = convertQuantity(perPlateQty, ing.unit || 'ml', std);
          }

          // Imports land in base units only: g or ml, never kg or L.
          const normPerPlate = forceBaseUnit(perPlateQty, unitSym);
          const norm = { value: Number((normPerPlate.value * 10).toFixed(4)), unit: normPerPlate.unit };

          return {
            name: ingredientName,
            unit: norm.unit,
            qty10: norm.value,
            qty100: 0,
            inventoryId: null,
            id: null
          };
        });
      } else {
        // Fetch from existing menu item recipe
        const ingredients = await RecipesService.getRecipeIngredients(target.id, businessId);
      
        if (!ingredients || ingredients.length === 0) {
          showToast('No ingredients found in selected item', 'warning');
          return null;
        }
      
        debugLog(`📥 Importing ${ingredients.length} ingredients from ${target.name}`);
      
        // Convert imported ingredients to the format used by ingredientList
        importedList = ingredients.map(ing => {
          const ingredientName = ing.ingredient_name || ing.name || '';
          const nameKey = ingredientName.trim().toLowerCase();
          const std = inventoryStdUnitMap[nameKey];
          let unitSym = ing.unit || std || 'g';
          let perPlateQty = ing.quantity; // backend per-plate quantity
        
          // Apply unit conversions if needed
          if ((ing.unit === 'g' || ing.unit === 'kg') && (std === 'ml' || std === 'L')) {
            unitSym = std;
            perPlateQty = convertQuantity(ing.quantity, ing.unit || 'g', std);
          }
          if ((ing.unit === 'ml' || ing.unit === 'L') && (std === 'g' || std === 'kg')) {
            unitSym = std;
            perPlateQty = convertQuantity(ing.quantity, ing.unit || 'ml', std);
          }
        
          // Imports land in base units only: g or ml, never kg or L.
          const normPerPlate = forceBaseUnit(perPlateQty, unitSym);
          const norm = { value: Number((normPerPlate.value * 10).toFixed(4)), unit: normPerPlate.unit };
        
          return {
            name: ingredientName,
            unit: norm.unit,
            qty10: norm.value,
            qty100: 0,
            inventoryId: ing.inventory_id || null,
            id: ing.id || null
          };
        });
      }

      return groupImportRows(importedList);
  };

  const normalizeImportName = (value) => String(value || '').trim().toLowerCase();

  // Collapse duplicate ingredient names inside one source into a single row (quantities summed).
  const groupImportRows = (importedList) => {
      const normalizeIngredientName = normalizeImportName;

      const mergeIngredientRows = (target, source) => {
        const targetUnit = target.unit || source.unit || 'g';
        const sourceUnit = source.unit || target.unit || 'g';
        const nextQty10 = Number(target.qty10 || 0) + Number(convertQuantity(source.qty10 || 0, sourceUnit, targetUnit) || 0);
        const nextQty100 = Number(target.qty100 || 0) + Number(convertQuantity(source.qty100 || 0, sourceUnit, targetUnit) || 0);

        return {
          ...target,
          unit: targetUnit,
          qty10: Number(nextQty10.toFixed(4)),
          qty100: Number(nextQty100.toFixed(4))
        };
      };

      const groupedImported = [];
      const groupedImportedIndex = new Map();

      importedList.forEach((ing) => {
        const key = normalizeIngredientName(ing.name);
        if (!key) return;

        const existingIndex = groupedImportedIndex.get(key);
        if (existingIndex === undefined) {
          groupedImportedIndex.set(key, groupedImported.length);
          groupedImported.push({ ...ing });
          return;
        }

        groupedImported[existingIndex] = mergeIngredientRows(groupedImported[existingIndex], ing);
      });

      return groupedImported;
  };

  // ── Repeat import by plates ──────────────────────────────────────────────────
  // The first import of a source appends only the ingredients not yet mapped. Importing
  // the SAME source again into the same dish asks "how many plates?" (0.1–1000) and adds
  // that many plates' worth of each ingredient onto the rows already there.
  // Remembered per dish for this session; after a reload, a first import that would add
  // nothing (everything already mapped) falls through to the same prompt.
  const importedSourcesRef = useRef(new Map());
  const [showRepeatImportModal, setShowRepeatImportModal] = useState(false);
  const [repeatImportPlates, setRepeatImportPlates] = useState('1');
  const [repeatImportBusy, setRepeatImportBusy] = useState(false);
  // true when this source has not been imported into the dish yet: ingredients the dish
  // already has keep their quantity (same rule as before), only missing ones are added.
  const [repeatImportIsFirst, setRepeatImportIsFirst] = useState(false);
  const REPEAT_IMPORT_MIN_PLATES = 0.1;
  const REPEAT_IMPORT_MAX_PLATES = 1000;

  const getImportTrackingKey = (target) => {
    const dishKey = String(selectedRecipe?.menu_item_id || selectedRecipe?.id || normalizeImportName(selectedRecipe?.name));
    const sourceKey = target?.source === 'library'
      ? `lib:${normalizeImportName(target?.name)}`
      : `menu:${target?.id || normalizeImportName(target?.name)}`;
    return `${dishKey}::${sourceKey}`;
  };

  const rememberImportedSource = (target, rows) => {
    importedSourcesRef.current.set(
      getImportTrackingKey(target),
      (rows || []).map((r) => normalizeImportName(r.name)).filter(Boolean)
    );
  };

  // Repeat only while at least one of that source's ingredients is still on the dish —
  // if the user undid or deleted the import, the next click is a first import again.
  const isRepeatImport = (target) => {
    const names = importedSourcesRef.current.get(getImportTrackingKey(target));
    if (!names || names.length === 0) return false;
    const current = new Set(ingredientList.map((ing) => normalizeImportName(ing.name)).filter(Boolean));
    return names.some((n) => current.has(n));
  };

  const openRepeatImportModal = (target, { first = false } = {}) => {
    setSelectedMenuItemForImport(target);
    setRepeatImportIsFirst(first);
    setRepeatImportPlates('1');
    setShowImportConfirmModal(false);
    setShowRepeatImportModal(true);
  };

  const closeRepeatImportModal = () => {
    if (repeatImportBusy) return;
    setShowRepeatImportModal(false);
    setSelectedMenuItemForImport(null);
    setRepeatImportPlates('1');
  };

  const parseRepeatImportPlates = (raw) => {
    const n = Number(raw);
    if (raw === '' || !Number.isFinite(n)) return null;
    if (n < REPEAT_IMPORT_MIN_PLATES || n > REPEAT_IMPORT_MAX_PLATES) return null;
    return n;
  };

  const runRepeatImport = async (target, plates, { first = false } = {}) => {
    if (!target || !(plates > 0)) return;
    setRepeatImportBusy(true);
    try {
      showToast('Importing ingredients...');
      const rows = await loadImportRows(target);
      if (!rows) return;

      // Same placeholder rule as the first import: the blank row the name was typed into
      // is dropped so it cannot shadow a real ingredient.
      const baseList = ingredientList.filter((ing, idx) => {
        if (idx !== editingIngredientIndex) return true;
        return Number(ing.qty10 || 0) > 0 || Number(ing.qty100 || 0) > 0;
      });

      // The merge itself lives in utils/recipeImportMerge (moved verbatim, and tested there):
      // a first import keeps the quantity of an ingredient the dish already has, EXCEPT a
      // saved preparation, which is added onto — its own ingredients follow that row.
      const {
        nextList, updatedCount, addedCount, keptCount, preparationsAddedOnto, unitConflicts,
      } = mergeImportRows({
        baseList,
        rows,
        plates,
        first,
        isSavedPreparation: isSavedPreparationIngredient,
      });

      if (updatedCount === 0 && addedCount === 0) {
        showToast(
          unitConflicts.length > 0
            ? `Nothing added — unit mismatch on ${unitConflicts.join(', ')}`
            : 'No quantities to add',
          'warning'
        );
        return;
      }

      const newHistory = ingredientHistory.slice(0, historyIndex + 1);
      newHistory.push({
        action: 'import',
        previousList: [...ingredientList],
        newList: nextList
      });
      setIngredientHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      setIngredientList(nextList);
      rememberImportedSource(target, rows);
      if (first) {
        setPlateStep(1);
        setCustomPlateStep('');
      }

      const platesLabel = `${plates} plate${plates === 1 ? '' : 's'}`;
      const parts = [];
      if (updatedCount > 0) {
        parts.push(preparationsAddedOnto > 0
          ? `updated ${updatedCount} (${preparationsAddedOnto} saved preparation${preparationsAddedOnto === 1 ? '' : 's'} added onto)`
          : `updated ${updatedCount}`);
      }
      if (addedCount > 0) parts.push(`added ${addedCount}`);
      if (keptCount > 0) parts.push(`kept ${keptCount} already mapped`);
      showToast(
        `Imported ${platesLabel}: ${parts.join(', ')}` +
          (unitConflicts.length > 0 ? ` (skipped ${unitConflicts.length} unit mismatch)` : ''),
        unitConflicts.length > 0 ? 'warning' : 'success'
      );

      setShowRepeatImportModal(false);
      setSelectedMenuItemForImport(null);
      setRepeatImportPlates('1');
      setShowNameSuggestions(false);
      setEditingIngredientIndex(null);
      setFormData({ name: '', quantity: 0, unit: '' });
    } catch (error) {
      console.error('Failed to import ingredients:', error);
      showToast(`❌ Failed to import ingredients: ${error.message}`, 'error');
    } finally {
      setRepeatImportBusy(false);
    }
  };

  const confirmRepeatImport = () => {
    const plates = parseRepeatImportPlates(repeatImportPlates);
    if (plates === null) {
      showToast(`Enter plates between ${REPEAT_IMPORT_MIN_PLATES} and ${REPEAT_IMPORT_MAX_PLATES}`, 'warning');
      return;
    }
    runRepeatImport(selectedMenuItemForImport, plates, { first: repeatImportIsFirst });
  };

  const runIngredientImport = async (target, opts = {}) => {
    if (!target) return;

    try {
      showToast('Importing ingredients...');
      const groupedImported = await loadImportRows(target);
      if (!groupedImported) {
        setShowImportConfirmModal(false);
        setSelectedMenuItemForImport(null);
        return;
      }
      const normalizeIngredientName = normalizeImportName;

      // The row the user typed the name into is only a placeholder for this import.
      // Drop it while it is still blank so it cannot shadow an imported ingredient
      // that happens to share its name.
      const baseList = ingredientList.filter((ing, idx) => {
        if (idx !== editingIngredientIndex) return true;
        return Number(ing.qty10 || 0) > 0 || Number(ing.qty100 || 0) > 0;
      });

      const existingNames = new Set(
        baseList.map((ing) => normalizeIngredientName(ing.name)).filter(Boolean)
      );
      const workingList = baseList.map((ing) => ({ ...ing }));
      const baseIndexByName = new Map();
      workingList.forEach((ing, idx) => {
        const key = normalizeIngredientName(ing.name);
        if (key && !baseIndexByName.has(key)) baseIndexByName.set(key, idx);
      });

      // Append only ingredients that are not already mapped. Anything already in
      // the list keeps the quantity the user entered — an import never rewrites it —
      // EXCEPT a saved preparation, which is added onto: one plate of the source, with the
      // same arithmetic as the plates import (utils/recipeImportMerge). Its own ingredients
      // follow that row; they are never rows on the dish.
      const addedRows = [];
      const skippedNames = [];
      const preparationNames = [];
      const unitConflicts = [];

      groupedImported.forEach((ing) => {
        const key = normalizeIngredientName(ing.name);
        if (existingNames.has(key)) {
          const idx = baseIndexByName.get(key);
          const addPerPlate = Number(ing.qty10 || 0) / 10;
          if (idx !== undefined && addPerPlate > 0 && isSavedPreparationIngredient(workingList[idx].name)) {
            const merged = addPerPlateOntoRow(workingList[idx], ing.unit, addPerPlate);
            if (merged) {
              workingList[idx] = merged;
              preparationNames.push(ing.name);
              return;
            }
            // g onto an ml row (or similar) has no honest conversion — kept as it is.
            unitConflicts.push(ing.name);
          }
          skippedNames.push(ing.name);
          return;
        }
        existingNames.add(key);
        addedRows.push({ ...ing });
      });

      const finalList = [...workingList, ...addedRows];
      const changed = addedRows.length > 0 || preparationNames.length > 0;

      debugLog(`✓ Import: ${addedRows.length} added, ${preparationNames.length} saved preparations added onto, ${skippedNames.length} skipped as already mapped`);
      addedRows.forEach((ing) => debugLog(`  + ${ing.name}: ${ing.qty10}${ing.unit}`));
      preparationNames.forEach((name) => debugLog(`  ↑ ${name} (saved preparation, added onto)`));
      skippedNames.forEach((name) => debugLog(`  = ${name} (kept existing quantity)`));

      if (!changed && skippedNames.length > 0 && opts.allowRepeatPrompt) {
        // Everything from this source is already on the dish, so this is a repeat import
        // (e.g. first one happened before a reload). Ask for plates instead of doing nothing.
        rememberImportedSource(target, groupedImported);
        openRepeatImportModal(target);
        return;
      }

      if (!changed) {
        // Nothing new — leave the list, the plate step and the undo stack alone.
        showToast(
          skippedNames.length > 0
            ? 'All imported ingredients are already mapped — nothing added'
            : 'No new ingredients to import',
          'warning'
        );
      } else {
        // Save current state to history before updating
        const newHistory = ingredientHistory.slice(0, historyIndex + 1);
        newHistory.push({
          action: 'import',
          previousList: [...ingredientList],
          newList: finalList
        });
        setIngredientHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);

        // Update ingredient list
        setIngredientList(finalList);
        rememberImportedSource(target, groupedImported);
        setPlateStep(1);
        setCustomPlateStep('');

        let message;
        if (preparationNames.length === 0) {
          message = skippedNames.length > 0
            ? `Added ${addedRows.length}, skipped ${skippedNames.length} already mapped`
            : `Added ${addedRows.length} ingredient${addedRows.length === 1 ? '' : 's'}`;
        } else {
          const parts = [];
          if (addedRows.length > 0) parts.push(`added ${addedRows.length}`);
          parts.push(`added onto ${preparationNames.length} saved preparation${preparationNames.length === 1 ? '' : 's'}`);
          if (skippedNames.length > 0) parts.push(`skipped ${skippedNames.length} already mapped`);
          const joined = parts.join(', ');
          message = joined.charAt(0).toUpperCase() + joined.slice(1);
        }
        if (unitConflicts.length > 0) message += ` (unit mismatch on ${unitConflicts.join(', ')})`;
        showToast(message, unitConflicts.length > 0 ? 'warning' : 'success');
      }

      // Close modal and reset
      setShowImportConfirmModal(false);
      setSelectedMenuItemForImport(null);
      setShowNameSuggestions(false);
      setEditingIngredientIndex(null);
      setFormData({ name: '', quantity: 0, unit: '' });
      
    } catch (error) {
      console.error('Failed to import ingredients:', error);
      showToast(`❌ Failed to import ingredients: ${error.message}`, 'error');
      setShowImportConfirmModal(false);
      setSelectedMenuItemForImport(null);
    }
  };

  // Thin wrapper so the existing "Import Ingredients?" confirm modal keeps working
  // by reading the selected item from state.
  const confirmImportIngredients = () => runIngredientImport(selectedMenuItemForImport, { allowRepeatPrompt: true });

  // ── Library Match & Import (sync button in the Ingredients toolbar) ──────────
  // Rank library dishes by name similarity to the current dish using the same
  // normalizeName + levenshtein helpers used elsewhere in this module.
  const rankLibraryDishes = (dishes, query) => {
    const q = normalizeName(query);
    return (dishes || [])
      .map((r) => {
        const name = r.item_name || r.name || '';
        const n = normalizeName(name);
        if (!q) return { ...r, _score: 0 };
        const dist = levenshtein(q, n);
        const maxLen = Math.max(q.length, n.length) || 1;
        const base = 1 - dist / maxLen;
        const contains = n && (n.includes(q) || q.includes(n));
        return { ...r, _score: contains ? Math.max(base, 0.85) : base };
      })
      .sort((a, b) => b._score - a._score);
  };

  const runLibraryMatchSearch = async (query, autoSelectBest = false) => {
    setLibraryMatchLoading(true);
    try {
      let all = recipeLibraryList;
      if (!all || all.length === 0) {
        all = await RecipeLibraryService.list();
        setRecipeLibraryList(all || []);
      }
      // Dishes only — exclude preparation / complimentary rows.
      const dishesOnly = (all || []).filter((r) => (r.item_type || 'dish') === 'dish');
      const q = (query || '').trim();
      const ranked = q ? rankLibraryDishes(dishesOnly, q).slice(0, 50) : dishesOnly.slice(0, 50);
      setLibraryMatchResults(ranked);
      if (autoSelectBest) setLibraryMatchSelected(ranked.length ? ranked[0] : null);
    } catch (e) {
      console.warn('Library match search failed', e);
      setLibraryMatchResults([]);
    } finally {
      setLibraryMatchLoading(false);
    }
  };

  const openLibraryMatchModal = async () => {
    const seed = selectedRecipe?.name || '';
    setLibraryMatchQuery(seed);
    setLibraryMatchSelected(null);
    setLibraryMatchResults([]);
    setShowLibraryMatchModal(true);
    await runLibraryMatchSearch(seed, true); // auto-select the single best match
  };

  const closeLibraryMatchModal = () => {
    setShowLibraryMatchModal(false);
    setLibraryMatchQuery('');
    setLibraryMatchResults([]);
    setLibraryMatchSelected(null);
  };

  const confirmLibraryMatchImport = async () => {
    if (!libraryMatchSelected) {
      showToast('Select a menu item to import from', 'warning');
      return;
    }
    const name = libraryMatchSelected.item_name || libraryMatchSelected.name;
    // Pass the already-loaded ingredients so the import uses this exact dish row
    // (merge/append — existing ingredients keep their quantities; a saved preparation
    // already on the dish is added onto).
    await runIngredientImport({
      name,
      source: 'library',
      ingredients: libraryMatchSelected.ingredients
    });
    closeLibraryMatchModal();
  };

  // Price auto-calculation removed per new flow

  const saveIngredient = async (opts = {}) => {
    const { silent = false, returnUpdatedList = false } = opts;
    // Validate all required fields
    if (!formData.name.trim() || formData.name.trim() === 'New Ingredient') {
      if (!silent) showToast('Please enter an ingredient name', 'warning');
      return returnUpdatedList ? null : false;
    }
    
    if (!formData.quantity || formData.quantity <= 0) {
      if (!silent) showToast('Please enter a valid quantity', 'warning');
      return returnUpdatedList ? null : false;
    }
    
    if (!formData.unit || formData.unit.trim() === '') {
      if (!silent) showToast('Please select a unit', 'warning');
      return returnUpdatedList ? null : false;
    }

    // Keep edit changes local; inventory rows are ensured only on final Confirm save.

    // Ensure unit defaults to inventory standard unit if available
    // For recipe context, prefer smaller units (ml over L, g over kg) since recipes
    // deal in per-plate quantities.
    const nameKey = formData.name.trim().toLowerCase();
    const std = inventoryStdUnitMap[nameKey];
    const originalUnit = formData.unit || std || 'g';
    let unitToUse = originalUnit;
    // Convert inventory standard to recipe-friendly smaller unit
    if (std === 'L') unitToUse = 'ml';
    else if (std === 'kg') unitToUse = 'g';
    else if ((unitToUse === 'g' || unitToUse === 'kg') && (std === 'ml' || std === 'L')) {
      unitToUse = std === 'L' ? 'ml' : std;
    }

    const previousList = editSessionSnapshotRef.current
      ? cloneIngredientList(editSessionSnapshotRef.current)
      : cloneIngredientList(ingredientList);
    const updated = [...ingredientList];
    const base = { name: formData.name, unit: unitToUse };
    let qty = normalizeQty(formData.quantity);
    if (unitToUse !== originalUnit) {
      qty = convertQuantity(qty, originalUnit, unitToUse);
    }
    const withQty = setQtyForPlateStep({ ...(updated[editingIngredientIndex] || {}), ...base }, qty);
    updated[editingIngredientIndex] = { ...base, qty10: withQty.qty10, qty100: withQty.qty100 };
    const nextList = cloneIngredientList(updated);
    if (JSON.stringify(previousList) !== JSON.stringify(nextList)) {
      pushIngredientHistory('update', previousList, nextList);
    }

    setIngredientList(updated);
    setEditingIngredientIndex(null);
    setFormData({ name: '', quantity: 0, unit: '' });
    setShowNameSuggestions(false);
    editSessionSnapshotRef.current = null;
    return returnUpdatedList ? updated : true;
  };

  // Sync the in-progress ingredient editor (formData) into ingredientList WITHOUT closing
  // the editor. The recipe editor unmounts when navigating to the complimentary page, so the
  // quantity input's onBlur never fires — without this flush the typed quantity would be lost
  // and the row would reload as 0. Returns the updated list synchronously for callers that need it.
  const flushEditingIngredient = () => {
    if (editingIngredientIndex === null || editingIngredientIndex === undefined) return ingredientList;
    const name = (formData.name || '').trim();
    if (!name || name === 'New Ingredient') return ingredientList;

    const flushQty = normalizeQty(formData.quantity);
    let nextList = ingredientList;
    setIngredientList(prev => {
      const updated = [...prev];
      const row = updated[editingIngredientIndex];
      if (row) {
        const withQty = setQtyForPlateStep(row, flushQty);
        updated[editingIngredientIndex] = {
          ...row,
          name,
          unit: formData.unit || row.unit || 'g',
          qty10: withQty.qty10,
          qty100: withQty.qty100
        };
      }
      nextList = updated;
      return updated;
    });
    setFormData(prev => ({ ...prev, quantity: flushQty }));
    return nextList;
  };

  // Delete ingredient with history tracking
  const deleteIngredient = (index) => {
    const currentList = [...ingredientList];
    const deletedItem = currentList[index];
    
    // Save current state to history
    const newHistory = ingredientHistory.slice(0, historyIndex + 1);
    newHistory.push({ action: 'delete', item: deletedItem, index, list: currentList });
    setIngredientHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    
    // Remove the ingredient
    currentList.splice(index, 1);
    setIngredientList(currentList);
    setEditingIngredientIndex(null);
    editSessionSnapshotRef.current = null;
  };

  // Undo last action
  const handleUndo = () => {
    if (historyIndex < 0) {
      return;
    }
    
    const action = ingredientHistory[historyIndex];
    if (action.action === 'delete') {
      // Restore deleted item
      const restored = [...ingredientList];
      restored.splice(action.index, 0, action.item);
      setIngredientList(restored);
    } else if (action.action === 'update') {
      setIngredientList([...action.previousList]);
    } else if (action.action === 'import') {
      // Restore previous list before import
      setIngredientList([...action.previousList]);
    } else if (action.action === 'deleteAll') {
      // Restore all deleted ingredients
      setIngredientList([...action.previousList]);
    }
    
    setHistoryIndex(historyIndex - 1);
  };

  // Redo last undone action
  const handleRedo = () => {
    if (historyIndex >= ingredientHistory.length - 1) {
      return;
    }
    
    const nextIndex = historyIndex + 1;
    const action = ingredientHistory[nextIndex];
    
    if (action.action === 'delete') {
      // Re-delete the item
      const updated = [...ingredientList];
      updated.splice(action.index, 1);
      setIngredientList(updated);
    } else if (action.action === 'update') {
      setIngredientList([...action.newList]);
    } else if (action.action === 'import') {
      // Re-apply the import
      setIngredientList([...action.newList]);
    } else if (action.action === 'deleteAll') {
      // Re-delete all ingredients
      setIngredientList([]);
    }
    
    setHistoryIndex(nextIndex);
  };

  const cancelEditing = () => {
    // If we're cancelling a freshly added placeholder row, remove it instead of showing "New Ingredient"
    if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
      const idx = editingIngredientIndex;
      const ing = ingredientList[idx];
      if (ing && ing.name === 'New Ingredient') {
        const updated = [...ingredientList];
        updated.splice(idx, 1);
        setIngredientList(updated);
      }
    }
    setEditingIngredientIndex(null);
    setFormData({ name: '', quantity: 0, unit: '' });
    setShowNameSuggestions(false);
    editSessionSnapshotRef.current = null;
  };

  const closeIngredientEditor = async () => {
    if (editingIngredientIndex === null || editingIngredientIndex === undefined) {
      setShowNameSuggestions(false);
      return;
    }

    const hasName = formData.name && formData.name.trim() && formData.name.trim() !== 'New Ingredient';
    const hasQty = Number(formData.quantity) > 0;
    const hasUnit = formData.unit && String(formData.unit).trim() !== '';

    if (hasName && hasQty && hasUnit) {
      const saved = await saveIngredient({ silent: true });
      if (saved) return;
    }

    cancelEditing();
  };

  useEffect(() => {
    if (editingIngredientIndex === null || editingIngredientIndex === undefined) return;

    const handleEditorOutsideClick = (e) => {
      const target = e.target;
      if (!target) return;

      if (ingredientEditorRef.current && ingredientEditorRef.current.contains(target)) return;
      if (target.closest && target.closest('.itemmap-unit-menu')) return;

      closeIngredientEditor();
    };

    document.addEventListener('mousedown', handleEditorOutsideClick);
    document.addEventListener('touchstart', handleEditorOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleEditorOutsideClick);
      document.removeEventListener('touchstart', handleEditorOutsideClick);
    };
  }, [editingIngredientIndex, formData.name, formData.quantity, formData.unit]);

  // Handle complimentary item click - toggle checkbox/link using advanced system
  const handleComplimentaryItemClick = async (item, event) => {
    // Stop propagation immediately (before async)
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    debugLog('🔘 Checkbox clicked:', item.name, 'comp_recipe_id:', item.comp_recipe_id);
    
    // For advanced complimentary system, use toggleComplimentaryLink
    const menuItemId = selectedRecipe?.menu_item_id || selectedRecipe?.id;
    
    if (item && item.comp_recipe_id && menuItemId) {
      // Check if item is currently linked
      const isLinked = linkedComplimentaryItems.some(linked => linked.comp_recipe_id === item.comp_recipe_id);
      debugLog('  Currently linked:', isLinked, '| menuItemId:', menuItemId);
      requestComplimentaryLinkToggle(item, isLinked, event);
    } else {
      console.warn('  Missing required data:', { 
        hasCompRecipeId: !!item?.comp_recipe_id, 
        hasMenuItemId: !!menuItemId,
        selectedRecipe
      });
    }
  };

  // Open complimentary item for editing
  const handleOpenComplimentaryEditor = (item) => {
    // Persist any in-progress ingredient edit before the recipe editor unmounts.
    flushEditingIngredient();
    setSelectedComplimentaryItem(item);
    setShowComplimentaryItemPage(true);
  };

  // Back from complimentary item page
  const handleBackFromComplimentary = async () => {
    try {
      // Reload library to show any newly created items
      await loadComplimentaryRecipesLibrary();
      if (selectedRecipe?.id || selectedRecipe?.menu_item_id) {
        await loadLinkedComplimentaryItems(selectedRecipe.menu_item_id || selectedRecipe.id);
      }
    } catch (err) {
      console.error('Failed to reload complimentary data:', err);
    } finally {
      setShowComplimentaryItemPage(false);
      setSelectedComplimentaryItem(null);
    }
  };

  // Save complimentary item ingredients
  const saveComplimentaryIngredients = async (ingredients) => {
    const itemName = typeof selectedComplimentaryItem === 'string' ? selectedComplimentaryItem : selectedComplimentaryItem.name;
    debugLog('Saving complimentary ingredients for:', itemName);
    debugLog('Ingredients:', ingredients);
    
    try {
      // Reload complimentary recipes library to show updated items
      await loadComplimentaryRecipesLibrary();
      if (selectedRecipe?.id || selectedRecipe?.menu_item_id) {
        await loadLinkedComplimentaryItems(selectedRecipe.menu_item_id || selectedRecipe.id);
      }
    } catch (err) {
      console.error('Failed to reload after save:', err);
      showToast('Saved but failed to refresh list', 'warning');
    } finally {
      setShowComplimentaryItemPage(false);
      setSelectedComplimentaryItem(null);
    }
  };

  // Handle add new complimentary item
  const handleAddNewComplimentaryItem = () => {
    setShowComplimentaryDropdown(false);
    setShowAddComplimentaryModal(true);
    setNewComplimentaryForm({ name: '', price: 0, servings: 1 });
  };

  const closeDeleteComplimentaryModal = (shouldRestoreDropdown = false) => {
    setShowDeleteComplimentaryModal(false);
    setComplimentaryItemToDelete(null);

    if (shouldRestoreDropdown && restoreComplimentaryDropdownAfterDelete) {
      window.setTimeout(() => {
        setShowComplimentaryDropdown(true);
      }, 0);
    }

    setRestoreComplimentaryDropdownAfterDelete(false);
  };

  const requestDeleteComplimentaryItem = (item, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const shouldRestoreDropdown = showComplimentaryDropdown;
    setRestoreComplimentaryDropdownAfterDelete(shouldRestoreDropdown);
    if (shouldRestoreDropdown) {
      setShowComplimentaryDropdown(false);
    }
    setComplimentaryItemToDelete(item);
    setShowDeleteComplimentaryModal(true);
  };

  const confirmDeleteComplimentaryItem = async () => {
    if (!complimentaryItemToDelete?.comp_recipe_id) {
      closeDeleteComplimentaryModal(true);
      showToast('⚠️ Complimentary item ID not found', 'warning');
      return;
    }

    try {
      await FeaturesService.deleteComplimentaryRecipe(complimentaryItemToDelete.comp_recipe_id);

      const menuItemId = selectedRecipe?.menu_item_id || selectedRecipe?.id;
      if (menuItemId) {
        await loadLinkedComplimentaryItems(menuItemId);
      }
      await loadComplimentaryRecipesLibrary();

      showToast(` Deleted ${complimentaryItemToDelete.name}`);
    } catch (error) {
      console.error('❌ Failed to delete complimentary recipe:', error);
      showToast('❌ Failed to delete complimentary item', 'error');
    } finally {
      closeDeleteComplimentaryModal(true);
    }
  };

  const closeAddComplimentaryModal = () => {
    setShowAddComplimentaryModal(false);
    setNewComplimentaryForm({ name: '' });
  };

  // 🆕 Load all available complimentary recipes (library)
  const loadComplimentaryRecipesLibrary = async () => {
    try {
      setLoadingComplimentaryLibrary(true);
      const result = await FeaturesService.getComplimentaryRecipes(true); // activeOnly=true
      if (result.success && result.complimentaryRecipes) {
        setComplimentaryRecipesLibrary(result.complimentaryRecipes);
      }
    } catch (error) {
      console.error('❌ Failed to load complimentary recipes library:', error);
    } finally {
      setLoadingComplimentaryLibrary(false);
    }
  };

  // 🆕 Load linked complimentary items for a specific recipe
  const loadLinkedComplimentaryItems = async (menuItemId) => {
    try {
      const result = await FeaturesService.getLinkedComplimentaryItems(menuItemId);
      if (result.success && result.complimentaryItems) {
        setLinkedComplimentaryItems(result.complimentaryItems);
        debugLog('🔗 Loaded linked complimentary items for recipe', menuItemId, ':', result.complimentaryItems.length, 'items');
        return result.complimentaryItems;
      }
      return [];
    } catch (error) {
      console.error('❌ Failed to load linked complimentary items:', error);
      return [];
    }
  };

  // 🆕 Link/unlink complimentary recipe to current recipe
  const toggleComplimentaryLink = async (compRecipeId, currentlyLinked, itemName = '') => {
    debugLog('🔗 toggleComplimentaryLink called:', { compRecipeId, currentlyLinked });
    
    if (!selectedRecipe) {
      throw new Error('Please select a recipe before updating complimentary items.');
    }

    const menuItemId = selectedRecipe.menu_item_id || selectedRecipe.id;
    const normalizedItemName = itemName.trim();
    debugLog('  menuItemId:', menuItemId);

    if (currentlyLinked) {
      const link = linkedComplimentaryItems.find(item => item.comp_recipe_id === compRecipeId);
      debugLog('  Found link to unlink:', link);
      if (!link?.link_id) {
        throw new Error('This complimentary item is not currently attached to the selected recipe.');
      }

      debugLog('  Calling unlinkComplimentaryItem with link_id:', link.link_id);
      const result = await FeaturesService.unlinkComplimentaryItem(link.link_id);
      debugLog('  Unlink result:', result);
      if (!result.success) {
        throw new Error(result.error || 'Failed to remove complimentary item.');
      }
    } else {
      debugLog('  Calling linkComplimentaryToRecipe...');
      const result = await FeaturesService.linkComplimentaryToRecipe(menuItemId, {
        compRecipeId: compRecipeId,
        scalingType: 'SCALED',
        quantityMultiplier: 1.0
      });
      debugLog('  Link result:', result);
      if (!result.success) {
        throw new Error(result.error || 'Failed to add complimentary item.');
      }
    }

    await loadLinkedComplimentaryItems(menuItemId);

    if (normalizedItemName) {
      setSelectedComplimentaryItems(prev => (
        currentlyLinked
          ? prev.filter(entry => entry !== normalizedItemName)
          : prev.includes(normalizedItemName)
            ? prev
            : [...prev, normalizedItemName]
      ));
    }
  };

  const confirmComplimentaryLinkChange = async () => {
    const modalItem = complimentaryLinkModal.item;

    if (!modalItem?.comp_recipe_id) {
      setComplimentaryLinkModal(prev => ({
        ...prev,
        error: 'This complimentary item is not available for updating right now.'
      }));
      return;
    }

    setComplimentaryLinkModal(prev => ({
      ...prev,
      isSubmitting: true,
      error: ''
    }));

    try {
      await toggleComplimentaryLink(
        modalItem.comp_recipe_id,
        complimentaryLinkModal.currentlyLinked,
        getComplimentaryItemName(modalItem)
      );
      closeComplimentaryLinkModal();
    } catch (error) {
      console.error('❌ Failed to toggle complimentary link:', error);
      setComplimentaryLinkModal(prev => ({
        ...prev,
        isSubmitting: false,
        error: error?.message || 'Failed to update complimentary item.'
      }));
    }
  };

  // Create new complimentary item using advanced ComplimentaryRecipes system
  const createNewComplimentaryItem = async () => {
    if (!newComplimentaryForm.name.trim()) {
      showToast('⚠️ Please enter item name');
      return;
    }
    
    const normalizedName = newComplimentaryForm.name.trim().toLowerCase();
    const duplicateInRecipes = recipes.some(r => 
      r.name && r.name.trim().toLowerCase() === normalizedName
    );
    const duplicateInCompLibrary = complimentaryRecipesLibrary.some(item => 
      item.name && item.name.trim().toLowerCase() === normalizedName
    );
    const duplicateInSavedPreps = (savedPreparations || []).some(prep => {
      const nameToCheck = prep.composite_name || prep.name || '';
      return nameToCheck.trim().toLowerCase() === normalizedName;
    });

    if (duplicateInRecipes || duplicateInCompLibrary || duplicateInSavedPreps) {
      setDuplicateRecipeError({ name: newComplimentaryForm.name.trim() });
      return;
    }
    
    if (!selectedRecipe || !selectedRecipe.name) {
      showToast('⚠️ No recipe selected');
      return;
    }
    
    try {
      const businessId = UnitMappingService.getBusinessId();
      const menuItemId = selectedRecipe.menu_item_id || selectedRecipe.id;
      const newItemName = newComplimentaryForm.name.trim();
      
      debugLog('🆕 Creating advanced ComplimentaryRecipe:', newItemName);
      
      // Step 1: Create ComplimentaryRecipe in database
      const createResult = await FeaturesService.createComplimentaryRecipe({
        name: newItemName,
        description: `Complimentary item for ${selectedRecipe.name}`,
        portionSize: '1.0',
        ingredients: [],
        displayOrder: 0
      });
      
      if (!createResult.success) {
        throw new Error(createResult.error || 'Failed to create complimentary recipe');
      }
      
      const compRecipeId = createResult.data.comp_recipe_id;
      debugLog(' Created ComplimentaryRecipe with ID:', compRecipeId);
      
      // Step 2: Link the complimentary recipe to the main recipe
      const linkResult = await FeaturesService.linkComplimentaryToRecipe(menuItemId, {
        compRecipeId: compRecipeId,
        scalingType: 'SCALED',
        quantityMultiplier: 1.0
      });
      
      if (!linkResult.success) {
        throw new Error(linkResult.error || 'Failed to link complimentary recipe');
      }
      
      debugLog(' Linked ComplimentaryRecipe to recipe:', menuItemId);
      
      // Step 3: Reload linked items and library
      await loadLinkedComplimentaryItems(menuItemId);
      await loadComplimentaryRecipesLibrary();
      
      showToast(` Created ${newItemName} and linked to recipe!`);
      closeAddComplimentaryModal();

      // Persist any in-progress ingredient edit before the recipe editor unmounts.
      flushEditingIngredient();
      // Step 4: Open comps editor for adding ingredients
      setSelectedComplimentaryItem({
        name: newItemName, 
        comp_recipe_id: compRecipeId 
      });
      setShowComplimentaryItemPage(true);
      
    } catch (error) {
      console.error('Failed to create complimentary recipe:', error);
      showToast('❌ Failed to create complimentary item');
    }
  };



  // Handle OCR upload - show selected images and enable batch processing
  const handleOCRUpload = (e) => {
    const files = Array.from(e.target.files || []);
    debugLog('📸 Files selected:', files.length);
    
    if (files.length === 0) {
      console.warn('⚠️ No files selected');
      return;
    }

    // Add new files to existing selection (accumulate multiple uploads)
    setSelectedMenuImages(prev => {
      const newImages = [...prev, ...files];
      debugLog('📚 Total images now:', newImages.length);
      return newImages;
    });
    
    // Clear the input so same file can be selected again
    e.target.value = '';
    
    debugLog(' Images added. Click "Process" button to scan all images.');
  };

  // Process menu images directly (for auto-process after upload)
  const processMenuImagesDirectly = async (images) => {
    try {
      debugLog(' Starting menu scan process...');
      setOcrProcessing(true);
      setMenuProcessingProgress({ current: 0, total: images.length });
      setMenuScanProgress(0);
      setScannedMenuItems([]);
      
      let allMenuItems = [];

      for (let i = 0; i < images.length; i++) {
        const file = images[i];
        setMenuProcessingProgress({ current: i + 1, total: images.length });
        
        debugLog(` Processing menu image ${i + 1} of ${images.length}: ${file.name}`);

        try {
          debugLog(' Uploading menu image for OCR + AI processing...');
          const data = { success: false, error: "OCR Removed" };

          if (data.jobId) {
            debugLog(` Menu scan initiated. Job ID: ${data.jobId}`);
            debugLog(`� Found ${data.totalItems} menu items`);

            setMenuScanJob(data.jobId);
            
            const menuItems = data.menuItems.map(item => ({ 
              name: typeof item === 'string' ? item : item.name, 
              price: typeof item === 'object' ? item.price : 0,
              status: 'pending',
              sourceFile: file.name
            }));

            setOcrResults(menuItems);

            // Start polling for this job and wait for completion
            const completedItems = await new Promise((resolve, reject) => {
              const pollInterval = setInterval(async () => {
                try {
                  const pollData = { success: false, error: "OCR Removed" };

                  if (pollData.job) {
                    const { status, progress, menuItems: polledItems, processedItems, totalItems } = pollData.job;

                    setMenuScanProgress(progress);
                    setOcrResults(polledItems.map(item => ({
                      name: item.name,
                      price: item.price || 0,
                      status: item.status,
                      ingredients: item.ingredients,
                      imageUrl: item.imageUrl,
                      error: item.error,
                      sourceFile: file.name
                    })));

                    debugLog(`📊 Progress: ${processedItems}/${totalItems} (${progress}%)`);

                    // Check if completed or failed
                    if (status === 'completed' || status === 'failed') {
                      debugLog(`✨ Menu scan finished with status: ${status}`);
                      clearInterval(pollInterval);

                      const completed = polledItems
                        .filter(item => item.status === 'completed')
                        .map(item => ({
                          ...item,
                          sourceFile: file.name
                        }));
                      
                      resolve(completed);
                    }
                  }
                } catch (error) {
                  console.error('Polling error:', error);
                  clearInterval(pollInterval);
                  reject(error);
                }
              }, 3000);

              // Timeout after 10 minutes
              setTimeout(() => {
                clearInterval(pollInterval);
                reject(new Error('Menu scan timeout'));
              }, 600000);
            });

            allMenuItems = [...allMenuItems, ...completedItems];
            setScannedMenuItems(allMenuItems);

          } else {
            throw new Error('No job ID returned from menu scan');
          }
        } catch (error) {
          console.error(`Error processing menu image ${file.name}:`, error);
          showToast(`Failed to process ${file.name}: ${error.message}`);
        }
      }

      debugLog(` All menu images processed. Total items: ${allMenuItems.length}`);
      setOcrProcessing(false);

    } catch (error) {
      console.error('Error in processMenuImagesDirectly:', error);
      setOcrProcessing(false);
      showToast('Failed to process menu images');
    }
  };

  // Remove a selected menu image
  const removeMenuImage = (index) => {
    setSelectedMenuImages(prev => prev.filter((_, i) => i !== index));
  };

  // Clear all selected images
  const clearAllMenuImages = () => {
    setSelectedMenuImages([]);
    setMenuProcessingProgress({ current: 0, total: 0 });
  };

  // Process all selected menu images
  const processAllMenuImages = async () => {
    debugLog('🚀 processAllMenuImages called');
    debugLog('📊 Selected images count:', selectedMenuImages.length);
    
    if (selectedMenuImages.length === 0) {
      console.warn('⚠️ No images selected');
      showToast('Please select at least one menu image');
      return;
    }

    try {
      debugLog(' Starting menu scan process...');
      setOcrProcessing(true);
      setMenuProcessingProgress({ current: 0, total: selectedMenuImages.length });
      setMenuScanProgress(0);
      setScannedMenuItems([]);
      
      let allMenuItems = [];

      for (let i = 0; i < selectedMenuImages.length; i++) {
        const file = selectedMenuImages[i];
        setMenuProcessingProgress({ current: i + 1, total: selectedMenuImages.length });
        
        debugLog(` Processing menu image ${i + 1} of ${selectedMenuImages.length}: ${file.name}`);

        try {
          debugLog(' Uploading menu image for OCR + AI processing...');
          const data = { success: false, error: "OCR Removed" };

          if (data.jobId) {
            debugLog(` Menu scan initiated. Job ID: ${data.jobId}`);
            debugLog(`📋 Found ${data.totalItems} menu items`);

            setMenuScanJob(data.jobId);
            
            const menuItems = data.menuItems.map(item => ({ 
              name: typeof item === 'string' ? item : item.name, 
              price: typeof item === 'object' ? item.price : 0,
              status: 'pending',
              sourceFile: file.name
            }));

            setOcrResults(menuItems);

            // Start polling for this job and wait for completion
            const completedItems = await new Promise((resolve, reject) => {
              const pollInterval = setInterval(async () => {
                try {
                  const pollData = { success: false, error: "OCR Removed" };

                  if (pollData.job) {
                    const { status, progress, menuItems: polledItems, processedItems, totalItems } = pollData.job;

                    setMenuScanProgress(progress);
                    setOcrResults(polledItems.map(item => ({
                      name: item.name,
                      price: item.price || 0,
                      status: item.status,
                      ingredients: item.ingredients,
                      imageUrl: item.imageUrl,
                      error: item.error,
                      sourceFile: file.name
                    })));

                    debugLog(`📊 Progress: ${processedItems}/${totalItems} (${progress}%)`);

                    // Check if completed or failed
                    if (status === 'completed' || status === 'failed') {
                      debugLog(`✨ Menu scan finished with status: ${status}`);
                      clearInterval(pollInterval);

                      const completed = polledItems
                        .filter(item => item.status === 'completed')
                        .map(item => ({
                          ...item,
                          sourceFile: file.name
                        }));
                      
                      resolve(completed);
                    }
                  }
                } catch (error) {
                  console.error('Polling error:', error);
                  clearInterval(pollInterval);
                  reject(error);
                }
              }, 3000);

              // Timeout after 10 minutes
              setTimeout(() => {
                clearInterval(pollInterval);
                reject(new Error('Menu scan timeout'));
              }, 600000);
            });

            allMenuItems = [...allMenuItems, ...completedItems];

          } else {
            console.error(`Failed to process ${file.name}: ${data.error || 'Unknown error'}`);
            showToast(`Failed to process ${file.name}. Please ensure the image contains a clear menu.`, 'error');
          }
        } catch (error) {
          console.error(`❌ Error processing menu ${i + 1}:`, error);
          showToast(`Error processing ${file.name}. Please try again.`, 'error');
        }
      }

      // Update scanned items with all results
      if (allMenuItems.length > 0) {
        setScannedMenuItems(allMenuItems);
        debugLog(`✨ Total scanned items from all menus: ${allMenuItems.length}`);
      }

      // Clear selected images after successful processing
      setSelectedMenuImages([]);

    } catch (error) {
      console.error('❌ Error processing menu images:', error);
      showToast('Error processing menu images. Please try again.', 'error');
    } finally {
      setOcrProcessing(false);
      setMenuProcessingProgress({ current: 0, total: 0 });
      setMenuScanProgress(0);
    }
  };

  // Poll for menu scan job progress with detailed statistics
  const startMenuScanPolling = (jobId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        const data = { success: false, error: "OCR Removed" };

        if (data.job) {
          const { status, progress, menuItems, processedItems, totalItems, currentItem, stats } = data.job;

          setMenuScanProgress(progress);
          setOcrResults(menuItems.map(item => ({
            name: item.name,
            price: item.price || 0,  // Include price from backend
            status: item.status,
            ingredients: item.ingredients,
            imageUrl: item.imageUrl,
            error: item.error
          })));

          // Enhanced progress logging with statistics
          const statusEmoji = currentItem ? '🎨' : '⏳';
          const currentMsg = currentItem ? ` | Current: ${currentItem}` : '';
          debugLog(`${statusEmoji} Progress: ${processedItems}/${totalItems} (${progress}%)${currentMsg}`);
          
          if (stats) {
            debugLog(`    ${stats.completed} | ⏳ ${stats.processing} | ❌ ${stats.failed} | 📋 ${stats.pending}`);
          }

          // Check if completed or failed
          if (status === 'completed' || status === 'failed') {
            debugLog(`✨ Menu scan finished with status: ${status}`);
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;

            // Store scanned items for adding to recipes
            const completedItems = menuItems.filter(item => item.status === 'completed');
            setScannedMenuItems(completedItems);

            showToast(`🎉 Generated ingredients for ${completedItems.length} menu items!`);
          }
        }
      } catch (error) {
        console.error('❌ Polling error:', error);
        // Stop polling on error to prevent infinite loops
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }, 1500); // Poll every 1.5 seconds for faster UI updates
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // ⚠️ PREVENT REFRESH when scanned items are pending (not yet saved to database)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Show warning if OCR items exist but haven't been saved yet
      if (scannedMenuItems.length > 0 || (ocrResults.length > 0 && menuScanProgress < 100)) {
        const message = '⚠️ You have unsaved menu items from OCR scan. If you leave now, they will be lost. Click "Add X Recipes" to save them first.';
        e.preventDefault();
        e.returnValue = message; // Required for Chrome
        return message; // Required for some browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [scannedMenuItems, ocrResults, menuScanProgress]);

  // Helper function to detect portion size variants from menu items
  const detectVariantGroups = (menuItems) => {
    // 🇮🇳 Indian Restaurant Standard Portion Keywords
    // Based on Indian food service standards - optimized for Biryani, Pizza, Fast Food, Thali, etc.
    const portionKeywords = [
      'single', 'solo', 'personal', 'mini', 'quarter',

      'half', 'regular', 'medium', 'full',

      'small', 'starter',

      'large', 'jumbo', 'king', 'xl',

      'double', 'family', 'sharing', 'bonanza', 'mega'
    ];
    
    const groups = {};
    const standalone = [];

    for (const item of menuItems) {
      const nameLower = item.name.toLowerCase();
      
      // Check if item has a portion keyword
      let foundKeyword = null;
      let baseName = item.name;
      
      for (const keyword of portionKeywords) {
        const regex = new RegExp(`\\b${keyword}\\b`, 'i');
        if (regex.test(nameLower)) {
          foundKeyword = keyword;
          // Remove the portion keyword to get base name
          baseName = item.name.replace(regex, '').trim().replace(/\s+/g, ' ');
          break;
        }
      }

      if (foundKeyword) {
        // Normalize base name for grouping
        const groupKey = baseName.toLowerCase().trim();
        
        if (!groups[groupKey]) {
          groups[groupKey] = {
            baseName: baseName,
            variants: []
          };
        }
        
        groups[groupKey].variants.push({
          ...item,
          detectedPortionSize: foundKeyword,
          detectedBaseName: baseName
        });
      } else {
        standalone.push(item);
      }
    }

    // Filter to only keep groups with 2+ variants
    const validGroups = Object.entries(groups)
      .filter(([_, group]) => group.variants.length >= 2)
      .map(([key, group]) => ({
        groupKey: key,
        baseName: group.baseName,
        variants: group.variants
      }));

    // Add standalone items back for single variants
    Object.entries(groups)
      .filter(([_, group]) => group.variants.length === 1)
      .forEach(([_, group]) => {
        standalone.push(...group.variants);
      });

    return { validGroups, standalone };
  };

  // Helper function to calculate portion multiplier based on detected portion size
  // 🇮🇳 INTELLIGENT PORTION MULTIPLIERS - INDIA FOOD STANDARDS
  // Optimized for Indian cuisine: Biryani, Pizza, Fast Food, Thali, Curries, etc.
  const calculatePortionMultiplier = (portionSize) => {
    const multipliers = {
      // INDIVIDUAL SERVINGS (250-300g approx)
      'mini': 0.5,        // Kids meal, small appetizer (150g)
      'quarter': 0.25,    // Quarter portions (common in sweets/mithai)
      'solo': 0.75,       // Personal single serving
      'personal': 1.0,    // Standard personal size
      
      // STANDARD SERVINGS (350-450g - BASE SIZE)
      'single': 1.0,      // Standard Indian single serve (Biryani: 350g)
      'regular': 1.0,     // Regular plate/bowl (Thali standard)
      'medium': 1.0,      // Medium pizza (10"), medium curry bowl
      'full': 1.0,        // Full portion/plate
      'half': 0.5,        // Half plate (common in India)
      
      // SMALL SIZES (200-300g)
      'small': 0.6,       // Small pizza (8"), small bowl
      'starter': 0.4,     // Starter/appetizer portion
      
      // LARGE SERVINGS (550-700g)
      'large': 1.5,       // Large pizza (12"), large bowl (serves 1-2)
      'jumbo': 2.0,       // Jumbo size (Biryani: 700g, serves 2)
      'king': 2.0,        // King size burger/pizza
      'xl': 2.0,          // Extra large single portions
      
      // FAMILY/SHARING SIZES (900g-1.5kg - VERY COMMON IN INDIA)
      'double': 2.0,      // Double portion (serves 2)
      'family': 2.5,      // Family pack (serves 3-4) - Most popular in Indian restaurants
      'sharing': 2.5,     // Sharing platter
      'bonanza': 3.0,     // Bonanza/feast pack (serves 4-5) - Popular in fast food
      'mega': 3.5         // Mega/party size (serves 5-6)
    };

    return multipliers[portionSize.toLowerCase()] || 1.0;
  };

  const addOCRIngredientsToRecipe = async () => {
    try {
      debugLog('🔵 addOCRIngredientsToRecipe called');
      debugLog('🔍 scannedMenuItems:', scannedMenuItems);

      if (scannedMenuItems.length === 0) {
        showToast('No menu items ready. Please wait for AI processing to complete.', 'warning');
        return;
      }

      debugLog(`📥 Adding ${scannedMenuItems.length} scanned menu items as recipes...`);

      setIsCreatingRecipes(true);
      setShowOCRModal(false); // Close the detected menu items modal immediately
      setRecipeCreationProgress({ current: 0, total: scannedMenuItems.length, message: 'Preparing recipes...' });

      const businessId = UnitMappingService.getBusinessId();
      debugLog('🔍 businessId:', businessId);

      //  DETECT VARIANT GROUPS
      const { validGroups, standalone } = detectVariantGroups(scannedMenuItems);
      
      debugLog(`🔍 Detected ${validGroups.length} variant groups and ${standalone.length} standalone items`);
      validGroups.forEach(group => {
        debugLog(`    Group "${group.baseName}": ${group.variants.map(v => v.detectedPortionSize).join(', ')}`);
      });

      // 🆕 If variant groups detected, process directly with default multipliers
      if (validGroups.length > 0) {
        debugLog('🎨 Processing variant groups with default multipliers...');
        
        // Prepare editable multipliers with suggestions
        const initialMultipliers = {};
        
        validGroups.forEach(group => {
          group.variants.forEach(variant => {
            const keyword = variant.detectedPortionSize.toLowerCase();
            
            // Get current multiplier (custom or default)
            initialMultipliers[keyword] = getMultiplierForKeyword(keyword);
          });
        });
        
        // Detect which item should be the base (multiplier = 1.0)
        validGroups.forEach(group => {
          // Find the base variant (usually 'single', 'regular', 'medium', 'full')
          const baseVariant = group.variants.find(v => 
            ['single', 'regular', 'medium', 'full'].includes(v.detectedPortionSize.toLowerCase())
          ) || group.variants[0];
          
          // Mark it on each variant
          group.variants.forEach(v => {
            v.isBaseVariant = (v === baseVariant);
          });
        });
        
        setEditableMultipliers(initialMultipliers);
        setPendingVariantGroups({ validGroups, standalone });
        
        // ✋ SHOW EDITOR UI - Wait for user confirmation
        // "Fix modal display bug (#1)"
        setShowMultiplierEditor(true);
        setIsCreatingRecipes(false); // Stop loader while user edits
        return;
      }

      // If no variant groups, process standalone items directly
      await processStandaloneItems(standalone);

    } catch (error) {
      console.error('❌ Error in addOCRIngredientsToRecipe:', error);
      showToast('❌ Error processing menu items. Please try again.', 'error');
    } finally {
      setIsCreatingRecipes(false);
      setRecipeCreationProgress({ current: 0, total: 0, message: '' });
    }
  };

  // 🆕 Process items after multipliers are confirmed
  const processVariantGroupsWithMultipliers = async (variantGroups = null, standaloneItems = null, multipliers = null) => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      
      // Use passed parameters if available, otherwise fall back to state (for manual button clicks)
      const validGroups = variantGroups || pendingVariantGroups.validGroups || [];
      const standalone = standaloneItems || pendingVariantGroups.standalone || [];
      const multiplierMap = multipliers || editableMultipliers || {};
      
      const createdRecipes = [];

      // Process variant groups first
      let processedCount = 0;
      for (const group of validGroups) {
        processedCount++;
        setRecipeCreationProgress({ 
          current: processedCount, 
          total: validGroups.length + standalone.length, 
          message: `Creating variant group: ${group.baseName}` 
        });
        debugLog(`\n🍽️ Creating parent recipe for: ${group.baseName}`);
        
        try {
          // Find the base variant (marked during detection)
          const baseVariant = group.variants.find(v => v.isBaseVariant) || group.variants[0];

          debugLog(`   📍 Using "${baseVariant.detectedPortionSize}" as base variant`);

          // Process complete recipe data for base variant using AI
          // Always fetch AI data to get description and ingredients
          debugLog(`🤖 Fetching AI ingredients and description for "${baseVariant.name}"...`);
          try {
            // Build API URL - if REACT_APP_API_URL already includes /api, don't add it again
            const baseUrl = process.env.REACT_APP_API_URL;
            const apiUrl = baseUrl.endsWith('/api') ? `${baseUrl}/ai/generate-ingredients` : `${baseUrl}/api/ai/generate-ingredients`;
            
            const response = await fetch(apiUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-business-id': businessId },
              body: JSON.stringify({ dishName: baseVariant.name, plateCount: 10 })
            });
            if (!response.ok) throw new Error(`AI generation failed (${response.status})`);
            const data = await response.json();
            debugLog(`📦 AI Response:`, data);
            
            if (data.success && data.ingredients) {
              debugLog(` Got ${data.ingredients.length} ingredients from AI`);
              baseVariant.ingredients = data.ingredients;
              baseVariant.description = data.description || ''; // Save description
              
              debugLog(`📝 Description:`, data.description);
              debugLog(`📝 Sample ingredient:`, data.ingredients[0]);
              
              // Save complimentary items to Recipe Library
              if (data.complimentary_items?.length > 0) {
                try {
                  await RecipeLibraryService.updateRecipe(baseVariant.name, {
                    complimentary_items: data.complimentary_items || []
                  }, businessId);
                  debugLog(` Saved complimentary items for ${baseVariant.name}`);
                } catch (e) {
                  console.warn('Failed to save to Recipe Library:', e);
                }
              }
            } else {
              console.warn(`⚠️ AI fallback failed for ${baseVariant.name}, using empty ingredients`);
              baseVariant.ingredients = [];
              baseVariant.description = '';
            }
          } catch (aiError) {
            console.error(`❌ AI fetch error for ${baseVariant.name}:`, aiError);
            baseVariant.ingredients = baseVariant.ingredients || [];
            baseVariant.description = '';
          }

          // Create parent recipe (base variant)
          let parentRecipe;
          let parentAlreadyExists = false;
          try {
            parentRecipe = await RecipesService.createRecipe({
              name: baseVariant.name,
              businessId,
              category_id: selectedCategory,
              is_parent: true,
              parent_id: null,
              portionMultiplier: 1.0, // Explicitly set base to 1.0
              price: baseVariant.price || 0
            });
            debugLog(` Created parent recipe: ${parentRecipe.name} (ID: ${parentRecipe.id})`);
          } catch (createError) {
            // Handle duplicate item error - fetch existing recipe instead
            if (createError.response?.status === 409 || createError.message?.includes('Duplicate')) {
              console.warn(`⚠️ Parent recipe "${baseVariant.name}" already exists, fetching existing...`);
              parentAlreadyExists = true;
              
              // Fetch all recipes and find the matching one
              try {
                const allRecipes = await RecipesService.getRecipes(businessId);
                parentRecipe = allRecipes.find(r => r.name === baseVariant.name);
                
                if (!parentRecipe) {
                  console.error(`❌ Could not find existing recipe: ${baseVariant.name}`);
                  showToast(`❌ Failed to find existing recipe "${baseVariant.name}"`, 'error');
                  continue;
                }
                
                debugLog(` Found existing parent recipe: ${parentRecipe.name} (ID: ${parentRecipe.id})`);
              } catch (fetchError) {
                console.error(`❌ Error fetching existing recipe:`, fetchError);
                showToast(`❌ Failed to fetch existing recipe "${baseVariant.name}"`, 'error');
                continue;
              }
            } else {
              throw createError;
            }
          }

          // Save description if available
          if (baseVariant.description) {
            try {
              await RecipesService.updateRecipe(parentRecipe.id, {
                description: baseVariant.description
              }, businessId);
              debugLog(`   💾 Saved description for ${parentRecipe.name}`);
            } catch (descError) {
              console.warn('Failed to save description:', descError);
            }
          }

          // Save ingredients for parent (always import and update, even if recipe already exists)
          let ingredients = [];
          debugLog(`📊 Processing ${baseVariant.ingredients.length} raw ingredients:`, baseVariant.ingredients);
        
          ingredients = baseVariant.ingredients.map(ing => {
            const ingredientName = ing.name || ing.ingredient;
            
            // Extract unit from whichever field is available
            let unit = 'g';
            if (ing.unit) {
              unit = ing.unit;
            } else if (ing.quantity_standard) {
              const unitMatch = (ing.quantity_standard || '').match(/[a-zA-Z]+/);
              unit = unitMatch ? unitMatch[0] : 'g';
            }
            
            // Try to extract quantity from JSON, fallback to 0
            let qty = 0;
            if (ing.quantity !== undefined && ing.quantity !== null) {
              qty = Number(ing.quantity);
            } else if (ing.quantity_standard) {
              const qtyMatch = (ing.quantity_standard || '').match(/[0-9.]+/);
              qty = qtyMatch ? Number(qtyMatch[0]) : 0;
            }

            const processed = {
              name: ingredientName,
              quantity: isNaN(qty) ? 0 : qty,
              unit: unit
            };
            
            debugLog(`   🔹 ${ingredientName}: unit=${processed.unit} | qty=${processed.quantity}`);
            
            return processed;
          });

          debugLog(`💾 Saving ${ingredients.length} ingredients to database for recipe ID ${parentRecipe.id}`);
          try {
            await RecipesService.updateRecipeIngredients(parentRecipe.id, ingredients, businessId);
            debugLog(`    Saved ${ingredients.length} ingredients for parent recipe`);
          } catch (updateIngredientsError) {
            console.error(`❌ Error saving parent ingredients:`, updateIngredientsError);
            showToast(`⚠️ Recipe loaded/created but failed to save ingredients`, 'warning');
          }

          createdRecipes.push(parentRecipe);

          // Create variant recipes (excluding base) and copy ingredients
          for (const variant of group.variants) {
            if (variant === baseVariant) continue;

            // 🔑 USE CUSTOM MULTIPLIER from multiplierMap parameter
            const keyword = variant.detectedPortionSize.toLowerCase();
            const multiplier = multiplierMap[keyword] || 1.0;

            debugLog(`   🔸 Creating variant: ${variant.name} (${variant.detectedPortionSize} = ×${multiplier})`);

            let variantRecipe;
            let variantAlreadyExists = false;
            
            try {
              variantRecipe = await RecipesService.createRecipe({
                name: variant.name,
                businessId,
                category_id: selectedCategory,
                is_parent: false,
                parent_id: parentRecipe.id,
                portionMultiplier: multiplier, // CAMEL CASE to match backend expectations
                price: variant.price || 0
              });
              debugLog(`       Created variant: ${variantRecipe.name} (ID: ${variantRecipe.id})`);
            } catch (variantCreateError) {
              // Handle duplicate variant error - fetch existing instead
              if (variantCreateError.response?.status === 409 || variantCreateError.message?.includes('Duplicate')) {
                console.warn(`⚠️ Variant "${variant.name}" already exists, fetching existing...`);
                variantAlreadyExists = true;
                
                try {
                  const allRecipes = await RecipesService.getRecipes(businessId);
                  variantRecipe = allRecipes.find(r => r.name === variant.name);
                  
                  if (!variantRecipe) {
                    console.error(`❌ Could not find existing variant: ${variant.name}`);
                    showToast(`⚠️ Skipped variant "${variant.name}" - could not find existing`, 'warning');
                    continue;
                  }
                  
                  debugLog(` Found existing variant: ${variantRecipe.name} (ID: ${variantRecipe.id})`);
                } catch (fetchError) {
                  console.error(`❌ Error fetching existing variant:`, fetchError);
                  showToast(`⚠️ Skipped variant "${variant.name}"`, 'warning');
                  continue;
                }
              } else {
                // For other errors, log but continue
                console.error(`❌ Error creating variant ${variant.name}:`, variantCreateError);
                showToast(`❌ Failed to create variant "${variant.name}"`, 'error');
                continue;
              }
            }

            const variantRecipeId = variantRecipe.id;

            // Copy description from parent to variant
            if (baseVariant.description) {
              try {
                await RecipesService.updateRecipe(variantRecipeId, {
                  description: baseVariant.description
                }, businessId);
                debugLog(`      📝 Saved description for variant: ${variantRecipe.name}`);
              } catch (descError) {
                console.warn(`      ⚠️ Failed to save description for ${variantRecipe.name}:`, descError);
              }
            }

            // Copy ingredients from parent and adjust quantities based on multiplier
            const adjustedIngredients = ingredients.map(ing => ({
              ...ing,
              quantity: Math.round(ing.quantity * multiplier * 100) / 100 // Apply multiplier and round to 2 decimals
            }));

            debugLog(`      📊 Adjusting ${adjustedIngredients.length} ingredients with multiplier ×${multiplier}`);
            debugLog(`      💾 Saving ${adjustedIngredients.length} ingredients for variant ID ${variantRecipeId}`);

            try {
              // Save ingredients with businessId
              await RecipesService.updateRecipeIngredients(variantRecipeId, adjustedIngredients, businessId);
              debugLog(`       Saved ${adjustedIngredients.length} ingredients for variant: ${variantRecipe.name}`);
            } catch (ingredientsSaveError) {
              console.error(`      ❌ Failed to save ingredients for ${variantRecipe.name}:`, ingredientsSaveError);
              showToast(`⚠️ Created "${variantRecipe.name}" but failed to save ingredients`, 'warning');
            }

            createdRecipes.push(variantRecipe);
          }

        } catch (error) {
          // Only show toast for non-duplicate errors
          if (error.response?.status !== 409 && !error.message?.includes('Duplicate')) {
            console.error(`❌ Error processing variant group ${group.baseName}:`, error);
            showToast(`❌ Failed to create variants for "${group.baseName}"`, 'error');
          }
        }
      }

      // Process standalone items
      await processStandaloneItems(standalone);

      // Close modal and reset state
      setShowMultiplierEditor(false);
      setPendingVariantGroups([]);
      setEditableMultipliers({});
      setShowOCRModal(false);
      setOcrResults([]);
      setScannedMenuItems([]);
      setMenuScanJob(null);
      setMenuScanProgress(0);

      //  Set flag to trigger menu refresh when returning to StockOutForm
      localStorage.setItem('itemMappingComplete', 'true');
      debugLog(' Item mapping completed - flag set for StockOutForm refresh');

      // Show summary
      let message = ` Created ${createdRecipes.length + standalone.length} recipes from menu scan!`;
      if (validGroups.length > 0) {
        message += `\n🍽️ Processed ${validGroups.length} variant group${validGroups.length > 1 ? 's' : ''} with your custom multipliers!`;
      }
      showToast(message);

      // Refresh recipe list
      await new Promise(resolve => setTimeout(resolve, 500));
      const refreshedRecipes = await RecipesService.getRecipes(businessId);
      setRecipes(applyLocalReviewDrafts(refreshedRecipes));

      // Auto-open first recipe
      if (refreshedRecipes.length > 0) {
        const firstRecipe = refreshedRecipes.find(r => r.name === createdRecipes[0]?.name) || refreshedRecipes[0];
        setSelectedRecipe(withImageMeta(firstRecipe));
      }

    } catch (error) {
      console.error('❌ Error in processVariantGroupsWithMultipliers:', error);
      showToast('❌ Failed to process recipes. Please try again.', 'error');
    }
  };

  // 🆕 Process standalone items (no variants)
  const processStandaloneItems = async (standalone) => {
    if (!standalone || standalone.length === 0) return;

    const businessId = UnitMappingService.getBusinessId();
    debugLog(`\n📝 Processing ${standalone.length} standalone items...`);

    for (let i = 0; i < standalone.length; i++) {
      const item = standalone[i];
      setRecipeCreationProgress({ 
        current: i + 1, 
        total: standalone.length, 
        message: `Item name: ${item.name}` 
      });
      try {
        debugLog(`\n📝 Creating standalone recipe: ${item.name}`);

        // AI fallback for complete recipe data if needed
        if (!item.ingredients || item.ingredients.length === 0) {
          const baseUrl = process.env.REACT_APP_API_URL || '';
          const apiUrl = baseUrl.endsWith('/api') ? `${baseUrl}/ai/generate-ingredients` : `${baseUrl}/api/ai/generate-ingredients`;
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-business-id': businessId },
            body: JSON.stringify({ dishName: item.name, plateCount: 10 })
          });
          if (!response.ok) throw new Error(`AI generation failed (${response.status})`);
          const data = await response.json();
          if (data.success && data.ingredients) {
            item.ingredients = data.ingredients;
            
            // Save complimentary items to Recipe Library
            if (data.complimentary_items?.length > 0) {
              try {
                await RecipeLibraryService.updateRecipe(item.name, {
                  complimentary_items: data.complimentary_items || []
                }, businessId);
                debugLog(` Saved complimentary items for ${item.name}`);
              } catch (e) {
                console.warn('Failed to save to Recipe Library:', e);
              }
            }
          } else {
            console.warn(`⚠️ No recipe data for ${item.name}, skipping...`);
            continue;
          }
        }

        // Fetch complimentary items from Recipe Library
        let complimentaryItems = [];
        
        try {
          const recipeLibraryData = await RecipeLibraryService.getRecipe(item.name, businessId);
          if (recipeLibraryData) {
            complimentaryItems = recipeLibraryData.complimentary_items || [];
            debugLog(` Fetched from Recipe Library: ${complimentaryItems.length} complimentary items`);
          }
        } catch (error) {
          console.warn(`⚠️ Could not fetch Recipe Library data for ${item.name}:`, error.message);
        }

        // Create recipe with price from OCR and complimentary items from Recipe Library
        let createdRecipe;
        try {
          createdRecipe = await RecipesService.createRecipe({
            name: item.name,
            businessId,
            category_id: selectedCategory,
            is_parent: false,
            parent_id: null,
            price: item.price || 0,  // Include price from OCR scan
            source: 'ocr' // Flag for backend to skip image generation
          }, businessId);
          // complimentary_items is NOT accepted by POST /recipes — it destructures a fixed
          // field list and ignores the rest, so passing it there quietly lost the Recipe
          // Library's complimentary items on every scanned item. It has to be a second call
          // to /customizations, which is the only writer of menu_complimentary_items.
          if (createdRecipe?.id && Array.isArray(complimentaryItems) && complimentaryItems.length > 0) {
            try {
              await RecipesService.updateRecipeCustomizations(
                createdRecipe.id,
                { complimentary_items: complimentaryItems },
                businessId
              );
            } catch (compError) {
              // The item itself was created; losing its complimentary items is worth a
              // warning but must not abort the rest of the scan.
              console.warn(`⚠️ Created "${item.name}" but could not save its complimentary items:`, compError?.message || compError);
            }
          }
        } catch (createError) {
          // Handle duplicate item error
          if (createError.response?.status === 409) {
            console.warn(`⚠️ Skipping duplicate: ${item.name} already exists`);
            showToast(`⚠️ Skipped "${item.name}" - already exists in menu`, 'warning');
            continue;
          }
          throw createError;
        }

        const recipeId = createdRecipe.id;
        debugLog(` Created recipe: ${item.name} (ID: ${recipeId}) with ${complimentaryItems.length} complimentary items`);

        // Save ingredients — all now have zero quantities with correct units (user fills manually)
        debugLog(`📦 Processing ingredients for ${item.name}:`, item.ingredients);
        const ingredients = item.ingredients.map(ing => {
          const ingredientName = ing.name || ing.ingredient;
          
          // Extract unit from whichever field is available
          let unit = 'g';
          if (ing.unit) {
            unit = ing.unit;
          } else if (ing.quantity_standard) {
            const unitMatch = (ing.quantity_standard || '').match(/[a-zA-Z]+/);
            unit = unitMatch ? unitMatch[0] : 'g';
          }
          
          const mappedIng = {
            name: ingredientName,
            quantity: 0,
            unit: unit
          };
          
          debugLog(`  📍 Ingredient: ${ingredientName} | unit: ${mappedIng.unit} | qty: 0 (user fills manually)`);
          
          return mappedIng;
        });
        debugLog(`📊 Final ingredients array for ${item.name}:`, ingredients);

        await RecipesService.updateRecipeIngredients(recipeId, ingredients, businessId);
        debugLog(` Saved ${ingredients.length} ingredients for ${item.name}`);

      } catch (error) {
        console.error(`❌ Error processing ${item.name}:`, error);
        // Only show alert for non-duplicate errors
        if (error.response?.status !== 409) {
          console.warn(`⚠️ Error occurred but continuing: ${error.message}`);
          // Don't show alert - recipe might have been created successfully despite the error
          // The error is often a transient issue that resolves itself
        }
      }
    }
    
    // Close modal and refresh recipes after all items processed
    debugLog(' All standalone items processed successfully');
    setShowOCRModal(false);
    setScannedMenuItems([]);
    setOcrResults([]);
    setMenuScanJob(null);
    setMenuScanProgress(0);
    
    //  Set flag to trigger menu refresh when returning to StockOutForm
    localStorage.setItem('itemMappingComplete', 'true');
    debugLog(' Item mapping completed - flag set for StockOutForm refresh');
    
    // Refresh the recipes list
    const refreshedRecipes = await RecipesService.getRecipes(businessId);
    setRecipes(applyLocalReviewDrafts(refreshedRecipes));
    showToast(` Added ${standalone.length} recipes successfully!`);
  };

  const handleSaveClick = async () => {
    // Prevent saving if cross-family conversion modal is open
    if (showNewConversionModal) {
      showToast('⚠️ Please complete or cancel the unit conversion first', 'warning');
      return;
    }

    // Flush the row being edited so the payload matches the screen.
    let listForValidation = ingredientList;
    if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
      const savedList = await saveIngredient({ silent: true, returnUpdatedList: true });
      if (!savedList) {
        showToast('Please complete the current ingredient before confirming');
        return;
      }
      listForValidation = savedList;
    }

    const plateCount = Number(plateStep) > 0 ? Number(plateStep) : 1;
    const { ingredients, roundedToZero } = buildPerPlatePayload(listForValidation, plateCount, { getDisplayQty });
    if (ingredients.length === 0) {
      showToast('Add at least one ingredient quantity before confirming');
      return;
    }
    if (roundedToZero.length > 0) {
      setShowRoundOffModal(true);
      showToast('Round off to zero. Costing cannot be applied.', 'warning');
    }

    // Ask the server BEFORE the summary: units, quantities, unit family against stock,
    // and the full duplicate scan (including the AI layer). Nothing is written.
    let verdict;
    try {
      verdict = await RecipesService.validateRecipeIngredients(selectedRecipe.id, {
        ingredients,
        duplicate_overrides: duplicateOverridesRef.current,
      });
    } catch (error) {
      showToast(`Could not validate ingredients: ${error?.message || 'Unknown error'}`, 'warning');
      return;
    }

    if (!verdict.ok) {
      const byName = {};
      verdict.blockers.forEach((b) => {
        const row = ingredients[b.index];
        const key = row ? row.name.trim().toLowerCase() : `#${b.index}`;
        byName[key] = b.message;
      });
      setIngredientBlockers(byName);
      showToast(`${verdict.blockers.length} ingredient${verdict.blockers.length > 1 ? 's need' : ' needs'} attention before saving`, 'warning');
      return;
    }
    setIngredientBlockers({});

    if (verdict.conflicts.length > 0) {
      pendingSaveListRef.current = listForValidation;
      pendingSavePlateCountRef.current = plateCount;
      setDuplicateConflicts(verdict.conflicts);
      const defaults = {};
      verdict.conflicts.forEach((d) => { defaults[d.attempted_name] = { action: 'use_existing', reason: 'Intentional Variant' }; });
      setDuplicateDecisions(defaults);
      setShowDuplicateModal(true);
      return;
    }

    setSummaryOpenDropdown('ingredients');
    setShowSaveConfirmationModal(true);
  };

  const handleConfirmClick = () => {
    const hasZeroPreps = summarySavedPreparationItems.length === 0;
    const hasZeroComplimentary = summaryComplimentaryItems.length === 0;

    if (hasZeroPreps || hasZeroComplimentary) {
      let msg = '';
      if (hasZeroPreps && hasZeroComplimentary) {
        msg = 'Please verify that there is no Saved Preparations and no Complimentary Items for this item. Would you like to continue?';
      } else if (hasZeroPreps) {
        msg = 'Please verify that there is no Saved Preparations for this item. Would you like to continue?';
      } else {
        msg = 'Please verify that there is no Complimentary Items for this item. Would you like to continue?';
      }
      setVerifyModalMessage(msg);
      setShowVerifyModal(true);
    } else {
      confirmAndSave();
    }
  };

  const confirmAndSave = async () => {
    if (savingIngredients) return; // prevent double-submit
    setSummaryOpenDropdown(null);
    setShowSaveConfirmationModal(false);

    let ingredientListForSave = ingredientList;

    if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
      const savedList = await saveIngredient({ silent: true, returnUpdatedList: true });
      if (!savedList) {
        showToast('Please complete the current ingredient before confirming');
        return;
      }
      ingredientListForSave = savedList;
    }

    const normalizedPlateCount = Number(plateStep) > 0 ? Number(plateStep) : 1;
    await savePerPlateToDB(ingredientListForSave, normalizedPlateCount);
  };

  const formatSummaryTitle = (value) => {
    return String(value || '')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatSummaryNumber = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '0';
    if (Number.isInteger(numeric)) return String(numeric);
    return String(Number(numeric.toFixed(2)));
  };

  const getPerPlateSummaryValue = (item) => {
    const q10 = Number(item?.qty10 || 0);
    const q100 = Number(item?.qty100 || 0);
    if (q10 > 0) return q10 / 10;
    if (q100 > 0) return q100 / 100;
    return 0;
  };

  /**
   * The quantity that will actually be written to the recipe for ONE plate.
   *
   * The Item Summary is the last thing seen before saving, so it reports this rather
   * than the batch view being edited: showing 15 kg while storing 1 kg/plate states a
   * different number from the one being committed, whatever the header says.
   * RecipeIngredients.quantity is always a per-plate figure, so this is what lands.
   *
   * Deliberately mirrors savePerPlateToDB: a plate count below 1 keeps the quantity as
   * entered, anything else is divided down to a single plate. Any change to the save
   * rule has to change here too, or the confirmation stops matching the write.
   */
  const getSavedPerPlateValue = (item) => {
    const step = Number(plateStep) > 0 ? Number(plateStep) : 1;
    const normalizedPlateCount = step < 1 ? 1 : step;
    return (getPerPlateSummaryValue(item) * step) / normalizedPlateCount;
  };

  const normalizeSummaryUnit = (unit) => {
    const key = String(unit || '').trim().toLowerCase();
    const aliases = {
      kg: 'kg', kilogram: 'kg', kilograms: 'kg',
      g: 'g', gram: 'g', grams: 'g',
      mg: 'mg', milligram: 'mg', milligrams: 'mg',
      l: 'l', litre: 'l', litres: 'l', liter: 'l', liters: 'l',
      ml: 'ml', milliliter: 'ml', milliliters: 'ml', millilitre: 'ml', millilitres: 'ml'
    };
    return aliases[key] || key;
  };

  const formatShortUnitLabel = (unit) => {
    const normalized = normalizeSummaryUnit(unit);
    if (normalized === 'l') return 'L';
    return normalized;
  };

  // Canonical display: full names (Gram/Kilogram/Millilitre/Litre/Pcs), auto-scaled.
  const formatCompactQtyUnit = (value, unit) => formatQuantity(value, unit);

  // Delegates to THE frontend converter. mg is pre-mapped to grams because a few
  // legacy rows still carry it (it is outside the Strict 5, so convert() refuses it);
  // anything else convert() cannot handle passes through unchanged, as before.
  const convertSummaryQuantity = (value, fromUnit, toUnit) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;

    let from = normalizeSummaryUnit(fromUnit);
    let to = normalizeSummaryUnit(toUnit);
    if (!from || !to || from === to) return numeric;

    let workingValue = numeric;
    if (from === 'mg') { workingValue = numeric * 0.001; from = 'g'; }
    if (to === 'mg') {
      const inGrams = from === 'g' ? workingValue : convert(workingValue, from, 'g');
      return inGrams === null ? numeric : inGrams * 1000;
    }

    const converted = convert(workingValue, from, to);
    return converted === null ? numeric : converted;
  };

  const getScaledPreparationIngredientRows = (prepItem, prepMeta) => {
    const rows = Array.isArray(prepMeta?.components) ? prepMeta.components : [];
    const totalQty = Number(prepMeta?.total_weight);
    const totalUnit = prepMeta?.total_weight_unit_symbol || prepMeta?.total_weight_unit_name || prepItem?.unit;
    const qty10 = Number(prepItem?.qty10) || 0;
    const qty100 = Number(prepItem?.qty100) || 0;
    const usedPerPlateRaw = qty100 > 0 ? (qty100 / 100) : (qty10 > 0 ? (qty10 / 10) : 0);
    // Per plate, matching getSavedPerPlateValue — the summary reports what is saved, so a
    // preparation's component breakdown is shown for one plate too, not for the batch
    // view being edited.
    const step = Number(plateStep) > 0 ? Number(plateStep) : 1;
    const normalizedPlateCount = step < 1 ? 1 : step;
    const usedSavedPerPlate = (usedPerPlateRaw * step) / normalizedPlateCount;
    const usedForStepInTotalUnit = convertSummaryQuantity(usedSavedPerPlate, prepItem?.unit, totalUnit);
    const scaleFactor = totalQty > 0 ? (usedForStepInTotalUnit / totalQty) : 0;

    return rows.map((ing) => {
      const sourceQty = Number(ing?.weight ?? ing?.quantity ?? 0);
      const sourceUnit = String(ing?.unit_symbol || ing?.unit_name || '').trim();

      // Deliberately NOT pre-rounded here. A component stored in a high unit scales to a
      // sub-1 value for a single plate (1 kg of a 2 kg batch used 5 g per plate -> 0.0025 kg);
      // rounding that to 2dp before it reaches the formatter turns a real quantity into "0".
      // formatQuantity auto-scales kg->g / L->ml at render, so the raw value is what it needs.
      return {
        itemName: String(ing?.item_name || '').trim(),
        quantity: sourceQty * scaleFactor,
        unit: sourceUnit || 'g'
      };
    });
  };

  const summaryComplimentaryItems = (linkedComplimentaryItems?.length > 0
    ? linkedComplimentaryItems
    : (selectedComplimentaryItems || []).map((entry) => {
      const itemName = typeof entry === 'string' ? entry : entry?.name;
      return { name: itemName };
    })
  ).filter((item) => String(item?.name || '').trim());

  const normalizeSummaryKey = (value) => String(value || '').trim().toLowerCase();

  const summaryActiveItems = ingredientList.filter(
    (i) => (i.name || '').trim() && i.name !== 'New Ingredient' && (i.qty10 > 0 || i.qty100 > 0)
  );

  /**
   * Which ingredient names in this recipe are driven by a saved preparation.
   *
   * Keyed TWO ways, matching how the deduction path resolves the same question
   * (ingredientDeduction.js: `pd.composite_item_id = $3 OR LOWER(pd.composite_name) = LOWER($2)`):
   *
   *   1. the preparation's own name — the historical implicit match, for a preparation that was
   *      never converted and shares its name with the ingredient;
   *   2. the name of the item it was CONVERTED to drive, resolved through composite_item_id.
   *
   * Only the first existed here, which is why a converted pair whose names differ went unmarked:
   * "Garam Masala" is deducted as six spices at sale time, but this screen drew it as an ordinary
   * purchased ingredient because no preparation is called "Garam Masala". The link is the fact;
   * the name was never more than a lucky coincidence.
   */
  const savedPreparationByName = new Map();
  for (const prep of savedPreparations || []) {
    const ownName = normalizeSummaryKey(prep?.composite_name);
    if (ownName) savedPreparationByName.set(ownName, prep);

    if (prep?.composite_item_id !== null && prep?.composite_item_id !== undefined) {
      const drivenName = normalizeSummaryKey(inventoryNameById[String(prep.composite_item_id)]);
      // set() not setIfAbsent: the explicit link is the stronger claim, so where an ingredient
      // name is reachable both ways the converted preparation is the one described.
      if (drivenName) savedPreparationByName.set(drivenName, prep);
    }
  }

  // The same question the yellow dot answers, asked by the import (utils/recipeImportMerge):
  // a saved preparation already on the dish is added onto rather than kept.
  const isSavedPreparationIngredient = (name) => savedPreparationByName.has(normalizeSummaryKey(name));

  const summarySavedPreparationItems = summaryActiveItems.filter((item) =>
    savedPreparationByName.has(normalizeSummaryKey(item?.name))
  );

  const summaryIngredientItems = summaryActiveItems.filter(
    (item) => !savedPreparationByName.has(normalizeSummaryKey(item?.name))
  );

  const getComplimentarySummaryMeta = (item) => {
    const ingredients = Array.isArray(item?.ingredients) ? item.ingredients : [];
    const parsedCount = Number(item?.ingredient_count);
    const ingredientCount = Number.isFinite(parsedCount) ? parsedCount : ingredients.length;
    const scalingType = String(item?.scaling_type || '').toUpperCase();
    const parsedMultiplier = Number(item?.quantity_multiplier);
    const quantityMultiplier = Number.isFinite(parsedMultiplier) ? parsedMultiplier : 1;

    let scalingLabel = 'Association saved';
    if (scalingType === 'FIXED') {
      scalingLabel = `Fixed x${formatSummaryNumber(quantityMultiplier)}`;
    } else if (scalingType === 'SCALED') {
      scalingLabel = `Scaled x${formatSummaryNumber(quantityMultiplier)}`;
    }

    const measurementPreview = ingredients.length > 0
      ? ingredients
          .slice(0, 3)
          .map((ing) => {
            const qtyLabel = formatQuantity(ing?.quantity, ing?.unit_symbol);
            const ingName = String(ing?.item_name || '').trim();
            return `${qtyLabel}${ingName ? ` ${ingName}` : ''}`;
          })
          .join(', ')
      : 'No measurements yet';

    const moreSuffix = ingredients.length > 3 ? ` +${ingredients.length - 3} more` : '';

    return {
      ingredientCount,
      scalingLabel,
      measurementText: `${measurementPreview}${moreSuffix}`
    };
  };

  const handleMarkAsReview = async () => {
    if (!selectedRecipe?.id) return;

    let ingredientListForDraft = ingredientList;

    if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
      const savedList = await saveIngredient({ silent: true, returnUpdatedList: true });
      if (!savedList) {
        showToast('Please complete the current ingredient before marking as review');
        return;
      }
      ingredientListForDraft = savedList;
    }

    try {
      persistLocalReviewDraft(ingredientListForDraft);
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      setAutoSaveStatus(null);
      setShowUnsavedChangesModal(false);
      setIngredientHistory([]);
      setHistoryIndex(-1);
      setRecipes(prev => applyLocalReviewDrafts(prev));
      setSelectedRecipe(null);
      setEditingIngredientIndex(null);
      showToast('Marked as under review. Changes saved locally.');
    } catch (error) {
      console.error('Failed to mark as review:', error);
      showToast('Failed to mark as review');
    }
  };

  // Auto-save: UI status only (DB save happens on Confirm)
  const autoSaveIngredients = async () => {
    if (!selectedRecipe?.id) return;
    const validIngredients = ingredientList.filter(i => (i.name || '').trim() && i.name !== 'New Ingredient' && (i.qty10 > 0 || i.qty100 > 0));
    if (validIngredients.length === 0) return;
    setAutoSaveStatus('saving');
    try {
      await new Promise(resolve => setTimeout(resolve, 50));
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 1500);
      debugLog('🔄 Auto-save status updated (memory only)');
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus(null);
    }
  };

  const ensureInventoryItemsForIngredients = async (ingredients) => {
    const businessId = UnitMappingService.getBusinessId();
    if (!businessId) throw new Error('Missing business_id. Please log in again.');

    const existing = new Set(Object.keys(inventoryStdUnitMap || {}).map(k => String(k).toLowerCase()));

    for (const ingredient of ingredients) {
      const ingredientName = String(ingredient?.name || '').trim();
      if (!ingredientName) continue;

      const key = ingredientName.toLowerCase();
      if (existing.has(key)) continue;

      // Check if this ingredient is a custom preparation
      try {
        const checkResult = await RecipesService.checkPreparation(ingredientName, businessId);
        if (checkResult.isPreparation) {
          debugLog(`✅ "${ingredientName}" is a preparation - skipping standard inventory creation in frontend`);
          continue;
        }
      } catch (prepErr) {
        console.warn(`Error checking if "${ingredientName}" is a preparation:`, prepErr);
      }

      try {
        await InventoryService.createInventoryItem({
          name: ingredientName,
          unit_symbol: ingredient.unit || 'g',
          business_id: businessId,
          source: 'ingredient_mapping'
        });

        existing.add(key);
        setInventoryStdUnitMap(prev => ({ ...prev, [key]: ingredient.unit || 'g' }));
      } catch (error) {
        const status = error?.status || error?.response?.status;
        const data = error?.data || error?.response?.data || {};
        const message = String(data?.error || data?.message || data?.details || error?.message || '').toLowerCase();
        if (status === 409 || message.includes('already') || message.includes('exists') || message.includes('duplicate') || message.includes('unique constraint')) {
          existing.add(key);
          continue;
        }
        console.warn(`Failed to create inventory item "${ingredientName}" during confirm save. Continuing with recipe save.`, error);
      }
    }
  };

  const savePerPlateToDB = async (listForSave = ingredientList, plateCountOverride, explicitOverrides) => {
    if (savingIngredientsRef.current) return; // prevent concurrent saves (ref-based — no stale React state)
    savingIngredientsRef.current = true;
    setSavingIngredients(true);
    try {
      let recipeDetailsSaved = true;
      const sourceList = Array.isArray(listForSave) ? listForSave : ingredientList;
      const rawPlateCount = Number(plateCountOverride) > 0
        ? Number(plateCountOverride)
        : (Number(plateStep) > 0 ? Number(plateStep) : 1);
      
      // If plate count is < 1, save exactly as entered (divide by 1). If >= 1, normalize to 1 plate.
      const normalizedPlateCount = rawPlateCount < 1 ? 1 : rawPlateCount;
      
      pendingSavePlateCountRef.current = normalizedPlateCount;

      // ONE payload builder, shared with the validate pre-flight and with QRitemmap:
      // per-plate at 4 dp, kg/L → g/ml, same-family merge only (no invented densities),
      // kitchen units passed through for the server's Layer A.5.
      const { ingredients: normalizedIngredients, roundedToZero } = buildPerPlatePayload(sourceList, normalizedPlateCount, { getDisplayQty });
      if (roundedToZero.length > 0) {
        setShowRoundOffModal(true);
        showToast("Round off to zero. Costing cannot be applied.", "warning");
      }

      if (normalizedIngredients.length === 0) {
        showToast('Add at least one ingredient quantity before confirming');
        return;
      }

      // Update local autocomplete maps eagerly. Backend is now authoritative for
      // onboarding duplicate detection and auto-creation of genuinely new items.
      normalizedIngredients.forEach(ing => {
        const key = (ing.name || '').trim().toLowerCase();
        if (key) {
          setInventoryStdUnitMap(prev => ({ ...prev, [key]: prev[key] || ing.unit || 'g' }));
          setInventoryItemNames(prev => prev.includes(ing.name.trim()) ? prev : [...prev, ing.name.trim()]);
        }
      });

      const businessId = UnitMappingService.getBusinessId();
      // SP9 (C3): status rides in the same request/transaction as the rows it describes.
      // Recipe Book only: the plate count the quantities were entered for (e.g. 25), kept so
      // the Recipe Book PDF can list this dish on its Custom Plates page at that count. The
      // ingredients above are still saved per 1 plate exactly as before; null (1 plate or
      // less) clears it.
      const savePayload = {
        ingredients: normalizedIngredients,
        status: 'confirmed',
        custom_plate_count: rawPlateCount > 1 ? rawPlateCount : null
      };
      // Use explicitly passed overrides (from retry) or fall back to ref (always up-to-date)
      const activeOverrides = explicitOverrides || duplicateOverridesRef.current;
      if (Object.keys(activeOverrides).length > 0) {
        savePayload.duplicate_overrides = activeOverrides;
      }

      let saveResult;
      try {
        saveResult = await http.put(`/recipes/${selectedRecipe.id}/ingredients`, savePayload);
      } catch (saveError) {
        if (saveError?.status === 409 && saveError?.data?.error === 'duplicate_detected') {
          const list = Array.isArray(saveError.data.duplicates)
            ? saveError.data.duplicates
            : (saveError.data.duplicate ? [saveError.data.duplicate] : []);
          pendingSaveListRef.current = listForSave;
          setDuplicateConflicts(list);
          // Default every duplicate to "use existing"; user can flip any to "create separate".
          const defaults = {};
          list.forEach(d => { defaults[d.attempted_name] = { action: 'use_existing', reason: 'Intentional Variant' }; });
          setDuplicateDecisions(defaults);
          setShowDuplicateModal(true);
          return;
        }
        // A race that slipped past validate: the gate refused rows at save time.
        if (saveError?.status === 400 && saveError?.data?.error === 'validation_failed') {
          const byName = {};
          (saveError.data.blockers || []).forEach((b) => {
            const row = normalizedIngredients[b.index];
            byName[row ? row.name.trim().toLowerCase() : `#${b.index}`] = b.message;
          });
          setIngredientBlockers(byName);
          showToast('Some ingredients were refused — see the messages above the Add Ingredient button', 'warning');
          return;
        }
        throw saveError;
      }

      // ⚡ Fix 6: Run complimentary items update + status update IN PARALLEL (independent operations)
      const parallelTasks = [];

      // Save selected complimentary items.
      //
      // MUST go to /customizations, not to PUT /recipes/:id. That handler destructures only
      // name/price/servings/description/portionMultiplier — `complimentary_items` is not a
      // field it knows, so it was dropped on the floor while the request still returned
      // success (it performs a timestamp-only update). The complimentary items chosen here
      // were never written to menu_complimentary_items, and the UI logged "Updated" anyway.
      // /customizations is the only endpoint that writes that junction table.
      if (selectedRecipe && selectedRecipe.id) {
        const complimentaryToSave = selectedComplimentaryItems || [];
        debugLog(`💾 Replacing MenuItems complimentary items for ${selectedRecipe.name}:`, {
          complimentary_items: complimentaryToSave.length
        });
        parallelTasks.push(
          RecipesService.updateRecipeCustomizations(
            selectedRecipe.id,
            { complimentary_items: complimentaryToSave },
            businessId
          )
            .then(() => debugLog(` Updated MenuItems with selected complimentary items for ${selectedRecipe.name} (Business ${businessId})`))
            .catch((customError) => {
              recipeDetailsSaved = false;
              console.error('Failed to save complimentary items to MenuItems:', customError);
            })
        );
      }

      // SP9 (C3): status is folded into the ingredients PUT above — no separate request.

      // Await both in parallel
      await Promise.all(parallelTasks);

      if (recipeDetailsSaved) {
        clearRecipeReviewDraft(selectedRecipe, businessId);
      }

      // ⚡ Fix 7: Optimistic update instead of full recipe list refresh
      // Instead of re-fetching ALL recipes (heavy JOIN), update local state directly
      setRecipes(prev => {
        const updated = prev.map(r => {
          const rId = r.menu_item_id || r.id;
          if (rId === selectedRecipe.id) {
            return {
              ...r,
              ingredientscount: String(normalizedIngredients.length),
              ingredientsCount: String(normalizedIngredients.length),
              status: 'confirmed'
            };
          }
          return r;
        });
        return applyLocalReviewDrafts(updated);
      });

      localStorage.setItem('itemMappingComplete', 'true');
      debugLog(' Item mapping completed - flag set for StockOutForm refresh');

      originalIngredientSnapshotRef.current = buildIngredientSnapshot(sourceList);
      setShowUnsavedChangesModal(false);

      setSelectedRecipe(null);
      setEditingIngredientIndex(null);

      setIngredientHistory([]);
      setHistoryIndex(-1);
      setDuplicateOverrides({});
      duplicateOverridesRef.current = {};
      setDuplicateConflictData(null);
      setDuplicateConflicts([]);
      setDuplicateDecisions({});
      setDuplicateForceReason('Intentional Variant');
      pendingSaveListRef.current = null;
      pendingSavePlateCountRef.current = null;

      const pendingTSRData = localStorage.getItem('pendingTSRData');
      if (pendingTSRData) {
        showToast('Recipe saved. Returning to TSR...');
        setTimeout(() => {
          goTo && goTo('todays-sales-report');
        }, 300);
      } else {
        if (saveResult?.mappedPreparations && saveResult.mappedPreparations.length > 0) {
          const prepNames = saveResult.mappedPreparations.map(p => p.name).join(', ');
          showToast(`✅ ${prepNames} mapped! (TSR will auto-decompose during sales)`, 'success');
        } else {
          showToast('Recipe saved successfully!');
        }
      }
    } catch (error) {
      console.error('Failed to save per-plate ingredients:', error);
      showToast(`Failed to save recipe changes: ${error?.message || 'Unknown error'}`);
    } finally {
      savingIngredientsRef.current = false;
      setSavingIngredients(false);
    }
  };

  // Set/patch a single duplicate's decision (action + reason)
  const setDuplicateDecision = (name, patch) => {
    setDuplicateDecisions(prev => ({
      ...prev,
      [name]: { action: 'use_existing', reason: 'Intentional Variant', ...(prev[name] || {}), ...patch }
    }));
  };

  // Bulk-set every duplicate's action at once ("use existing for all" / "create separate for all")
  const setAllDuplicateDecisions = (action) => {
    setDuplicateDecisions(prev => {
      const next = { ...prev };
      duplicateConflicts.forEach(c => {
        next[c.attempted_name] = { action, reason: next[c.attempted_name]?.reason || 'Intentional Variant' };
      });
      return next;
    });
  };

  // Apply ALL decisions in one shot → build the full overrides map → single re-save.
  const applyAllDuplicateDecisions = () => {
    const overrides = { ...duplicateOverridesRef.current };
    for (const c of duplicateConflicts) {
      const d = duplicateDecisions[c.attempted_name] || { action: 'use_existing' };
      if (d.action === 'force_create') {
        overrides[c.attempted_name] = {
          action: 'force_create',
          matched_item_id: c.existing_item_id,
          matched_item_name: c.existing_item_name,
          reason: d.reason || 'Intentional Variant'
        };
      } else {
        overrides[c.attempted_name] = {
          action: 'use_existing',
          existing_item_id: c.existing_item_id
        };
      }
    }
    duplicateOverridesRef.current = overrides; // update ref immediately (no React batching delay)
    setDuplicateOverrides(overrides);
    setShowDuplicateModal(false);
    setDuplicateConflicts([]);
    setDuplicateDecisions({});
    // Decisions made → re-run the pre-flight (now carrying the overrides in the ref)
    // and open the summary. The PUT still sends the same overrides at save time.
    setTimeout(() => handleSaveClick(), 150);
  };

  const cancelDuplicateModal = () => {
    setShowDuplicateModal(false);
    setDuplicateConflicts([]);
    setDuplicateDecisions({});
    pendingSaveListRef.current = null;
    pendingSavePlateCountRef.current = null;
    pendingConfirmListRef.current = null;
  };

  const exitRecipeEditorWithoutSaving = () => {
    setIngredientBlockers({});
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    setShowUnsavedChangesModal(false);
    setAutoSaveStatus(null);
    setIngredientList([]);
    originalIngredientSnapshotRef.current = [];
    setEditingIngredientIndex(null);
    setFormData({ name: '', quantity: 0, unit: '' });
    setShowNameSuggestions(false);
    setIngredientHistory([]);
    setHistoryIndex(-1);
    setShowDuplicateModal(false);
    setDuplicateConflictData(null);
    setDuplicateConflicts([]);
    setDuplicateDecisions({});
    setDuplicateOverrides({});
    duplicateOverridesRef.current = {};
    setDuplicateForceReason('Intentional Variant');
    pendingSaveListRef.current = null;
    pendingSavePlateCountRef.current = null;
    pendingConfirmListRef.current = null;
    setSelectedRecipe(null);
  };

  const hasUnsavedIngredientChanges = () => {
    const current = JSON.stringify(buildIngredientSnapshot(ingredientList));
    const original = JSON.stringify(originalIngredientSnapshotRef.current || []);
    return current !== original;
  };

  const discardUnsavedChangesAndExitEditor = () => {
    setShowUnsavedChangesModal(false);
    exitRecipeEditorWithoutSaving();
  };

  const handleRecipeBackButton = () => {
    // Prevent closing if cross-family conversion modal is open
    if (showNewConversionModal) {
      showToast('⚠️ Please complete or cancel the unit conversion first', 'warning');
      return;
    }
    
    if (hasUnsavedIngredientChanges()) {
      setShowUnsavedChangesModal(true);
      return;
    }
    exitRecipeEditorWithoutSaving();
  };

  // const squareBtnStyle = {
  //   // Match TSR complementary quantity button styling
  //   width: 24,
  //   height: 24,
  //   borderRadius: 4,
  //   border: '1px solid #d1d5db',
  //   backgroundColor: '#ffffff',
  //   color: '#000',
  //   fontWeight: 600,
  //   fontSize: 12,
  //   cursor: 'pointer',
  //   display: 'flex',
  //   alignItems: 'center',
  //   justifyContent: 'center',
  //   padding: 0,
  // };

  // Capsule container similar to TSR complementary item styling (unused)
  // const controlsBoxStyle = {
  //   width: 28,
  //   height: 28,
  //   borderRadius: 6,
  //   border: '1px solid #F3F4F6',
  //   backgroundColor: '#F3F4F6',
  //   fontWeight: 'bold',
  //   fontSize: 18,
  //   cursor: 'pointer',
  //   display: 'flex',
  //   alignItems: 'center',
  //   justifyContent: 'center',
  //   padding: 0,
  // };

  if (selectedRecipe) {
    return (
      <>
        {showRoundOffModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px'
          }}>
            <div style={{
              background: '#fff', borderRadius: '16px', padding: '28px',
              maxWidth: '380px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              textAlign: 'center',
              animation: 'fadeIn 0.2s ease'
            }}>
              <div style={{ fontSize: '44px', marginBottom: '16px' }}>⚠️</div>
              <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#111' }}>
                Round off to zero
              </h3>
              <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
                Costing cannot be applied.
              </p>
              <button
                onClick={() => setShowRoundOffModal(false)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}
        {showDuplicateModal && duplicateConflicts.length > 0 && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px'
          }}>
            <div style={{
              background: '#fff', borderRadius: '16px', padding: '24px',
              maxWidth: '520px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              animation: 'fadeIn 0.2s ease', display: 'flex', flexDirection: 'column', maxHeight: '85vh',
              overflow: 'visible'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                <div style={{ fontSize: '38px', marginBottom: '6px' }}>⚠️</div>
                <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 700, color: '#111' }}>
                  {duplicateConflicts.length} Possible Duplicate{duplicateConflicts.length === 1 ? '' : 's'} Detected
                </h3>
              </div>

              {/* Bulk actions */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center', marginBottom: '12px' }}>
                <button
                  onClick={() => setAllDuplicateDecisions('use_existing')}
                  style={{ background: 'transparent', border: 'none', color: '#2563eb', fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >Use existing for all</button>
                <span style={{ color: '#d1d5db' }}>|</span>
                <button
                  onClick={() => setAllDuplicateDecisions('force_create')}
                  style={{ background: 'transparent', border: 'none', color: '#2563eb', fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >Create separate for all</button>
              </div>

              {/* Per-duplicate rows */}
              <div style={{ flex: 1, overflow: 'visible', display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '2px' }}>
                {duplicateConflicts.map((c) => {
                  const d = duplicateDecisions[c.attempted_name] || { action: 'use_existing', reason: 'Intentional Variant' };
                  const isForce = d.action === 'force_create';
                  return (
                    <div key={c.attempted_name} style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                        <div style={{ fontSize: 17, lineHeight: 1.4 }}>
                          <span style={{ fontWeight: 700, color: '#111' }}>{c.attempted_name}</span>
                          <span style={{ color: '#9ca3af' }}> → </span>
                          <span style={{ fontWeight: 600, color: '#1e3a5f' }}>{c.existing_item_name}</span>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', color: (c.confidence >= 85) ? '#dc2626' : '#d97706' }}>
                          {c.confidence}%{c.layer ? ` (${c.layer})` : ''}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => setDuplicateDecision(c.attempted_name, { action: 'use_existing' })}
                          style={{
                            flex: 1, padding: '8px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            background: !isForce ? '#000' : '#fff', color: !isForce ? '#fff' : '#374151',
                            border: `1px solid ${!isForce ? '#000' : '#d1d5db'}`, transition: 'all 0.15s'
                          }}
                        >Use existing</button>
                        <button
                          onClick={() => setDuplicateDecision(c.attempted_name, { action: 'force_create' })}
                          style={{
                            flex: 1, padding: '8px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            background: isForce ? '#111' : '#fff', color: isForce ? '#fff' : '#374151',
                            border: `1px solid ${isForce ? '#111' : '#d1d5db'}`, transition: 'all 0.15s'
                          }}
                        >Create separate</button>
                      </div>
                      {isForce && (
                        <NiceSelect
                          value={d.reason || 'Intentional Variant'}
                          onChange={(val) => setDuplicateDecision(c.attempted_name, { reason: val })}
                          options={DUPLICATE_OVERRIDE_OPTIONS}
                          placeholder="Select reason"
                          fullWidth={true}
                          className="duplicate-override-select open-above-wrap"
                          style={{ marginTop: 8 }}
                          openAbove={true}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button
                  onClick={cancelDuplicateModal}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '10px',
                    background: '#e5e7eb', color: '#000', border: 'none',
                    fontSize: '14px', fontWeight: 700, cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={applyAllDuplicateDecisions}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '10px',
                    background: '#000', color: '#fff', border: 'none',
                    fontSize: '14px', fontWeight: 700, cursor: 'pointer'
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Complimentary Item Page - Use dedicated component */}
        {showComplimentaryItemPage && selectedComplimentaryItem && (
          <ComplimentaryItemsPage 
            item={selectedComplimentaryItem}
            onBack={handleBackFromComplimentary}
            onSave={saveComplimentaryIngredients}
          />
        )}

        {/* Original Recipe Editor - Hide when showing complimentary page */}
        {!showComplimentaryItemPage && (
        <div className="viewport-wrapper">
        <div className="itemmap-container editor">
          {/* Top header with back button and page title */}
          <div className="itemmap-header">
            <button
              type="button"
              className="back-arrow-btn"
              aria-label="Back to Item Mapping"
              onClick={handleRecipeBackButton}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h3 className="page-title">Item Mapping</h3>
          </div>

          <div className="recipe-titlebar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="recipe-title" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {formatSummaryTitle(selectedRecipe.name)}
            </div>
            <div ref={plateMenuRef} style={{ position: 'relative', display: 'inline-block', marginLeft: '8px', flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => {
                  const next = !showPlateMenu;
                  setShowPlateMenu(next);
                  if (!next) setShowCustomPlateInput(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  marginTop:'1rem',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#111',
                  cursor: 'pointer',
                  opacity: 1,
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
              >
                <span style={{ fontSize: '14px', lineHeight: 1 }}>☰</span>
                <span>{`${plateStep} ${plateStep === 1 ? 'plate' : 'plates'}`}</span>
              </button>
              {showPlateMenu && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  right: 0,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  zIndex: 9999,
                  overflow: 'hidden',
                  minWidth: '180px',
                  textAlign: 'left'
                }}>
                  {[1, 10, 100].map(size => (
                    <div
                      key={size}
                      onClick={() => applyPlateStepChange(size)}
                      style={{
                        padding: '12px 20px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: plateStep === size ? '700' : '400',
                        background: '#ffffff',
                        color: '#111',
                        borderBottom: size !== 100 ? '1px solid #f3f4f6' : 'none',
                        transition: 'background 0.15s',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#ffffff'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                    >
                      <span>{size} {size === 1 ? 'plate' : 'plates'}</span>
                      {plateStep === size && (
                        <FontAwesomeIcon icon={faCheck} style={{ marginLeft: '6px', color: '#16a34a' }} />
                      )}
                    </div>
                  ))}
                  <div
                    onClick={() => {
                      setShowCustomPlateInput(true);
                      if (isCustomPlateStep) {
                        setCustomPlateStep(String(plateStep));
                      }
                    }}
                    style={{
                      padding: showCustomPlateInput ? '10px 20px 4px 20px' : '12px 20px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: isCustomPlateStep ? '700' : '400',
                      background: '#ffffff',
                      color: '#111',
                      borderTop: '1px solid #f3f4f6',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#ffffff'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                  >
                    <span>{isCustomPlateStep ? `${plateStep} plates (custom)` : 'Custom plates'}</span>
                    {isCustomPlateStep && (
                      <FontAwesomeIcon icon={faCheck} style={{ marginLeft: '6px', color: '#16a34a' }} />
                    )}
                  </div>
                  {showCustomPlateInput && (
                    <div style={{ padding: '4px 16px 12px 16px', borderTop: 'none', background: '#ffffff' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="number"
                          min="0.01"
                          step="any"
                          inputMode="decimal"
                          value={customPlateStep}
                          onChange={(e) => setCustomPlateStep(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') applyCustomPlateStep();
                          }}
                          placeholder="Enter plates (e.g. 0.5)"
                          style={{
                            flex: 1,
                            height: '32px',
                            padding: '0 10px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '13px',
                            fontWeight: '600'
                          }}
                        />
                        <button
                          type="button"
                          onClick={applyCustomPlateStep}
                          style={{
                            height: '32px',
                            padding: '0 12px',
                            borderRadius: '6px',
                            border: '1px solid #111',
                            background: '#111',
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer'
                          }}
                        >
                          Set
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="recipe-hero-shell">
            <img
              src={selectedRecipe.image || selectedRecipe.fallbackImage || 'https://storage.googleapis.com/menuimages001/itemmap/default.png'}
              alt="Dish"
              className="recipe-hero"
            />
            {/* Custom Image Upload Button */}
            <CustomImageUploadModal
              isOpen={true}
              recipeId={selectedRecipe.id || selectedRecipe.menu_item_id}
              recipeName={selectedRecipe.name}
              currentImageUrl={selectedRecipe.image}
              imageSource={selectedRecipe.imageSource}
              showCameraButton={true}
              onUploadSuccess={({ imageUrl, imageSource }) => {
                setSelectedRecipe(prev => ({
                  ...prev,
                  image: imageUrl,
                  imageSource: imageSource
                }));
                // Also update in recipes list
                setRecipes(prev => prev.map(r => 
                  (r.id || r.menu_item_id) === (selectedRecipe.id || selectedRecipe.menu_item_id)
                    ? { ...r, image: imageUrl, imageSource: imageSource }
                    : r
                ));
              }}
              onRevertToAI={() => {
                setSelectedRecipe(prev => ({
                  ...prev,
                  image: prev.fallbackImage || 'https://storage.googleapis.com/menuimages001/itemmap/default.png',
                  imageSource: 'ai_generated'
                }));
              }}
            />
          </div>

          <div className="panel" style={{ marginTop: 16 }}>
            <div className="itemmap-ingredients-topbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '8px', flexWrap: 'wrap' }}>
              {/* Ingredients heading on its own line */}
              <h4 className="panel-title itemmap-ingredients-heading" style={{ textAlign: 'left', fontSize: '1.4rem', margin: 0 }}>Ingredients</h4>

              {/* Action buttons (Edit/Undo/Redo/Delete/Add) - on the same row, right side */}
              <div className="itemmap-ingredients-action-buttons" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                {/* Sync from Recipe Library — icon button styled to match the pencil/undo/redo siblings */}
                {isOnboarding && (
                  <button
                    onClick={openLibraryMatchModal}
                    style={{
                      background: '#eff6ff',
                      color: '#2563eb',
                      border: '1px solid #bfdbfe',
                      borderRadius: '6px',
                      width: '34px',
                      height: '34px',
                      padding: 0,
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '14px',
                      flexShrink: 0,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
                    title="Match & import from Recipe Library"
                  >
                    <FontAwesomeIcon icon={faSync} style={{ fontSize: 13 }} />
                  </button>
                )}
                {/* Inventory item edit menu (pencil): Change item name / Merge / Change units */}
                <div className="desktop-only-edit-menu">
                  <InventoryItemEditMenu
                    businessId={UnitMappingService.getBusinessId()}
                    preferredNames={ingredientList
                      .map((ing) => ing?.name)
                      .filter((n) => n && n !== 'New Ingredient')}
                    onChanged={handleInventoryItemChanged}
                    onNotify={(message, type) => showToast(message, type)}
                    buttonTitle="Edit inventory items (rename / merge / change units)"
                    externalTrigger={externalEditItemTrigger}
                    onTriggerClear={() => setExternalEditItemTrigger(null)}
                    buttonStyle={{
                      background: '#ffffff',
                      color: '#000000',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginTop: 0,
                      height: 'auto',
                      width: 'auto',
                      boxShadow: 'none',
                      transition: 'all 0.2s',
                    }}
                  />
                </div>
                <button
                  onClick={handleUndo}
                  disabled={historyIndex < 0}
                  style={{
                    background: historyIndex < 0 ? '#f3f4f6' : '#ffffff',
                    color: historyIndex < 0 ? '#9ca3af' : '#000000',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: historyIndex < 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                  title="Undo"
                >
                  <FontAwesomeIcon icon={faUndo} style={{ fontSize: 12 }} />
                </button>
                <button
                  onClick={handleRedo}
                  disabled={historyIndex >= ingredientHistory.length - 1}
                  style={{
                    background: historyIndex >= ingredientHistory.length - 1 ? '#f3f4f6' : '#ffffff',
                    color: historyIndex >= ingredientHistory.length - 1 ? '#9ca3af' : '#000000',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: historyIndex >= ingredientHistory.length - 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                  title="Redo"
                >
                  <FontAwesomeIcon icon={faRedo} style={{ fontSize: 12 }} />
                </button>
                <button
                  onClick={() => setShowDeleteAllModal(true)}
                  disabled={ingredientList.length === 0}
                  style={{
                    background: ingredientList.length === 0 ? '#f3f4f6' : '#ffffff',
                    color: ingredientList.length === 0 ? '#9ca3af' : '#dc2626',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: ingredientList.length === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                  title="Delete All Ingredients"
                >
                  <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
                </button>
                <button
                  onClick={() => {
                    debugLog('🆕 Create Preparation button clicked');
                    setShowPreparationCreator(true);
                  }}
                  style={{
                    background: '#ffffff',
                    color: '#16a34a',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                  title="Create Preparation"
                >
                  <FontAwesomeIcon icon={faPlus} style={{ fontSize: 12 }} />
                </button>
              </div>
            </div>
            
            {/* Button row: Complimentary Items */}
            <div className="itemmap-ingredients-toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
              {/* Complimentary Items Button - Hidden for Sides category */}
              {(() => {
                const isSides = selectedRecipe?.category?.toLowerCase().includes('sides') || 
                               selectedRecipe?.category?.toLowerCase().includes('complimentary');
                return !isSides;
              })() && (
                <div className="itemmap-ingredients-dropdown-wrap" ref={complimentaryDropdownRef} style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowComplimentaryDropdown(!showComplimentaryDropdown)}
                    className="itemmap-dropdown-btn"
                    style={{
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                    title="Complimentary Items"
                  >
                    Complimentary Items
                    <FaChevronDown style={{ 
                      fontSize: '10px',
                      transform: showComplimentaryDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }} />
                  </button>
                
                  {showComplimentaryDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '0',
                      marginTop: '8px',
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      minWidth: '200px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      zIndex: 9999
                    }}>
                      {(() => {
                        debugLog('🔍 Rendering complimentary dropdown');
                        debugLog('  - complimentaryRecipesLibrary:', complimentaryRecipesLibrary);
                        debugLog('  - complimentaryRecipesLibrary type:', typeof complimentaryRecipesLibrary);
                        debugLog('  - complimentaryRecipesLibrary is Array:', Array.isArray(complimentaryRecipesLibrary));
                        debugLog('  - complimentaryRecipesLibrary length:', complimentaryRecipesLibrary?.length);
                        debugLog('  - linkedComplimentaryItems:', linkedComplimentaryItems);
                        
                        // Show ALL valid complimentary items (linked + unlinked)
                        const filtered = complimentaryRecipesLibrary.filter(item => {
                          const itemName = item?.name;
                          const isValid = itemName && itemName.trim();
                          return isValid;
                        });
                        
                        debugLog('  - Filtered (all valid) count:', filtered.length);
                        return null;
                      })()}
                      {complimentaryRecipesLibrary.filter(item => {
                        const itemName = item?.name;
                        const isValid = itemName && itemName.trim();
                        return isValid; // Show ALL valid items (linked + unlinked)
                      }).map((item, index, filteredArray) => {
                        // All items from library are objects with name and comp_recipe_id
                        const itemName = item.name;
                        return (
                          <div
                            key={index}
                            style={{
                              padding: '12px 16px',
                              borderBottom: index < filteredArray.length - 1 ? '1px solid #e5e7eb' : 'none',
                              cursor: 'pointer',
                              transition: 'background 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              textAlign: 'left'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <span 
                              className="complimentary-item-name"
                              onClick={() => handleOpenComplimentaryEditor(item)}
                              style={{ 
                                flex: 1, 
                                cursor: 'pointer', 
                                textAlign: 'left',
                                color: '#000000',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                            >
                              {itemName}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
                              <div 
                                className="checkbox-wrapper"
                                onClick={(e) => handleComplimentaryItemClick(item, e)}
                                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={linkedComplimentaryItems.some(linked => linked.comp_recipe_id === item.comp_recipe_id)}
                                  onChange={(e) => e.preventDefault()}
                                  onClick={(e) => e.preventDefault()}
                                  readOnly
                                  style={{
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer',
                                    accentColor: '#000000',
                                    pointerEvents: 'none'
                                  }}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={(e) => requestDeleteComplimentaryItem(item, e)}
                                aria-label={`Delete ${itemName}`}
                                title={`Delete ${itemName}`}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  border: 'none',
                                  borderRadius: 0,
                                  background: 'transparent',
                                  color: '#dc2626',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  padding: 0,
                                  boxShadow: 'none',
                                  transition: 'color 0.2s ease, opacity 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = '#b91c1c';
                                  e.currentTarget.style.opacity = '0.85';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = '#dc2626';
                                  e.currentTarget.style.opacity = '1';
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      <div
                        onClick={handleAddNewComplimentaryItem}
                        style={{
                          padding: '12px 16px',
                          background: '#f9fafb',
                          color: '#000000',
                          fontWeight: '600',
                          textAlign: 'center',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#f9fafb'}
                      >
                        + Add New Item
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Mobile-only Edit Menu */}
              <div className="mobile-only-edit-menu">
                <InventoryItemEditMenu
                  businessId={UnitMappingService.getBusinessId()}
                  preferredNames={ingredientList
                    .map((ing) => ing?.name)
                    .filter((n) => n && n !== 'New Ingredient')}
                  onChanged={handleInventoryItemChanged}
                  onNotify={(message, type) => showToast(message, type)}
                  buttonTitle="Edit inventory items (rename / merge / change units)"
                  externalTrigger={externalEditItemTrigger}
                  onTriggerClear={() => setExternalEditItemTrigger(null)}
                  buttonStyle={{
                    background: '#ffffff',
                    color: '#000000',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: 0,
                    height: 'auto',
                    width: 'auto',
                    boxShadow: 'none',
                    transition: 'all 0.2s',
                  }}
                />
              </div>
            </div>

            {/* Container for ingredient rows */}
            <div style={{
              overflow: 'visible',
              marginBottom: '12px',
              position: 'relative',
              zIndex: 1
            }}>
            {ingredientList.map((item, idx) => (
              <div
                key={idx}
                className={`ingredient-row ${editingIngredientIndex === idx ? 'editing' : ''} ${openUnitIndex === idx ? 'unit-dropdown-open' : ''} ${ (typeof window !== 'undefined' && window.innerWidth <= 640 && editingIngredientIndex !== idx)
                  ? (((item.name || '').trim().length <= 18) ? 'row-inline' : 'row-stacked')
                  : ''} ${Math.abs(swipeOffsets[idx] || 0) > 10 ? 'swipe-active' : ''}`}
                style={{
                  transform: `translateX(${swipeOffsets[idx] || 0}px)`,
                  transition: swipingIndex === idx ? 'none' : 'transform 0.12s ease'
                }}
                onTouchStart={(e) => handleRowTouchStart(idx, e)}
                onTouchMove={(e) => handleRowTouchMove(idx, e)}
                onTouchEnd={() => handleRowTouchEnd(idx)}
              >
                {editingIngredientIndex === idx ? (
                  <div
                    ref={editingIngredientIndex === idx ? ingredientEditorRef : null}
                    className="itemmap-ingredient-editing-wrapper"
                    style={{ position: 'relative', width: '100%' }}
                  >
                    
                    {/* Main content below */}
                    <div className="itemmap-ingredient-editing-main" style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
                      {/* Name on the left */}
                      <div className="itemmap-ingredient-name-input" style={{ flex: 1, minWidth: 0, position: 'relative', textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            ref={editNameInputRef}
                            type="text"
                            value={formData.name}
                            onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setShowNameSuggestions(true); }}
                            onFocus={() => setShowNameSuggestions(true)}
                            onBlur={() => {
                              // Live-sync typed name into ingredientList so auto-save picks it up
                              const typed = formData.name?.trim();
                              if (typed && typed !== 'New Ingredient' && editingIngredientIndex !== null) {
                                setIngredientList(prev => {
                                  const updated = [...prev];
                                  const row = updated[editingIngredientIndex];
                                  if (row && row.name !== typed) {
                                    updated[editingIngredientIndex] = { ...row, name: typed };
                                  }
                                  return updated;
                                });
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const typed = formData.name?.trim();
                                if (typed && typed !== 'New Ingredient' && editingIngredientIndex !== null) {
                                  setIngredientList(prev => {
                                    const updated = [...prev];
                                    const row = updated[editingIngredientIndex];
                                    if (row && row.name !== typed) {
                                      updated[editingIngredientIndex] = { ...row, name: typed };
                                    }
                                    return updated;
                                  });
                                }
                                setShowNameSuggestions(false);
                                if (quantityInputRef.current) {
                                  quantityInputRef.current.focus();
                                  quantityInputRef.current.select();
                                }
                              }
                            }}
                            placeholder="Search Ingredient Name"
                            className="text-input"
                            style={{ fontWeight: 500, width: '100%', flex: 1, minWidth: 0, textAlign: 'left', fontSize: '1rem', lineHeight: '1.2', padding: 0, border: 'none', background: 'transparent' }}
                          />
                        </div>
                      {showNameSuggestions && (
                          (() => {
                            const isLastRow = idx === ingredientList.length - 1;
                            return (
                        <div className="suggestions" style={{ 
                          position: 'absolute',
                            top: isLastRow ? 'auto' : '100%',
                            bottom: isLastRow ? '100%' : 'auto',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          maxHeight: 400,
                          overflowY: 'auto',
                          zIndex: 3000,
                          marginTop: 4,
                          minWidth: '280px',
                          width: '100%'
                        }}>
                          {/* Ingredient Names Section */}
                          {nameSuggestions?.length > 0 && (
                            <>
                              <div style={{
                                padding: '8px 12px',
                                background: '#f9fafb',
                                borderBottom: '1px solid #e5e7eb',
                                fontWeight: 600,
                                fontSize: 12,
                                color: '#666',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <span>Ingredient Names</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeIngredientEditor();
                                  }}
                                  title="Close suggestions"
                                  style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: 999,
                                    border: '1px solid #d1d5db',
                                    background: '#fff',
                                    color: '#6b7280',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0
                                  }}
                                >
                                  <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                                </button>
                              </div>
                              {nameSuggestions.map(s => (
                                <div 
                                  key={`ing-${s.id || s.name}`} 
                                  onClick={() => pickNameSuggestion(s)} 
                                  className="suggestion-item"
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f3f4f6',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'background 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                  <span style={{ fontWeight: 500 }}>{s.name}</span>
                                  {s.standardUnit && (
                                    <span className="suggestion-unit" style={{
                                      background: '#f3f4f6',
                                      padding: '2px 8px',
                                      borderRadius: 4,
                                      fontSize: 12,
                                      color: '#666'
                                    }}>{toBaseUnitSymbol(s.standardUnit)}</span>
                                  )}
                                </div>
                              ))}
                            </>
                          )}
                          
                          {/* Saved Preparations Section */}
                          {preparationSuggestions?.length > 0 && (
                            <>
                              <div style={{
                                padding: '8px 12px',
                                background: '#f9fafb',
                                borderBottom: '1px solid #e5e7eb',
                                fontWeight: 600,
                                fontSize: 12,
                                color: '#666',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginTop: nameSuggestions?.length > 0 ? 4 : 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <span>Saved Preparations</span>
                                {nameSuggestions?.length === 0 && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeIngredientEditor();
                                    }}
                                    title="Close suggestions"
                                    style={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: 999,
                                      border: '1px solid #d1d5db',
                                      background: '#fff',
                                      color: '#6b7280',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      padding: 0
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                                  </button>
                                )}
                              </div>
                              {preparationSuggestions.map(s => (
                                <div 
                                  key={`prep-${s.id || s.name}`} 
                                  onClick={() => pickNameSuggestion(s)} 
                                  className="suggestion-item"
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f3f4f6',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'background 0.2s',
                                    background: 'white'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                  <span style={{ fontWeight: 500 }}>{s.name}</span>
                                  {s.standardUnit && (
                                    <span className="suggestion-unit" style={{
                                      background: '#f3f4f6',
                                      padding: '2px 8px',
                                      borderRadius: 4,
                                      fontSize: 12,
                                      color: '#666'
                                    }}>{toBaseUnitSymbol(s.standardUnit)}</span>
                                  )}
                                </div>
                              ))}
                            </>
                          )}
                          
                          {/* Menu Items Section */}
                          {menuSuggestions?.length > 0 && (
                            <>
                              <div style={{
                                padding: '8px 12px',
                                background: '#f9fafb',
                                borderBottom: '1px solid #e5e7eb',
                                fontWeight: 600,
                                fontSize: 12,
                                color: '#666',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginTop: (nameSuggestions?.length > 0 || preparationSuggestions?.length > 0) ? 4 : 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <span>Menu Items (Import All Ingredients)</span>
                                {nameSuggestions?.length === 0 && preparationSuggestions?.length === 0 && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeIngredientEditor();
                                    }}
                                    title="Close suggestions"
                                    style={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: 999,
                                      border: '1px solid #d1d5db',
                                      background: '#fff',
                                      color: '#6b7280',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      padding: 0
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                                  </button>
                                )}
                              </div>
                              {menuSuggestions.map(m => (
                                <div 
                                  key={`menu-${m.id || m.name}`} 
                                  onClick={() => pickMenuItemSuggestion(m)} 
                                  className="suggestion-item"
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f3f4f6',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'background 0.2s',
                                    background: 'white'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                  <span style={{ fontWeight: 500 }}>{m.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}</span>
                                  <span style={{
                                    background: '#f3f4f6',
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    fontSize: 11,
                                    color: '#666',
                                    fontWeight: 600
                                  }}>IMPORT</span>
                                </div>
                              ))}
                            </>
                          )}

                          {/* Recipe Library Section */}
                          {librarySuggestions?.length > 0 && (
                            <>
                              <div style={{
                                padding: '8px 12px',
                                background: '#f9fafb',
                                borderBottom: '1px solid #e5e7eb',
                                fontWeight: 600,
                                fontSize: 12,
                                color: '#666',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginTop: (nameSuggestions?.length > 0 || preparationSuggestions?.length > 0 || menuSuggestions?.length > 0) ? 4 : 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <span>Recipe Library</span>
                                {nameSuggestions?.length === 0 && preparationSuggestions?.length === 0 && menuSuggestions?.length === 0 && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeIngredientEditor();
                                    }}
                                    title="Close suggestions"
                                    style={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: 999,
                                      border: '1px solid #d1d5db',
                                      background: '#fff',
                                      color: '#6b7280',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      padding: 0
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                                  </button>
                                )}
                              </div>
                              {librarySuggestions.map(r => (
                                <div 
                                  key={`lib-${r.recipe_library_id || r.item_name}`} 
                                  onClick={() => pickLibraryRecipeSuggestion(r)} 
                                  className="suggestion-item"
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f3f4f6',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'background 0.2s',
                                    background: 'white'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                  <span style={{ fontWeight: 500 }}>{(r.item_name || r.name || '').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}</span>
                                  <span style={{
                                    background: '#f3f4f6',
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    fontSize: 11,
                                    color: '#666',
                                    fontWeight: 600
                                  }}>IMPORT</span>
                                </div>
                              ))}
                            </>
                          )}
                          
                          {/* No Results Message */}
                          {nameSuggestions?.length === 0 && preparationSuggestions?.length === 0 && menuSuggestions?.length === 0 && librarySuggestions?.length === 0 && (
                            <div style={{ 
                              padding: '16px', 
                              textAlign: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              {formData.name?.trim() ? (
                                <>
                                  <span style={{ color: '#666', fontSize: 14 }}>
                                    Click Add to save "{formData.name}"
                                  </span>
                                  <button
                                    onClick={() => {
                                      setShowNameSuggestions(false);
                                      if (quantityInputRef.current) {
                                        quantityInputRef.current.focus();
                                        quantityInputRef.current.select();
                                      }
                                    }}
                                    style={{
                                      background: '#000',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: '8px',
                                      padding: '10px 24px',
                                      fontSize: '14px',
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      transition: 'background 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#333'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = '#000'}
                                  >
                                    Add
                                  </button>
                                </>
                              ) : (
                                <span style={{ color: '#999', fontSize: 14 }}>
                                  Start typing to search ingredients or menu items
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                          );
                        })()
                      )}
                    </div>
                    {/* Controls on the right */}
                    <div className="itemmap-ingredient-quantity-row" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <div className="controls-inline">
                        <button
                          className="square-btn"
                          onClick={() => {
                            const newQty = normalizeQty((formData.quantity || 0) - 1);
                            setFormData(prev => ({ ...prev, quantity: newQty }));
                            // Live-sync qty into ingredientList
                            if (editingIngredientIndex !== null) {
                              setIngredientList(prev => {
                                const updated = [...prev];
                                const row = updated[editingIngredientIndex];
                                if (row) {
                                  const withQty = setQtyForPlateStep(row, newQty);
                                  updated[editingIngredientIndex] = { ...row, qty10: withQty.qty10, qty100: withQty.qty100 };
                                }
                                return updated;
                              });
                            }
                          }}
                        >
                          −
                        </button>
                        <input
                          ref={quantityInputRef}
                          type="number"
                          step="any"
                          className="number-input"
                          value={formData.quantity}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData({ ...formData, quantity: val });
                            // Live-sync qty into ingredientList so it is never lost if the
                            // editor unmounts (e.g. navigating to the complimentary page)
                            // before onBlur fires.
                            if (editingIngredientIndex !== null) {
                              setIngredientList(prev => {
                                const updated = [...prev];
                                const row = updated[editingIngredientIndex];
                                if (row) {
                                  const withQty = setQtyForPlateStep(row, Number(val) || 0);
                                  updated[editingIngredientIndex] = { ...row, qty10: withQty.qty10, qty100: withQty.qty100 };
                                }
                                return updated;
                              });
                            }
                          }}
                          onFocus={(e) => {
                            if (Number(formData.quantity) === 0) {
                              setFormData(prev => ({ ...prev, quantity: '' }));
                            } else {
                              e.target.select();
                            }
                          }}
                          onBlur={() => {
                            // SP9 sacred numbers: blur commits exactly what was typed.
                            const typedQty = normalizeQty(formData.quantity);
                            setFormData(prev => ({ ...prev, quantity: typedQty }));
                            // Live-sync qty into ingredientList
                            if (editingIngredientIndex !== null) {
                              setIngredientList(prev => {
                                const updated = [...prev];
                                const row = updated[editingIngredientIndex];
                                if (row) {
                                  const withQty = setQtyForPlateStep(row, typedQty);
                                  updated[editingIngredientIndex] = { ...row, qty10: withQty.qty10, qty100: withQty.qty100 };
                                }
                                return updated;
                              });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (isCurrentEditingIngredientNew()) {
                                const typedQty = normalizeQty(formData.quantity);
                                setFormData(prev => ({ ...prev, quantity: typedQty }));
                                if (editingIngredientIndex !== null) {
                                  setIngredientList(prev => {
                                    const updated = [...prev];
                                    const row = updated[editingIngredientIndex];
                                    if (row) {
                                      const withQty = setQtyForPlateStep(row, typedQty);
                                      updated[editingIngredientIndex] = { ...row, qty10: withQty.qty10, qty100: withQty.qty100 };
                                    }
                                    return updated;
                                  });
                                }
                                setPendingAutoAddAfterUnitSelect(true);
                                setTimeout(() => {
                                  if (unitDropdownRef.current?.focus) unitDropdownRef.current.focus();
                                  if (unitDropdownRef.current?.open) unitDropdownRef.current.open();
                                }, 0);
                              } else {
                                saveIngredient({ silent: true });
                                setTimeout(() => {
                                  triggerAddIngredientFromKeyboard();
                                }, 0);
                              }
                            }
                          }}
                          onWheel={(e) => e.target.blur()}
                        />
                        <button
                          className="square-btn"
                          onClick={() => {
                            const newQty = normalizeQty((formData.quantity || 0) + 1);
                            setFormData(prev => ({ ...prev, quantity: newQty }));
                            // Live-sync qty into ingredientList
                            if (editingIngredientIndex !== null) {
                              setIngredientList(prev => {
                                const updated = [...prev];
                                const row = updated[editingIngredientIndex];
                                if (row) {
                                  const withQty = setQtyForPlateStep(row, newQty);
                                  updated[editingIngredientIndex] = { ...row, qty10: withQty.qty10, qty100: withQty.qty100 };
                                }
                                return updated;
                              });
                            }
                          }}
                        >
                          ＋
                        </button>
                        <SmartUnitDropdown
                          ref={unitDropdownRef}
                          isNewItem={isCurrentEditingIngredientNew()}
                          currentUnit={formData.unit}
                          onUnitChange={(unitId, unitSymbol) => {
                            const sameQty = formData.quantity;
                            setFormData({ ...formData, unit: unitSymbol, quantity: sameQty });
                            // Live-sync unit while preserving entered qty in ingredientList
                            if (editingIngredientIndex !== null) {
                              setIngredientList(prev => {
                                const updated = [...prev];
                                const row = updated[editingIngredientIndex];
                                if (row) {
                                  const withQty = setQtyForPlateStep(row, Number(sameQty || 0));
                                  updated[editingIngredientIndex] = { ...row, unit: unitSymbol, qty10: withQty.qty10, qty100: withQty.qty100 };
                                }
                                return updated;
                              });
                            }
                            if (pendingAutoAddAfterUnitSelect) {
                              setPendingAutoAddAfterUnitSelect(false);
                              setTimeout(() => {
                                saveIngredient({ silent: true });
                                setTimeout(() => {
                                  triggerAddIngredientFromKeyboard();
                                }, 0);
                              }, 0);
                            }
                          }}
                          onCrossFamilyConversion={({ currentUnit, newUnit, newUnitId }) => {
                            // IMMEDIATE SYNCHRONOUS HANDLING - No async operations
                            debugLog('═══════════════════════════════════════');
                            debugLog('🔄 CROSS-FAMILY CONVERSION STARTED');
                            debugLog('═══════════════════════════════════════');
                            debugLog('Ingredient:', formData.name);
                            debugLog('Current Unit:', currentUnit, '→ New Unit:', newUnit);
                            
                            // For itemmap, recipe ingredients should NOT trigger cross-family modal
                            // because the ingredient may not exist in inventory yet
                            // Instead, just change the unit directly and let the user save the recipe
                            
                            debugLog('✅ Recipe ingredient unit change (no inventory conversion needed)');
                            
                            // Update formData immediately
                            setFormData({ ...formData, unit: newUnit });
                            
                            // Update ingredient list immediately
                            if (editingIngredientIndex !== null) {
                              setIngredientList(prev => {
                                const updated = [...prev];
                                const row = updated[editingIngredientIndex];
                                if (row) {
                                  const withQty = setQtyForPlateStep(row, Number(formData.quantity || 0));
                                  updated[editingIngredientIndex] = { ...row, unit: newUnit, qty10: withQty.qty10, qty100: withQty.qty100 };
                                }
                                return updated;
                              });
                            }
                            
                            showToast(`✅ Unit changed to ${newUnit}`, 'success');
                            debugLog('═══════════════════════════════════════');
                            if (pendingAutoAddAfterUnitSelect) {
                              setPendingAutoAddAfterUnitSelect(false);
                              setTimeout(() => {
                                saveIngredient({ silent: true });
                                setTimeout(() => {
                                  triggerAddIngredientFromKeyboard();
                                }, 0);
                              }, 0);
                            }
                          }}
                          onOtherUnitsClick={() => {
                            const enteredName = formData.name || '';
                            if (enteredName && enteredName.trim() && enteredName.trim() !== 'New Ingredient') {
                              setExternalEditItemTrigger({
                                name: enteredName.trim(),
                                mode: 'unit'
                              });
                            } else {
                              showToast('Please enter an ingredient name first', 'warning');
                            }
                          }}
                          disabled={false}
                          style={{ width: 68 }}
                        />
                        <button
                          className="ingredient-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteIngredient(idx);
                          }}
                          title="Delete"
                          type="button"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      {/* SP9: conversion is asked for, never done to you — equivalence hint
                          (tap to apply) + inline stock-family note replace the old popup. */}
                      {(() => {
                        const hint = equivalentIn(formData.quantity, formData.unit);
                        const mismatch = rowFamilyMismatch(formData.name, formData.unit);
                        if (!hint && !mismatch) return null;
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 4, fontSize: '0.74rem' }}>
                            {hint && (
                              <span style={{ color: '#6B7280' }}>
                                {formData.quantity} {formData.unit} = {hint.value} {hint.unit}
                                <button
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, quantity: hint.value, unit: hint.unit }))}
                                  style={{ marginLeft: 6, border: 'none', background: 'none', color: '#2563EB', cursor: 'pointer', padding: 0, fontSize: 'inherit', textDecoration: 'underline' }}
                                >
                                  apply
                                </button>
                              </span>
                            )}
                            {mismatch && (
                              <span style={{ color: '#92400E' }}>
                                ⚠ stocked by {mismatch.stockFamily}
                                <button
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, unit: mismatch.fixUnit }))}
                                  style={{ marginLeft: 6, border: 'none', background: 'none', color: '#92400E', cursor: 'pointer', padding: 0, fontSize: 'inherit', textDecoration: 'underline' }}
                                >
                                  use {mismatch.fixUnit}
                                </button>
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    {/* end main content */}
                    </div>
                    {/* end editing wrapper */}
                  </div>
                ) : (
                  <>
                    {(() => {
                      // The ingredient's own name stays the row's identity: it is what the recipe
                      // points at, what the quantity belongs to, and what clicking here edits.
                      // After a conversion the preparation that actually gets deducted has a
                      // different name, and a dot cannot say which one — so it is named on a
                      // second line rather than substituted for the first.
                      const prep = savedPreparationByName.get(normalizeSummaryKey(item.name));
                      const prepName = String(prep?.composite_name || '').trim();
                      const converted = Boolean(prepName)
                        && normalizeSummaryKey(prepName) !== normalizeSummaryKey(item.name);
                      const dotLabel = converted
                        ? `Made from the saved preparation "${prepName}" — a sale deducts its ingredients, not this item`
                        : 'Saved preparation';
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                            <div
                              style={{ fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, cursor: 'pointer', color: '#000000' }}
                              className="ingredient-name clickable"
                              onClick={() => startEditingIngredient(idx)}
                            >{item.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}</div>
                            {/* Yellow dot: this ingredient is a saved preparation (always shown, incl. onboarding) */}
                            {prep && (
                              <span
                                className="itemmap-prep-dot"
                                title={dotLabel}
                                aria-label={dotLabel}
                                style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#FBBF24', flexShrink: 0 }}
                              />
                            )}
                          </div>
                          {/* Only when the names differ. Where they match, the line would just
                              repeat the name above it and the dot already says what it is. */}
                          {converted && (
                            <div
                              style={{ fontSize: '0.72rem', color: '#92400E', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
                              title={dotLabel}
                            >
                              made from "{prepName}"
                            </div>
                          )}
                          {/* This portion was settled by a leakage calibration (§10.4).
                              Raised twice inside six months it stops being a correction
                              and starts being a habit, which the line says out loud. */}
                          {(() => {
                            const calib = calibratedPortions.get(normalizeSummaryKey(item.name));
                            if (!calib || calib.decision !== 'ACCEPTED') return null;
                            const when = new Date(calib.created_at);
                            const on = Number.isNaN(when.getTime())
                              ? ''
                              : when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                            return (
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  goTo && goTo('leakage-monthly-history', {
                                    itemId: calib.item_id,
                                    ingredientName: calib.ingredient_name,
                                    from: 'itemmap',
                                  });
                                }}
                                onKeyDown={(e) => {
                                  if (e.key !== 'Enter' && e.key !== ' ') return;
                                  e.stopPropagation();
                                  goTo && goTo('leakage-monthly-history', {
                                    itemId: calib.item_id,
                                    ingredientName: calib.ingredient_name,
                                    from: 'itemmap',
                                  });
                                }}
                                title={`Changed from ${calib.old_quantity} to ${calib.new_quantity}${calib.new_unit_symbol ? ` ${calib.new_unit_symbol}` : ''} after a leakage review. Open the history.`}
                                style={{
                                  fontSize: '0.72rem',
                                  color: calib.ratchet_flag ? '#991B1B' : '#57534E',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  textUnderlineOffset: '2px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  minWidth: 0,
                                }}
                              >
                                {calib.ratchet_flag
                                  ? `calibrated ${on} · raised ${calib.accepted_in_window}× in 6 months`
                                  : `calibrated ${on}`}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}
                    {item.name !== 'New Ingredient' && (
                      <div className="controls-inline">
                        <button 
                          onClick={() => {
                            setLocalInputValues(prev => {
                              const updated = { ...prev };
                              delete updated[idx];
                              return updated;
                            });
                            updateQuantity(idx, (getDisplayQty(item) || 0) - 1, true);
                          }} 
                          className="square-btn"
                        >
                          −
                        </button>
                        <input 
                          type="number" 
                          step="any"
                          className="number-input" 
                          value={localInputValues[idx] !== undefined ? localInputValues[idx] : (getDisplayQty(item) || '')} 
                          placeholder="0" 
                          onChange={(e) => {
                            const val = e.target.value;
                            setLocalInputValues(prev => ({ ...prev, [idx]: val }));
                            updateQuantity(idx, val);
                          }} 
                          onFocus={(e) => {
                            const val = e.target.value;
                            if (Number(val) === 0) {
                              setLocalInputValues(prev => ({ ...prev, [idx]: '' }));
                            } else {
                              e.target.select();
                            }
                          }} 
                          onBlur={(e) => {
                            const val = e.target.value;
                            setLocalInputValues(prev => {
                              const updated = { ...prev };
                              delete updated[idx];
                              return updated;
                            });
                            updateQuantity(idx, val, true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = e.target.value;
                              setLocalInputValues(prev => {
                                const updated = { ...prev };
                                delete updated[idx];
                                return updated;
                              });
                              updateQuantity(idx, val, true);
                              setTimeout(() => {
                                triggerAddIngredientFromKeyboard();
                              }, 0);
                            }
                          }}
                          onWheel={(e) => e.target.blur()}
                        />
                        <button 
                          onClick={() => {
                            setLocalInputValues(prev => {
                              const updated = { ...prev };
                              delete updated[idx];
                              return updated;
                            });
                            updateQuantity(idx, (getDisplayQty(item) || 0) + 1, true);
                          }} 
                          className="square-btn"
                        >
                          ＋
                        </button>
                        <SearchableSelect
                          key={`unit-display-${idx}`}
                          value={item.unit || 'g'}
                          onChange={(newUnit) => {
                            // Handle "Other Units" toggle — open the pencil unit change modal directly
                            if (newUnit === '__show_other__') {
                              const enteredName = item.name || '';
                              if (enteredName && enteredName.trim() && enteredName.trim() !== 'New Ingredient') {
                                setExternalEditItemTrigger({
                                  name: enteredName.trim(),
                                  mode: 'unit'
                                });
                              } else {
                                showToast('Please enter an ingredient name first', 'warning');
                              }
                              return;
                            }
                            handleUnitChangeWithConfirmation(item.name, item.unit || 'g', newUnit, (finalUnit) => {
                              const prevList = cloneIngredientList(ingredientList);
                              const updated = [...ingredientList];
                              const curQty = Number(getDisplayQty(updated[idx]) || 0);
                              const withNewQty = setQtyForPlateStep(updated[idx], curQty);
                              updated[idx].qty10 = withNewQty.qty10;
                              updated[idx].qty100 = withNewQty.qty100;
                              updated[idx].unit = finalUnit;
                              setIngredientList(updated);
                              pushIngredientHistory('update', prevList, cloneIngredientList(updated));
                              // Clear the "show other" state after selection
                              setShowOtherUnitsFor(prev => {
                                const next = new Set(prev);
                                next.delete(`display-${idx}`);
                                return next;
                              });
                            });
                          }}
                          onAddClick={() => { setKitchenUnitForm(prev => ({ ...prev, item: item.name || '' })); setShowKitchenUnitModal(true); }}
                          openUpward={idx === ingredientList.length - 1}
                          isOpen={openUnitIndex === idx && editingIngredientIndex !== idx}
                          onToggle={(isOpen) => setOpenUnitIndex(prev => isOpen ? idx : (prev === idx ? null : prev))}
                          options={(() => {
                            const rawName = (item.name || '').trim();
                            const nameKey = rawName.toLowerCase();
                            const extras = toolExtrasForName(rawName); // cached — no per-render Levenshtein sweep
                            const isNewItem = !rawName || rawName === 'new ingredient' || !inventoryStdUnitMap[nameKey];
                            return getSmartUnitOptions(
                              item.unit || 'g',
                              isNewItem,
                              showOtherUnitsFor.has(`display-${idx}`),
                              extras
                            );
                          })()}
                          placeholder="Search unit..."
                          style={{ width: 68 }}
                        />
                        <button
                          className="ingredient-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteIngredient(idx);
                          }}
                          title="Delete"
                          type="button"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            </div> {/* End of scrollable ingredient list container */}
            {Object.keys(ingredientBlockers).length > 0 && (
              <div style={{ margin: '12px 0', padding: '12px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca' }}>
                <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: 6 }}>These ingredients need attention before saving</div>
                {Object.entries(ingredientBlockers).map(([key, message]) => (
                  <div key={key} style={{ fontSize: 13, color: '#7f1d1d', marginTop: 4 }}>{message}</div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button ref={addIngredientButtonRef} className="add-ingredient-button" onClick={async () => {
                // Auto-save current ingredient if it has valid data
                if (editingIngredientIndex !== null && editingIngredientIndex !== undefined) {
                  const hasValidName = formData.name && formData.name.trim() && formData.name.trim() !== 'New Ingredient';
                  if (hasValidName) {
                    const saved = await saveIngredient({ silent: true });
                    if (!saved) {
                      showToast('Please complete the current ingredient (check name, quantity, and unit)', 'warning');
                      return;
                    }
                  } else {
                    // Remove the unsaved "New Ingredient" placeholder
                    const cleaned = ingredientList.filter(ing => ing.name !== 'New Ingredient');
                    setIngredientList(cleaned);
                    setEditingIngredientIndex(null);
                    setFormData({ name: '', quantity: 0, unit: '' });
                  }
                }
                
                // Use latest ingredientList (after potential save/clean above)
                setTimeout(() => {
                  setIngredientList(prev => {
                    const defaults = setQtyForPlateStep({}, 0);
                    const newIngredient = { name: 'New Ingredient', unit: '', qty10: defaults.qty10, qty100: defaults.qty100 };
                    const updatedIngredients = [...prev, newIngredient];
                    setEditingIngredientIndex(updatedIngredients.length - 1);
                    setFormData({ name: '', quantity: 0, unit: '' });
                    return updatedIngredients;
                  });
                }, 50);
              }} style={{ flex: 1, fontSize: '1.1rem' }}>
                Add Ingredient
              </button>
              {/* Scan Receipt removed as requested */}
            </div>
          </div>
          <div className="footer-actions" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn--save"
                onClick={handleMarkAsReview}
                style={{ flex: 1, background: '#e5e7eb', color: '#111', border: '1px solid #d1d5db' }}
              >
                Mark as Review
              </button>
              <button
                className="btn--save"
                onClick={handleSaveClick}
                style={{ flex: 1, background: '#000', color: '#fff', border: '1px solid #000' }}
              >
                Confirm
              </button>
            </div>
          </div>
          {showKitchenUnitModal && (
            <div className="modal-overlay" style={{
              position: 'fixed', left: 0, right: 0, top: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
            }}>
              <div className="modal-card" style={{
                width: '100%', maxWidth: 520, background: '#fff', borderRadius: 12, padding: 20,
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
              }}>
                {/* Item (searchable dropdown) */}
                <div style={{ marginBottom: 12, position: 'relative' }}>
                  <div style={{ color: '#111', fontWeight: 'bold', marginBottom: 6 }}>Item</div>
                  <div
                    onClick={() => { setModalItemOpen(o => !o); setModalToolOpen(false); setModalUnitOpen(false); }}
                    className="nice-field"
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {kitchenUnitForm.item || formData?.name || 'Select item'}
                    </span>
                    <FaChevronDown />
                  </div>
                  {modalItemOpen && (
                    <div className="popup-panel" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 8px)' }}>
                      <div className="popup-search">
                        <input
                          className="popup-search-input"
                          value={modalItemSearch}
                          onChange={e => setModalItemSearch(e.target.value)}
                          placeholder="Search item..."
                        />
                      </div>
                      <div className="popup-list" style={{ maxHeight: 220 }}>
                        {sortBySearchPriority(inventoryItemNames || [], modalItemSearch, (itemName) => itemName || '')
                          .map((n, i) => (
                            <div
                              key={i}
                              onClick={() => { setKitchenUnitForm(prev => ({ ...prev, item: n })); setModalItemOpen(false); setModalItemSearch(''); }}
                              className="popup-item"
                              style={{ fontWeight: 'bold' }}
                            >{n}</div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Row: Kitchen Tool, Quantity, Unit */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {/* Kitchen tool dropdown (searchable) */}
                  <div style={{ flex: 1, position: 'relative' }}>
                    <div
                      onClick={() => { setModalToolOpen(o => !o); setModalUnitOpen(false); setModalShowAddTool(false); }}
                      className="nice-field"
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{kitchenUnitForm.kitchenTool || 'Select Tool'}</span>
                      <FaChevronDown />
                    </div>
                    {modalToolOpen && (
                      <div className="popup-panel" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 8px)' }}>
                        <div className="popup-search">
                          <input
                            className="popup-search-input"
                            value={modalToolSearch}
                            onChange={e => setModalToolSearch(e.target.value)}
                            placeholder="Search kitchen tool..."
                          />
                        </div>
                        <div className="popup-list" style={{ maxHeight: 180 }}>
                          {sortBySearchPriority(modalKitchenTools, modalToolSearch, (tool) => tool || '')
                            .map((t) => (
                              <div
                                key={t}
                                onClick={() => { setKitchenUnitForm(prev => ({ ...prev, kitchenTool: t })); setModalToolOpen(false); setModalToolSearch(''); }}
                                className="popup-item"
                              >{t}</div>
                            ))}
                        </div>
                        <div className="popup-footer">
                          {!modalShowAddTool ? (
                            <button
                              onClick={() => setModalShowAddTool(true)}
                              style={{ width: '100%', padding: '10px', background: '#000', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 'bold', cursor: 'pointer' }}
                            >
                              + Add Tool
                            </button>
                          ) : (
                            <div>
                              <input
                                placeholder="Enter Tool Name..."
                                value={modalNewToolName}
                                onChange={(e) => setModalNewToolName(e.target.value)}
                                maxLength={15}
                                style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6, marginBottom: 8 }}
                              />
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                  onClick={() => {
                                    const name = (modalNewToolName || '').trim();
                                    if (!name) return;
                                    // avoid duplicates (case-insensitive)
                                    if (!modalKitchenTools.some(t => t.toLowerCase() === name.toLowerCase())) {
                                      setModalKitchenTools(prev => [...prev, name]);
                                    }
                                    setKitchenUnitForm(prev => ({ ...prev, kitchenTool: name }));
                                    setModalNewToolName('');
                                    setModalShowAddTool(false);
                                    setModalToolOpen(false);
                                  }}
                                  style={{ flex: 1, padding: '10px', background: '#000', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => { setModalShowAddTool(false); setModalNewToolName(''); }}
                                  style={{ flex: 1, padding: '10px', background: '#f5f5f5', color: '#000', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quantity */}
                  <div style={{
                    display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: 10, overflow: 'hidden',
                    height: 44
                  }}>
                    <button
                      onClick={() => setKitchenUnitForm(prev => ({ ...prev, quantity: String(Math.max(0, Number(prev.quantity) - 1)) }))}
                      style={{ width: 36, height: '100%', border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer' }}
                    >−</button>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={kitchenUnitForm.quantity}
                      onChange={(e) => {
                        const v = e.target.value; if (/^\d*$/.test(v)) setKitchenUnitForm(prev => ({ ...prev, quantity: v }));
                      }}
                      style={{ width: 60, border: 'none', outline: 'none', textAlign: 'center', fontSize: 16 }}
                    />
                    <button
                      onClick={() => setKitchenUnitForm(prev => ({ ...prev, quantity: String((Number(prev.quantity) || 0) + 1) }))}
                      style={{ width: 36, height: '100%', border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer' }}
                    >+</button>
                  </div>

                  {/* Unit dropdown (searchable) */}
                  <div style={{ flex: 1, position: 'relative' }}>
                    <div
                      onClick={() => { setModalUnitOpen(o => !o); setModalToolOpen(false); }}
                      className="nice-field"
                    >
                      <span>{kitchenUnitForm.unit || 'Select unit'}</span>
                      <FaChevronDown />
                    </div>
                    {modalUnitOpen && (
                      <div className="popup-panel" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 8px)' }}>
                        <div className="popup-search">
                          <input
                            className="popup-search-input"
                            value={modalUnitSearch}
                            onChange={e => setModalUnitSearch(e.target.value)}
                            placeholder="Search unit..."
                          />
                        </div>
                        <div className="popup-list" style={{ maxHeight: 180 }}>
                          {sortBySearchPriority(baseUnitOptions, modalUnitSearch, (unitOption) => unitOption.label || unitOption.value || '')
                            .map(u => (
                              <div
                                key={u.value}
                                onClick={() => { setKitchenUnitForm(prev => ({ ...prev, unit: u.value })); setModalUnitOpen(false); setModalUnitSearch(''); }}
                                className="popup-item"
                              >{u.label}</div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                  <button
                    onClick={() => setShowKitchenUnitModal(false)}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: '#ffdddd', color: '#d00', border: 'none', borderRadius: 10, fontWeight: 'bold', cursor: 'pointer'
                    }}
                  >
                    Remove Kitchen Unit
                  </button>
                  <button
                    onClick={handleSaveKitchenUnit}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: '#000', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 'bold', cursor: 'pointer'
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add New Complimentary Item Modal */}
          {showAddComplimentaryModal && (
            <div
              className="itemmap-comp-create-modal__backdrop"
              onClick={(event) => {
                if (event.target === event.currentTarget) {
                  closeAddComplimentaryModal();
                }
              }}
              role="presentation"
            >
              <div
                className="itemmap-comp-create-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="itemmap-comp-create-modal-title"
              >
                <form
                  className="itemmap-comp-create-modal__form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    createNewComplimentaryItem();
                  }}
                >
                  <div className="itemmap-comp-create-modal__header">
                    <h3 id="itemmap-comp-create-modal-title" className="itemmap-comp-create-modal__title">
                      Create New Item
                    </h3>
                    <button
                      type="button"
                      className="itemmap-comp-create-modal__close"
                      onClick={closeAddComplimentaryModal}
                      aria-label="Close create recipe modal"
                    >
                      ×
                    </button>
                  </div>

                  <div className="itemmap-comp-create-modal__body">
                    <label htmlFor="itemmap-comp-create-modal-input" className="itemmap-comp-create-modal__label">
                      Item Name
                    </label>
                    <input
                      id="itemmap-comp-create-modal-input"
                      type="text"
                      value={newComplimentaryForm.name}
                      onChange={(e) => setNewComplimentaryForm({ ...newComplimentaryForm, name: e.target.value })}
                      placeholder="Type item name"
                      className="itemmap-comp-create-modal__input"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                    />
                  </div>

                  <div className="itemmap-comp-create-modal__actions">
                    <button
                      type="button"
                      onClick={closeAddComplimentaryModal}
                      className="itemmap-comp-create-modal__button itemmap-comp-create-modal__button--cancel"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="itemmap-comp-create-modal__button itemmap-comp-create-modal__button--confirm"
                      disabled={!newComplimentaryForm.name.trim()}
                    >
                      Create Item
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Unsaved Changes Modal */}
          {showUnsavedChangesModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '0.5rem',
                maxWidth: '450px',
                width: '90%',
                padding: '28px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <h3 style={{
                    margin: '0 0 12px 0',
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#111'
                  }}>Leave Without Saving?</h3>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    color: '#666',
                    lineHeight: '1.5'
                  }}>
                    You have unsaved changes. If you leave now, all ingredient edits on <strong style={{ color: '#000' }}>"{formatSummaryTitle(selectedRecipe?.name || 'This Recipe')}"</strong> will be lost.
                  </p>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => setShowUnsavedChangesModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#e5e7eb',
                      color: '#000',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#d1d5db'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={discardUnsavedChangesAndExitEditor}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1f1f1f'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#000000'}
                  >
                    Leave
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Save Confirmation Modal */}
          {showSaveConfirmationModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                maxWidth: '500px',
                width: '100%',
                padding: '1rem',
                height: 'auto',
                maxHeight: '100%',
                overflowY: 'auto'
              }}>
                <h3 style={{ 
                  margin: '0 0 20px 0', 
                  fontSize: '22px', 
                  fontWeight: '700',
                  color: '#111',
                  textAlign: 'center'
                }}>Item Summary</h3>

                {/* Ingredients Count with Dropdown */}
                <div style={{
                  background: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  position: 'relative'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setSummaryOpenDropdown((prev) => prev === 'ingredients' ? null : 'ingredients');
                  }}>
                    <span style={{ fontWeight: '600', color: '#000000', fontSize: '16px' }}>
                      Number of Ingredients
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        color: 'black',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: '700',
                        fontSize: '1rem'
                      }}>
                        {summaryIngredientItems.length}
                      </span>
                      <FaChevronDown style={{
                        fontSize: '12px',
                        color: '#000',
                        transform: summaryOpenDropdown === 'ingredients' ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.22s ease'
                      }} />
                    </div>
                  </div>
                  
                  {/* Ingredients Dropdown */}
                  <div id="ingredients-dropdown-standard" style={{
                    display: 'block',
                    position: 'relative',
                    marginTop: summaryOpenDropdown === 'ingredients' ? '8px' : '0px',
                    background: '#ffffff',
                    border: summaryOpenDropdown === 'ingredients' ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '8px',
                    width: '100%',
                    maxHeight: summaryOpenDropdown === 'ingredients' ? '14rem' : '0px',
                    overflowY: summaryOpenDropdown === 'ingredients' ? 'auto' : 'hidden',
                    overflowX: 'hidden',
                    opacity: summaryOpenDropdown === 'ingredients' ? 1 : 0,
                    boxShadow: summaryOpenDropdown === 'ingredients' ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                    transition: 'max-height 0.28s ease, opacity 0.2s ease, margin-top 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
                    pointerEvents: summaryOpenDropdown === 'ingredients' ? 'auto' : 'none'
                  }}>
                    {/* Dropdown Header */}
                    <div style={{
                      padding: '12px 16px',
                      borderBottom: '2px solid #e5e7eb',
                      background: '#f9fafb',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#000',
                      textAlign: 'left'
                    }}>
                      Ingredients Per 1 Plate
                    </div>

                    {summaryIngredientItems
                      .map((ingredient, index) => {
                        const perPlateVal = getSavedPerPlateValue(ingredient);

                        return (
                          <div
                            key={index}
                            style={{
                              padding: '10px 16px',
                              borderBottom: index < summaryIngredientItems.length - 1 ? '1px solid #e5e7eb' : 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '12px'
                            }}
                          >
                            <span style={{ color: '#000', fontSize: '14px', fontWeight: '500', flex: 1, textAlign: 'left' }}>
                              {ingredient.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                            </span>
                            <span style={{
                              color: '#000',
                              fontSize: '14px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap'
                            }}>
                              {formatQuantity(perPlateVal, ingredient.unit)}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Saved Preparations */}
                <div style={{
                  background: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  position: 'relative'
                }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setSummaryOpenDropdown((prev) => prev === 'savedPreparations' ? null : 'savedPreparations');
                    }}
                  >
                    <span style={{ fontWeight: '600', color: '#000000', fontSize: '16px', textAlign: 'left' }}>
                      Saved Preparations
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        color: 'black',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: '700',
                        fontSize: '1rem'
                      }}>
                        {summarySavedPreparationItems.length}
                      </span>
                      <FaChevronDown style={{
                        fontSize: '12px',
                        color: '#000',
                        transform: summaryOpenDropdown === 'savedPreparations' ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.22s ease'
                      }} />
                    </div>
                  </div>

                  <div style={{
                    display: 'block',
                    position: 'relative',
                    marginTop: summaryOpenDropdown === 'savedPreparations' ? '8px' : '0px',
                    background: '#ffffff',
                    border: summaryOpenDropdown === 'savedPreparations' ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '8px',
                    width: '100%',
                    maxHeight: summaryOpenDropdown === 'savedPreparations' ? '16rem' : '0px',
                    overflowY: summaryOpenDropdown === 'savedPreparations' ? 'auto' : 'hidden',
                    overflowX: 'hidden',
                    opacity: summaryOpenDropdown === 'savedPreparations' ? 1 : 0,
                    boxShadow: summaryOpenDropdown === 'savedPreparations' ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                    transition: 'max-height 0.28s ease, opacity 0.2s ease, margin-top 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
                    pointerEvents: summaryOpenDropdown === 'savedPreparations' ? 'auto' : 'none'
                  }}>
                    <div style={{
                      padding: '12px 16px',
                      borderBottom: '2px solid #e5e7eb',
                      background: '#f9fafb',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#000',
                      textAlign: 'left'
                    }}>
                      Saved Preparation Links
                    </div>
                    {summarySavedPreparationItems.length > 0 ? (
                      summarySavedPreparationItems.map((prepItem, index) => {
                        const prepMeta = savedPreparationByName.get(normalizeSummaryKey(prepItem?.name));
                        const ingredientRows = getScaledPreparationIngredientRows(prepItem, prepMeta);
                        const componentCount = ingredientRows.length;
                        const scaledPrepQty = getSavedPerPlateValue(prepItem);
                        const perPlateLabel = `${formatCompactQtyUnit(scaledPrepQty, prepItem.unit || 'g')} per plate`;

                        return (
                          <div
                            key={`${prepItem.name}-${index}`}
                            style={{
                              padding: '10px 16px',
                              borderBottom: index < summarySavedPreparationItems.length - 1 ? '1px solid #e5e7eb' : 'none',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                              <span style={{ color: '#000', fontSize: '14px', fontWeight: '600', flex: 1, textAlign: 'left' }}>
                                {formatSummaryTitle(prepItem.name)}
                              </span>
                              <span style={{ color: '#374151', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                {perPlateLabel}
                              </span>
                            </div>
                            <div style={{
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              overflow: 'hidden',
                              background: '#ffffff'
                            }}>
                              {ingredientRows.length > 0 ? (
                                ingredientRows.map((ing, ingIndex) => {
                                  const ingName = String(ing?.itemName || '').trim();
                                  const qtyLabel = formatQuantity(ing?.quantity, ing?.unit);

                                  return (
                                    <div
                                      key={`${prepItem.name}-${index}-${ingIndex}-${ingName}`}
                                      style={{
                                        padding: '8px 12px',
                                        borderBottom: ingIndex < ingredientRows.length - 1 ? '1px solid #e5e7eb' : 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '8px'
                                      }}
                                    >
                                      <span style={{ color: '#111827', fontSize: '12px', fontWeight: '500', flex: 1, textAlign: 'left' }}>
                                        {formatSummaryTitle(ingName || 'Ingredient')}
                                      </span>
                                      <span style={{ color: '#111827', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                        {qtyLabel}
                                      </span>
                                    </div>
                                  );
                                })
                              ) : (
                                <div style={{ padding: '8px 12px', color: '#6b7280', fontSize: '12px' }}>
                                  No ingredients mapped
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '10px 16px', color: '#9ca3af', fontSize: '14px' }}>No Saved Preparations</div>
                    )}
                  </div>
                </div>

                {/* Complimentary Items */}
                <div style={{
                  background: '#fff',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  position: 'relative'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setSummaryOpenDropdown((prev) => prev === 'complimentary' ? null : 'complimentary');
                  }}>
                    <span style={{ fontWeight: '600', color: '#000000', fontSize: '16px', textAlign:'left' }}>
                      Complimentary Items
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        color: 'black',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: '700',
                        fontSize: '1rem'
                      }}>
                        {summaryComplimentaryItems.length}
                      </span>
                      <FaChevronDown style={{
                        fontSize: '12px',
                        color: '#000',
                        transform: summaryOpenDropdown === 'complimentary' ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.22s ease'
                      }} />
                    </div>
                  </div>

                  {/* Complimentary Items Dropdown */}
                  <div id="complimentary-dropdown-standard" style={{
                    display: 'block',
                    position: 'relative',
                    marginTop: summaryOpenDropdown === 'complimentary' ? '8px' : '0px',
                    background: '#ffffff',
                    border: summaryOpenDropdown === 'complimentary' ? '1px solid #d1d5db' : '1px solid transparent',
                    borderRadius: '8px',
                    width: '100%',
                    maxHeight: summaryOpenDropdown === 'complimentary' ? '16rem' : '0px',
                    overflowY: summaryOpenDropdown === 'complimentary' ? 'auto' : 'hidden',
                    overflowX: 'hidden',
                    opacity: summaryOpenDropdown === 'complimentary' ? 1 : 0,
                    boxShadow: summaryOpenDropdown === 'complimentary' ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                    transition: 'max-height 0.28s ease, opacity 0.2s ease, margin-top 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
                    pointerEvents: summaryOpenDropdown === 'complimentary' ? 'auto' : 'none'
                  }}>
                    <div style={{
                      padding: '12px 16px',
                      borderBottom: '2px solid #e5e7eb',
                      background: '#f9fafb',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#000',
                      textAlign: 'left'
                    }}>
                      Complimentary Links
                    </div>
                    {summaryComplimentaryItems.length > 0 ? (
                      summaryComplimentaryItems.map((item, index) => {
                        const itemName = item.name;
                        const meta = getComplimentarySummaryMeta(item);
                        const detailLabel = `${meta.ingredientCount} ingredients`;
                        const ingredientRows = Array.isArray(item?.ingredients) ? item.ingredients : [];
                        return (
                          <div
                            key={index}
                            style={{
                              padding: '10px 16px',
                              borderBottom: index < summaryComplimentaryItems.length - 1 ? '1px solid #e5e7eb' : 'none',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              gap: '4px',
                              color: '#000',
                              fontSize: '14px',
                              textAlign: 'left'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                              <span style={{ color: '#000', fontSize: '14px', fontWeight: '600', flex: 1 }}>
                                {formatSummaryTitle(itemName)}
                              </span>
                              <span style={{ color: '#111827', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                {detailLabel}
                              </span>
                            </div>
                            <div style={{
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              overflow: 'hidden',
                              background: '#ffffff'
                            }}>
                              {ingredientRows.length > 0 ? (
                                ingredientRows.map((ing, ingIndex) => {
                                  const ingName = String(ing?.item_name || '').trim();
                                  const qtyLabel = formatQuantity(ing?.quantity, ing?.unit_symbol);

                                  return (
                                    <div
                                      key={`${index}-${ingIndex}-${ingName}`}
                                      style={{
                                        padding: '8px 12px',
                                        borderBottom: ingIndex < ingredientRows.length - 1 ? '1px solid #e5e7eb' : 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '8px'
                                      }}
                                    >
                                      <span style={{ color: '#111827', fontSize: '12px', fontWeight: '500', flex: 1 }}>
                                        {formatSummaryTitle(ingName || 'Ingredient')}
                                      </span>
                                      <span style={{ color: '#111827', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                        {qtyLabel}
                                      </span>
                                    </div>
                                  );
                                })
                              ) : (
                                <div style={{ padding: '8px 12px', color: '#6b7280', fontSize: '12px' }}>
                                  No ingredients mapped
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '10px 16px', color: '#9ca3af', fontSize: '14px' }}>No Complimentary Items Linked</div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => {
                      setSummaryOpenDropdown(null);
                      setShowSaveConfirmationModal(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#EBEBEB',
                      color: '#000',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      height: '44px',
                      minHeight: '44px',
                      maxHeight: '44px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmClick}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      height: '44px',
                      minHeight: '44px',
                      maxHeight: '44px'
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          {showVerifyModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2100
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '16px',
                maxWidth: '400px',
                width: '90%',
                padding: '24px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                position: 'relative'
              }}>
                <button
                  onClick={() => setShowVerifyModal(false)}
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px'
                  }}
                >
                  <FaTimes />
                </button>

                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1e293b',
                  textAlign: 'left',
                  paddingRight: '24px'
                }}>
                  Please verify
                </h3>

                <p style={{
                  margin: '0 0 24px 0',
                  fontSize: '14px',
                  color: '#64748b',
                  lineHeight: '1.5',
                  textAlign: 'left'
                }}>
                  {verifyModalMessage}
                </p>

                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => setShowVerifyModal(false)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      background: '#f1f5f9',
                      color: '#1e293b',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      height: '40px'
                    }}
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      setShowVerifyModal(false);
                      confirmAndSave();
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      background: '#0b0f19',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      height: '40px'
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Import Ingredients Confirmation Modal */}
          {showImportConfirmModal && selectedMenuItemForImport && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '0.5rem',
                maxWidth: '450px',
                width: '90%',
                padding: '28px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ 
                    margin: '0 0 12px 0', 
                    fontSize: '22px', 
                    fontWeight: '700',
                    color: '#111'
                  }}>Import Ingredients?</h3>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    color: '#666',
                    lineHeight: '1.5'
                  }}>
                    Do you want to import all ingredients from <strong style={{ color: '#000' }}>"{selectedMenuItemForImport.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}"</strong>?
                  </p>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => {
                      setShowImportConfirmModal(false);
                      setSelectedMenuItemForImport(null);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#e5e7eb',
                      color: '#000',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#d1d5db'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmImportIngredients}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1f1f1f'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#000000'}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Repeat Import — How many plates? */}
          {showRepeatImportModal && selectedMenuItemForImport && (() => {
            const parsedPlates = parseRepeatImportPlates(repeatImportPlates);
            const platesInvalid = parsedPlates === null;
            const stepPlates = (delta) => {
              const current = Number(repeatImportPlates);
              const base = Number.isFinite(current) ? current : 1;
              const next = Math.min(REPEAT_IMPORT_MAX_PLATES, Math.max(REPEAT_IMPORT_MIN_PLATES, Number((base + delta).toFixed(1))));
              setRepeatImportPlates(String(next));
            };
            const stepBtnStyle = {
              width: 44,
              height: 44,
              fontSize: 20,
              fontWeight: 600,
              background: '#f3f4f6',
              color: '#000',
              border: '1px solid #d1d5db',
              borderRadius: '0.5rem',
              cursor: repeatImportBusy ? 'not-allowed' : 'pointer'
            };
            return (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2000
                }}
                onClick={closeRepeatImportModal}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: '#ffffff',
                    borderRadius: '0.5rem',
                    maxWidth: '450px',
                    width: '90%',
                    padding: '28px',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', fontWeight: '700', color: '#111' }}>
                      How many plates?
                    </h3>
                    <p style={{ margin: 0, fontSize: '15px', color: '#666', lineHeight: '1.5' }}>
                      <strong style={{ color: '#000' }}>"{selectedMenuItemForImport.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}"</strong>{repeatImportIsFirst
                        ? ' — its ingredients for this many plates will be added to this dish. Ingredients already on the dish keep their quantity, except saved preparations, which are added onto.'
                        : ' is already imported. Its ingredient quantities for this many plates will be added to the matching ingredients.'}
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '8px' }}>
                    <button type="button" disabled={repeatImportBusy} onClick={() => stepPlates(-1)} style={stepBtnStyle}>−</button>
                    <input
                      type="number"
                      inputMode="decimal"
                      min={REPEAT_IMPORT_MIN_PLATES}
                      max={REPEAT_IMPORT_MAX_PLATES}
                      step="0.1"
                      autoFocus
                      value={repeatImportPlates}
                      disabled={repeatImportBusy}
                      onChange={(e) => setRepeatImportPlates(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); if (!platesInvalid && !repeatImportBusy) confirmRepeatImport(); }
                        if (e.key === 'Escape') closeRepeatImportModal();
                      }}
                      style={{
                        width: 110,
                        height: 44,
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 600,
                        border: `1px solid ${platesInvalid ? '#dc2626' : '#d1d5db'}`,
                        borderRadius: '0.5rem',
                        outline: 'none'
                      }}
                    />
                    <button type="button" disabled={repeatImportBusy} onClick={() => stepPlates(1)} style={stepBtnStyle}>+</button>
                  </div>
                  <div style={{ textAlign: 'center', fontSize: '13px', color: platesInvalid ? '#dc2626' : '#6b7280', marginBottom: '20px' }}>
                    {platesInvalid
                      ? `Enter a value from ${REPEAT_IMPORT_MIN_PLATES} to ${REPEAT_IMPORT_MAX_PLATES} plates`
                      : `Range: ${REPEAT_IMPORT_MIN_PLATES} – ${REPEAT_IMPORT_MAX_PLATES} plates`}
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="button"
                      onClick={closeRepeatImportModal}
                      disabled={repeatImportBusy}
                      style={{
                        flex: 1,
                        padding: '12px 20px',
                        fontSize: '15px',
                        fontWeight: '600',
                        background: '#e5e7eb',
                        color: '#000',
                        border: 'none',
                        borderRadius: '0.5rem',
                        cursor: repeatImportBusy ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmRepeatImport}
                      disabled={platesInvalid || repeatImportBusy}
                      style={{
                        flex: 1,
                        padding: '12px 20px',
                        fontSize: '15px',
                        fontWeight: '600',
                        background: (platesInvalid || repeatImportBusy) ? '#9ca3af' : '#000000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        cursor: (platesInvalid || repeatImportBusy) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {repeatImportBusy ? 'Importing…' : 'Import'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Library Match & Import Modal (sync button) */}
          {showLibraryMatchModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '0.5rem',
                maxWidth: '460px',
                width: '90%',
                padding: '24px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '80vh'
              }}>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ margin: '0 0 6px 0', fontSize: '20px', fontWeight: '700', color: '#111', textAlign: 'center' }}>
                    Import from Recipe Library
                  </h3>
                  <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.5', textAlign: 'center' }}>
                    Best match for <strong style={{ color: '#000' }}>"{selectedRecipe?.name || 'this item'}"</strong> — pick one and import all its ingredients.
                  </p>
                </div>

                {/* Search bar */}
                <input
                  type="text"
                  value={libraryMatchQuery}
                  onChange={(e) => { setLibraryMatchQuery(e.target.value); runLibraryMatchSearch(e.target.value, false); }}
                  placeholder="Search recipe library menu items..."
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    outline: 'none',
                    marginBottom: '12px',
                    boxSizing: 'border-box'
                  }}
                />

                {/* Results */}
                <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #f3f4f6', borderRadius: '8px', minHeight: '120px', maxHeight: '320px' }}>
                  {libraryMatchLoading ? (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>Searching…</div>
                  ) : libraryMatchResults.length === 0 ? (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>No matching menu items in the library</div>
                  ) : (
                    libraryMatchResults.map((r, idx) => {
                      const rname = r.item_name || r.name || '';
                      const isSelected = libraryMatchSelected && (libraryMatchSelected.recipe_library_id === r.recipe_library_id);
                      const ingCount = Array.isArray(r.ingredients) ? r.ingredients.length : 0;
                      return (
                        <div
                          key={`libmatch-${r.recipe_library_id || rname}`}
                          onClick={() => setLibraryMatchSelected(r)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f3f4f6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: isSelected ? '#eff6ff' : '#ffffff',
                            borderLeft: isSelected ? '3px solid #2563eb' : '3px solid transparent'
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ fontWeight: 600, fontSize: '14px', color: '#111' }}>
                              {rname.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}
                              {idx === 0 && libraryMatchQuery.trim() ? (
                                <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase' }}>Best match</span>
                              ) : null}
                            </span>
                            {r.category && <span style={{ fontSize: 12, color: '#9ca3af' }}>{r.category}</span>}
                          </div>
                          <span style={{ fontSize: 12, color: '#666', background: '#f3f4f6', padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap' }}>{ingCount} ing</span>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button
                    onClick={closeLibraryMatchModal}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#e5e7eb',
                      color: '#000',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#d1d5db'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmLibraryMatchImport}
                    disabled={!libraryMatchSelected}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: libraryMatchSelected ? '#000000' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: libraryMatchSelected ? 'pointer' : 'not-allowed',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => { if (libraryMatchSelected) e.currentTarget.style.background = '#1f1f1f'; }}
                    onMouseLeave={(e) => { if (libraryMatchSelected) e.currentTarget.style.background = '#000000'; }}
                  >
                    Import
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete All Ingredients Confirmation Modal */}
          {showDeleteAllModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '0.5rem',
                maxWidth: '450px',
                width: '90%',
                padding: '28px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ 
                    margin: '0 0 12px 0', 
                    fontSize: '22px', 
                    fontWeight: '700',
                    color: '#111'
                  }}>Delete All Ingredients?</h3>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    color: '#666',
                    lineHeight: '1.5'
                  }}>
                    This will remove all <strong style={{ color: '#000' }}>{ingredientList.length}</strong> ingredient(s) from the current recipe. This action can be undone.
                  </p>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => setShowDeleteAllModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#e5e7eb',
                      color: '#000',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#d1d5db'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  >
                    No
                  </button>
                  <button
                    onClick={confirmDeleteAllIngredients}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1f1f1f'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#000000'}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Complimentary Item Confirmation Modal */}
          {showDeleteComplimentaryModal && complimentaryItemToDelete && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '0.5rem',
                maxWidth: '450px',
                width: '90%',
                padding: '28px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <h3 style={{
                    margin: '0 0 12px 0',
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#111'
                  }}>Delete Complimentary Item?</h3>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    color: '#666',
                    lineHeight: '1.5'
                  }}>
                    This will completely remove <strong style={{ color: '#000' }}>&quot;{complimentaryItemToDelete.name}&quot;</strong> from the complimentary library and unlink it from recipes.
                  </p>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => closeDeleteComplimentaryModal(true)}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#e5e7eb',
                      color: '#000',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#d1d5db'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#e5e7eb'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteComplimentaryItem}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1f1f1f'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#000000'}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {complimentaryLinkModal.visible && complimentaryLinkModal.item && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2001,
                padding: '20px'
              }}
              onClick={() => {
                if (!complimentaryLinkModal.isSubmitting) {
                  closeComplimentaryLinkModal();
                }
              }}
            >
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '0.5rem',
                  maxWidth: '460px',
                  width: '100%',
                  padding: '28px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                }}
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <h3 style={{
                    margin: '0 0 12px 0',
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#111'
                  }}>
                    {complimentaryLinkModal.currentlyLinked ? 'Remove Complimentary Item?' : 'Add Complimentary Item?'}
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    color: '#666',
                    lineHeight: '1.6'
                  }}>
                    {complimentaryLinkModal.currentlyLinked ? (
                      <>
                        <strong style={{ color: '#000' }}>&quot;{getComplimentaryItemName(complimentaryLinkModal.item)}&quot;</strong> will be removed from <strong style={{ color: '#000' }}>&quot;{selectedRecipe?.name || 'this recipe'}&quot;</strong> and will no longer appear as a complimentary item for this dish.
                      </>
                    ) : (
                      <>
                        <strong style={{ color: '#000' }}>&quot;{getComplimentaryItemName(complimentaryLinkModal.item)}&quot;</strong> will be added to <strong style={{ color: '#000' }}>&quot;{selectedRecipe?.name || 'this recipe'}&quot;</strong> and will be served as a complimentary item for this dish.
                      </>
                    )}
                  </p>
                  {complimentaryLinkModal.error && (
                    <p style={{
                      margin: '14px 0 0 0',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#dc2626',
                      lineHeight: '1.5'
                    }}>
                      {complimentaryLinkModal.error}
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={closeComplimentaryLinkModal}
                    disabled={complimentaryLinkModal.isSubmitting}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#e5e7eb',
                      color: '#000',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: complimentaryLinkModal.isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: complimentaryLinkModal.isSubmitting ? 0.7 : 1,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!complimentaryLinkModal.isSubmitting) e.currentTarget.style.background = '#d1d5db';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#e5e7eb';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmComplimentaryLinkChange}
                    disabled={complimentaryLinkModal.isSubmitting}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      background: '#000000',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: complimentaryLinkModal.isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: complimentaryLinkModal.isSubmitting ? 0.7 : 1,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!complimentaryLinkModal.isSubmitting) {
                        e.currentTarget.style.background = '#1f1f1f';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#000000';
                    }}
                  >
                    {complimentaryLinkModal.isSubmitting
                      ? (complimentaryLinkModal.currentlyLinked ? 'Removing...' : 'Adding...')
                      : (complimentaryLinkModal.currentlyLinked ? 'Remove Item' : 'Add Item')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {toastModal.visible && (
            <div className="toast-modal-overlay" onClick={closeToast} role="presentation">
              <div
                className="toast-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <div className="toast-modal-message">{toastModal.message}</div>
              </div>
            </div>
          )}
          {savingIngredients && (
            <div className="saving-overlay">
              <div className="saving-card">
                <div style={{ 
                  width: '50px', 
                  height: '50px', 
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 24px'
                }}></div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                <div className="saving-title">Saving Ingredient List</div>
                <div className="saving-subtitle">Please wait while we save your item ingredients...</div>
              </div>
            </div>
          )}

        </div>
        </div>
        )}


        {/* Preparation Creator Modal - Standalone feature */}
        {showPreparationCreator && (
          <PreparationCreatorModal
            businessId={UnitMappingService.getBusinessId()}
            onSave={(result) => {
              debugLog(' Preparation created:', result);
              loadSavedPreparations();
              setShowPreparationCreator(false);
            }}
            onCancel={() => {
              debugLog('❌ Preparation creation cancelled');
              setShowPreparationCreator(false);
            }}
          />
        )}

        {duplicateRecipeError && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2147483010
          }}>
            <div style={{
              width: 'min(380px, 90vw)',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '36px', color: '#eab308' }} />
              </div>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#1f2937'
              }}>
                Item Already Exists
              </div>
              <div style={{
                fontSize: '14px',
                color: '#4b5563',
                marginBottom: '20px',
                lineHeight: '1.4'
              }}>
                The item "{duplicateRecipeError.name}" already exists. Please choose a different name.
              </div>
              <button 
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#000000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  width: '120px'
                }}
                onClick={() => setDuplicateRecipeError(null)}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (loading) {
    return (
      <div className="status-container">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
          <div style={{ position: 'relative', width: '95px', height: '95px' }}>
            <img 
              src="/invexis-logo.png"
              alt="Invexis"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                backgroundColor: '#fff',
                padding: '0px',
                pointerEvents: 'none',
                objectFit: 'contain',
                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                zIndex: 1
              }}
            />
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '70px',
              height: '70px',
              border: '6px solid #000000',
              borderTop: '6px solid #f9c806',
              borderRight: '6px solid #f9c806',
              borderRadius: '50%',
              animation: 'itemMapSpin 1s linear infinite',
              zIndex: 16
            }}></div>
          </div>
          <div style={{ fontSize: 16, color: '#666', fontWeight: '500', margin: 0 }}>Loading items...</div>
        </div>
        <style>{`
          @keyframes itemMapSpin {
            0% { transform: translate(-50%, -50%) rotate(0deg); }
            100% { transform: translate(-50%, -50%) rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="status-container">
        <div>
          <div style={{ fontSize: 18, marginBottom: 10, color: '#d32f2f' }}>Error</div>
          <div style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>{error}</div>
          <button onClick={() => window.location.reload()} style={{ padding: '10px 20px', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Retry</button>
        </div>
      </div>
    );
  }

   if (showNewRecipeForm) {
    return (
      <>
        <div className="newrecipe-container" style={{ 
          maxWidth: '600px', 
          margin: '0 auto', 
          paddingBottom: '80px', 
          minHeight: '100vh',
          height: '100vh',
          background: '#ffffff',
          position: 'relative',
        }}>
        <div className="itemmap-header" style={{ marginBottom: '16px' }}>
          <button
            type="button"
            onClick={() => { setShowNewRecipeForm(false); setRecipeCards([{ id: Date.now(), name: '', price: '', servings: '' }]); setCapturedImage(null); }}
            className="back-arrow-btn"
            aria-label="Back to Item Mapping"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h3 className="page-title" style={{ marginTop: '0.2rem' }}>Create New Item</h3>
        </div>
        {capturedImage && (
          <div className="recipe-hero-shell recipe-hero-shell--padded">
            <img src={capturedImage} alt="Captured menu" className="recipe-hero" />
          </div>
        )}

        <div style={{ padding: '0 16px', marginBottom: '5rem' }}>
          {/* Recipe Cards */}
          {recipeCards.map((card, index) => (
            <div key={card.id} className="card itemmap-create-recipe-card" style={{ maxWidth: '111%', width: 'calc(100% + 20px)', margin: '0 -10px 16px -10px', position: 'relative', textAlign: 'left', padding:'0px 16px 0px 16px' }}>
              <div className="itemmap-create-recipe-card-topbar">
                <label className="form-label itemmap-recipe-label itemmap-create-recipe-heading" style={{ color: '#000', fontFamily: 'Roboto, sans-serif', fontWeight: '600', fontSize: '16px', marginBottom: '0', display: 'block' }}>Item Name</label>
                {recipeCards.length > 1 && (
                  <button
                    onClick={() => removeRecipeCard(card.id)}
                    className="itemmap-create-recipe-remove-btn"
                    aria-label="Remove recipe card"
                  >
                    <FontAwesomeIcon icon={faTimes} className="itemmap-create-recipe-remove-icon" />
                  </button>
                )}
              </div>

              <div className="form-row itemmap-recipe-name-row">
                <input
                  type="text"
                  value={card.name}
                  onChange={(e) => updateRecipeCard(card.id, 'name', e.target.value)}
                  placeholder="Enter item name"
                  className="boxed-input itemmap-recipe-name-input itemmap-create-recipe-input"
                  ref={(el) => {
                    if (!cardFieldRefs.current[card.id]) cardFieldRefs.current[card.id] = {};
                    cardFieldRefs.current[card.id].name = el;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      cardFieldRefs.current[card.id]?.price?.focus();
                    }
                  }}
                  style={{ 
                    width: '100%', 
                    fontWeight:'600', 
                    fontSize: '14px',
                    textAlign: 'left',
                    fontFamily: 'Roboto, sans-serif',
                    borderColor: creationErrors[card.id]?.name ? '#ef4444' : '',
                    backgroundColor: creationErrors[card.id]?.name ? '#fef2f2' : ''
                  }}
                />
                {creationErrors[card.id]?.name && (
                  <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '-2px', lineHeight: '1.25', display: 'block', fontWeight: '500', textAlign: 'left' }}>
                    Item Name is required
                  </span>
                )}
              </div>
              <div className="form-grid itemmap-recipe-details-grid">
                <div className="form-col itemmap-price-col">
                  <label className="form-label itemmap-recipe-label" style={{ color: '#000', fontFamily: 'Roboto, sans-serif', fontWeight: '600', fontSize: '16px', marginBottom: '8px', display: 'block' }}>Price (₹)</label>
                  <input
                    type="text"
                    value={card.price}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d*\.?\d*$/.test(val)) {
                        updateRecipeCard(card.id, 'price', val);
                      }
                    }}
                    className="boxed-input itemmap-recipe-price-input itemmap-create-recipe-input"
                    placeholder="Enter price"
                    ref={(el) => {
                      if (!cardFieldRefs.current[card.id]) cardFieldRefs.current[card.id] = {};
                      cardFieldRefs.current[card.id].price = el;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        cardFieldRefs.current[card.id]?.servings?.focus();
                      }
                    }}
                    style={{ 
                      width: '100%', 
                      fontWeight:'600', 
                      fontSize: '14px',
                      textAlign: 'left',
                      fontFamily: 'Roboto, sans-serif',
                      borderColor: creationErrors[card.id]?.price ? '#ef4444' : '',
                      backgroundColor: creationErrors[card.id]?.price ? '#fef2f2' : ''
                    }}
                  />
                  {creationErrors[card.id]?.price && (
                    <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '4px', lineHeight: '1.25', display: 'block', fontWeight: '500', textAlign: 'left' }}>
                      Price is required
                    </span>
                  )}
                </div>
                <div className="form-col itemmap-servings-col">
                  <label className="form-label itemmap-recipe-label" style={{ color: '#000', fontFamily: 'Roboto, sans-serif', fontWeight: '600', fontSize: '16px', marginBottom: '8px', display: 'block' }}>Servings</label>
                  <input
                    type="text"
                    value={card.servings}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d+$/.test(val)) {
                        updateRecipeCard(card.id, 'servings', val);
                      }
                    }}
                    className="boxed-input itemmap-recipe-servings-input itemmap-create-recipe-input"
                    placeholder="Enter servings"
                    ref={(el) => {
                      if (!cardFieldRefs.current[card.id]) cardFieldRefs.current[card.id] = {};
                      cardFieldRefs.current[card.id].servings = el;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        // If last card, add a new one; otherwise jump to next card's name
                        if (index === recipeCards.length - 1) {
                          addMoreRecipeCard();
                        } else {
                          cardFieldRefs.current[recipeCards[index + 1].id]?.name?.focus();
                        }
                      }
                    }}
                    style={{ 
                      width: '100%', 
                      fontWeight:'600', 
                      fontSize: '14px',
                      textAlign: 'left',
                      fontFamily: 'Roboto, sans-serif',
                      borderColor: creationErrors[card.id]?.servings ? '#ef4444' : '',
                      backgroundColor: creationErrors[card.id]?.servings ? '#fef2f2' : ''
                    }}
                  />
                  {creationErrors[card.id]?.servings && (
                    <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: '4px', lineHeight: '1.25', display: 'block', fontWeight: '500', textAlign: 'left' }}>
                      Servings is required
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Add More Recipe Button below cards */}
          <div style={{ marginBottom: '16px', width: 'calc(100% + 20px)', margin: '0 -10px 16px -10px', maxWidth: '111%' }}>
            <button
              onClick={addMoreRecipeCard}
              className="btn btn--primary"
              style={{
                width: '100%',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                background: '#000000',
                border: 'none',
                fontSize: '14px',
                fontFamily: 'Roboto, sans-serif'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Add More Items
            </button>
          </div>

          {/* Bottom Action Button */}
          <div style={{
            width: 'calc(100% + 20px)',
            margin: '0 -10px 0 -10px',
            maxWidth: '111%',
            padding: '0'
          }}>
            <button
              onClick={() => createRecipesWithoutIngredients()}
              className="btn btn--primary"
              style={{
                width: '100%',
                padding: '16px 24px',
                fontSize: '14px',
                fontWeight: '600',
                background:'#16A34A',
                fontFamily: 'Roboto, sans-serif'
              }}
            >
              Create Item
            </button>
          </div>
        </div>
        </div>

        {/* Recipe Creation Loading Overlay */}
        {isCreatingRecipes && (
          <div className="modal-overlay" style={{ zIndex: 10001 }}>
            <div className="creating-recipe-popup" style={{ 
              width: '400px',
              maxWidth: 'calc(100% - 40px)',
              height: 'auto',
              textAlign: 'center', 
              padding: '24px 24px', 
              borderRadius: '0.5rem',
              overflow: 'visible',
              boxSizing: 'border-box',
              background: 'white',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              margin: '0 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {/* Loading Spinner */}
              <div style={{ 
                width: '40px', 
                height: '40px', 
                position: 'relative',
                margin: '0 auto 16px auto'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  width: '40px',
                  height: '40px',
                  border: '4px solid transparent',
                  borderTop: '4px solid #000',
                  borderRight: '4px solid #000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
              </div>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
              
              <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '8px', color: '#000' }}>
                Creating Ingredients...
              </div>
              
              {recipeCreationProgress.message && (
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                  {recipeCreationProgress.message}
                </div>
              )}
              {recipeCreationProgress.total > 0 && (
                <div style={{ width: '100%' }}>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#666', 
                    fontWeight: 400,
                    marginBottom: '8px'
                  }}>
                    {Math.round((recipeCreationProgress.current / recipeCreationProgress.total) * 100)}% Complete
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '6px', 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: '3px', 
                    overflow: 'hidden',
                    marginBottom: '16px'
                  }}>
                    <div style={{ 
                      width: `${(recipeCreationProgress.current / recipeCreationProgress.total) * 100}%`, 
                      height: '100%', 
                      backgroundColor: '#000',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}
              
              {/* Cancel Button */}
              <button 
                onClick={() => setIsCreatingRecipes(false)}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#000',
                  color: '#fff',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontWeight: 500,
                  marginBottom: '0'
                }}
                onMouseOver={(e) => e.target.style.background = '#333'}
                onMouseOut={(e) => e.target.style.background = '#000'}
              >
                Cancel
              </button>
              
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                  
                  @media (max-width: 600px) {
                    .creating-recipe-popup {
                      height: auto !important;
                      padding: 20px !important;
                      justify-content: center !important;
                    }
                  }
                `}
              </style>
            </div>
          </div>
        )}

        {duplicateRecipeError && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2147483010
          }}>
            <div style={{
              width: 'min(380px, 90vw)',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '36px', color: '#eab308' }} />
              </div>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#1f2937'
              }}>
                Item Already Exists
              </div>
              <div style={{
                fontSize: '14px',
                color: '#4b5563',
                marginBottom: '20px',
                lineHeight: '1.4'
              }}>
                The item "{duplicateRecipeError.name}" already exists. Please choose a different name.
              </div>
              <button 
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#000000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  width: '120px'
                }}
                onClick={() => setDuplicateRecipeError(null)}
              >
                OK
              </button>
            </div>
          </div>
        )}

        {variantConfirm && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2147483010 }}>
            <div style={{ width: 'min(420px, 92vw)', background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: 32, color: '#eab308' }} />
              </div>
              <div style={{ fontSize: 18, fontWeight: 600, textAlign: 'center', color: '#1f2937' }}>Item name already exists</div>
              <div style={{ fontSize: 14, color: '#4b5563', textAlign: 'center', margin: '8px 0 16px', lineHeight: 1.4 }}>
                Create it as a variant of the existing item? It gets its own name and price, and starts with the existing recipe scaled by the multiplier (1 = same recipe, 0.5 = half portion).
              </div>
              {variantConfirm.items.map((item, idx) => (
                <div key={item.name} style={{ marginBottom: 14, padding: 12, borderRadius: 8, background: '#f9fafb' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>{item.parentName}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Existing: ₹{item.existingPrices.join(', ₹')} → New: ₹{item.newPrice}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <label style={{ flex: 2, fontSize: 12, color: '#374151' }}>Label
                      <input
                        value={item.label}
                        onChange={(e) => setVariantConfirm((prev) => {
                          const items = [...prev.items];
                          items[idx] = { ...items[idx], label: e.target.value };
                          return { items };
                        })}
                        placeholder="e.g. Half, Large, ₹249"
                        style={{ width: '100%', marginTop: 4, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6 }}
                      />
                    </label>
                    <label style={{ flex: 1, fontSize: 12, color: '#374151' }}>Recipe ×
                      <input
                        type="number"
                        min="0.05"
                        step="0.05"
                        value={item.multiplier}
                        onChange={(e) => setVariantConfirm((prev) => {
                          const items = [...prev.items];
                          items[idx] = { ...items[idx], multiplier: e.target.value };
                          return { items };
                        })}
                        style={{ width: '100%', marginTop: 4, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6 }}
                      />
                    </label>
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Will be created as "{item.parentName} ({String(item.label || '').trim() || `₹${item.newPrice}`})"</div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button
                  style={{ padding: '10px 24px', backgroundColor: '#fff', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', flex: 1 }}
                  onClick={() => setVariantConfirm(null)}
                >
                  Cancel
                </button>
                <button
                  style={{ padding: '10px 24px', backgroundColor: '#000', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', flex: 1 }}
                  onClick={() => {
                    const choices = {};
                    variantConfirm.items.forEach((item) => {
                      const multiplier = parseFloat(item.multiplier);
                      choices[item.name.trim().toLowerCase()] = {
                        parentId: item.parentId,
                        label: String(item.label || '').trim() || `₹${item.newPrice}`,
                        multiplier: Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1,
                      };
                    });
                    setVariantConfirm(null);
                    createRecipesWithoutIngredients({ variantChoices: choices });
                  }}
                >
                  Create variant
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="itemmap-container">
      <div className="itemmap-header">
        {cameFromTSR && (
          <button
            type="button"
            onClick={() => {
              debugLog('🔙 Navigating back to TSR with preserved data');
              goTo && goTo('todays-sales-report');
            }}
            className="back-arrow-btn"
            aria-label="Back to Today's Sales Report"
            title="Back to Sales Report"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        {cameFromWastage && (
          <button
            type="button"
            onClick={() => {
              debugLog('🔙 Navigating back to Wastage with preserved data');
              goTo && goTo('stockOutUsage');
            }}
            className="back-arrow-btn"
            aria-label="Back to Wastage"
            title="Back to Wastage"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        {cameFromMenuManagement && (
          <button
            type="button"
            onClick={() => {
              debugLog('🔙 Navigating back to Menu Management with preserved data');
              goTo && goTo('qr-menu-management');
            }}
            className="back-arrow-btn"
            aria-label="Back to Menu Management"
            title="Back to Menu Management"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        {!cameFromTSR && !cameFromWastage && !cameFromMenuManagement && (
          <button
            type="button"
            onClick={() => {
              if (isOnboarding && onBack) {
                onBack();
              } else {
                goTo && goTo('dashboard');
              }
            }}
            className="back-arrow-btn"
            aria-label={isOnboarding ? 'Back to User Details' : 'Back to Dashboard'}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        <h3 className="page-title" style={{marginTop:'0.2rem'}}>Item Mapping</h3>
      </div>
      
      <div className="action-tiles">
        <div className="action-tile scan-full" style={{ padding: 0 }}>
          <button onClick={() => { setShowNewRecipeForm(true); }} className="tile-btn">
            <div className="scan-card scan-card--filled">
              <div className="camera-icon-container">
                <div className="camera-icon" aria-hidden>
                  <FontAwesomeIcon icon={faCamera} className="scan-svg" />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className="scan-title">Add Menu</div>
                <div className="scan-subtitle" style={{ fontSize: '14px' }}>Click here to add items to the menu</div>
              </div>
            </div>
          </button>
        </div>
      </div>
      {recipes.length === 0 && (
        <div style={{ textAlign: 'center', padding: '8rem 1rem 0 1rem', color: '#000', fontSize: '1rem', fontWeight: '500' }}>
          Enter the name, price, and details for each item
        </div>
      )}
      
      {recipes.length > 0 && (
        <div className="itemmap-sticky-filters">
          <div className="search-wrap" style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
            <div style={{ flex: 1 }}>
              <StandardSearch
                value={search}
                onChange={setSearch}
                onClear={() => setSearch('')}
                placeholder="Search ingredients..."
                style={{ marginBottom: 0 }}
              />
            </div>
            {isOnboarding && (
              <button
                className="sync-library-btn"
                onClick={handleSyncFromLibrary}
                title="Sync ingredients from Recipe Library"
                disabled={syncLoading}
              >
                <FontAwesomeIcon icon={faSync} spin={syncLoading} />
              </button>
            )}
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '8px 4px',
            marginBottom: '4px'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937', fontFamily: 'Roboto, sans-serif' }}>
              Filter by Master Categories
            </span>
            <label className="switch" style={{ 
              position: 'relative', 
              display: 'inline-block', 
              width: '42px', 
              height: '24px' 
            }}>
              <input 
                type="checkbox" 
                checked={useMasterCategories} 
                onChange={(e) => setUseMasterCategories(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span className="slider round" style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: useMasterCategories ? '#000000' : '#D1D5DB',
                transition: '0.3s',
                borderRadius: '24px'
              }}>
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '18px',
                  width: '18px',
                  left: '3px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  transition: '0.3s',
                  borderRadius: '50%',
                  transform: useMasterCategories ? 'translateX(18px)' : 'none',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }} />
              </span>
            </label>
          </div>

          <div className="category-chips-viewall-wrap">
            <div ref={categoryChipsRef} className="category-chips">
              {visibleCategoryOptions.map((c) => (
                <button
                  key={c}
                  data-visible-category-chip="true"
                  onClick={() => setFilter(c)}
                  className={`chip ${filter === c ? 'active' : ''}`}
                >
                  {c}
                </button>
              ))}
              {hasCategoryChipOverflow && (
                <button
                  type="button"
                  data-category-toggle="true"
                  onClick={() => setShowAllCategoryChips((prev) => !prev)}
                  className="chip chip-view-all"
                >
                  {showAllCategoryChips ? 'Show less' : 'View all'}
                </button>
              )}
            </div>
            <div
              ref={categoryMeasureRef}
              className="category-chips category-chips-measure"
              aria-hidden="true"
            >
              {categoryFilterOptions.map((c) => (
                <button
                  key={`measure-${c}`}
                  type="button"
                  data-measure-chip="true"
                  tabIndex={-1}
                  className="chip"
                >
                  {c}
                </button>
              ))}
              <button
                type="button"
                data-measure-view-all="true"
                tabIndex={-1}
                className="chip chip-view-all"
              >
                View all
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredRecipes.slice(0, visibleCardCount).map((r) => {
        const isEditing = r.editing;
        // Edit a field in local state only. Persisting happens ONCE, on the
        // explicit Save button (saveRecipeChanges) — never per keystroke.
        // Auto-saving price on every onChange previously fired one PUT (and so
        // one Item-Mapping log) for each digit typed: "150" logged ₹0→₹1,
        // ₹1→₹15, ₹15→₹150. One change must produce exactly one log.
        const handleUpdateRecipeField = (id, field, value) => {
          setRecipes((prev) => prev.map((rec) => rec.id === id ? { ...rec, [field]: value } : rec));
        };
        const saveRecipeChanges = async (id) => {
          const recipe = recipes.find(r => r.id === id);
          if (recipe) {
            const editedName = recipe.name ? recipe.name.trim().toLowerCase() : '';
            const isDuplicate = recipes.some(r => r.id !== id && r.name.trim().toLowerCase() === editedName);
            if (isDuplicate) {
              setShowDuplicateModal(true);
              return;
            }
          }
          try {
            setSavingRecipeId(id);
            const recipe = recipes.find(r => r.id === id);
            const updatedData = { name: recipe.name, price: parseFloat(recipe.price) || 0, servings: parseFloat(recipe.servings) || 1 };
            const result = await RecipesService.updateRecipe(id, updatedData, UnitMappingService.getBusinessId());
            setRecipes((prev) => prev.map((rec) => rec.id === id ? { ...rec, ...result } : rec));
            debugLog(' Recipe updated successfully');
            
            //  Navigate back to TSR if we came from there
            const pendingTSRData = localStorage.getItem('pendingTSRData');
            const pendingWastageData = localStorage.getItem('pendingWastageData');
            const pendingMenuManagementData = localStorage.getItem('pendingMenuManagementData');
            
            if (pendingTSRData) {
              debugLog('🔙 Recipe saved! Navigating back to TSR...');
              // Small delay to show success before navigating
              setTimeout(() => {
                goTo && goTo('todays-sales-report');
              }, 500);
            } else if (pendingWastageData) {
              debugLog('🔙 Recipe saved! Navigating back to Wastage...');
              setTimeout(() => {
                goTo && goTo('stockOutUsage');
              }, 500);
            } else if (pendingMenuManagementData) {
              debugLog('🔙 Recipe saved! Navigating back to Menu Management...');
              
              // Mark the removed ingredients as resolved now that recipe is saved
              try {
                const data = JSON.parse(pendingMenuManagementData);
                if (data.pendingResolve && Array.isArray(data.pendingResolve)) {
                  debugLog(' Marking ingredients as resolved:', data.pendingResolve);
                  
                  // Update the stored data to include newly resolved ingredients
                  const updatedResolved = [...(data.resolvedIngredients || []), ...data.pendingResolve];
                  data.resolvedIngredients = updatedResolved;
                  delete data.pendingResolve; // No longer pending
                  
                  // Update localStorage with the resolved ingredients
                  localStorage.setItem('pendingMenuManagementData', JSON.stringify(data));
                  debugLog('📝 Updated resolved ingredients:', updatedResolved);
                }
              } catch (err) {
                console.error('Error updating resolved ingredients:', err);
                showToast('Failed to update resolved ingredients');
              }

              setTimeout(() => {
                goTo && goTo('qr-menu-management');
              }, 500);
            }
          } catch (error) {
            console.error('Failed to update recipe:', error);
            showToast('Failed To Save Recipe Changes', 'error');
            try {
              const recipesData = await RecipesService.getRecipes(UnitMappingService.getBusinessId());
              setRecipes(applyLocalReviewDrafts(recipesData));
            } catch (reloadError) {
              console.error('Failed to reload recipes:', reloadError);
            }
          } finally {
            setSavingRecipeId(null);
          }
        };

        const toggleEditMode = (id, shouldRevert = false) => {
          setRecipes((prev) => prev.map((rec) => {
            if (rec.id === id) {
              const nextEditing = !rec.editing;
              if (nextEditing) {
                editingOriginalsRef.current[id] = {
                  name: rec.name,
                  price: rec.price,
                  servings: rec.servings,
                  category: rec.category,
                  master_category: rec.master_category,
                  status: rec.status
                };
                return { ...rec, editing: nextEditing };
              } else {
                const original = editingOriginalsRef.current[id];
                delete editingOriginalsRef.current[id];
                if (shouldRevert && original) {
                  return { ...rec, ...original, editing: nextEditing };
                }
                return { ...rec, editing: nextEditing };
              }
            }
            return rec;
          }));
        };
        return (
          <div key={r.id} className={`recipe-card${r.status === 'draft' ? ' recipe-card--review' : ''}${isEditing ? ' recipe-card--editing' : ''}`}>
            {!isEditing && (
              <FontAwesomeIcon
                icon={faPenToSquare}
                onClick={(e) => { e.stopPropagation(); toggleEditMode(r.id); }}
                className="edit-icon"
              />
            )}
            {isEditing ? (
              <div className="recipe-card-edit-container" style={{ textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                  <h4 style={{ textAlign: 'left', margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>Edit Item</h4>
                  <FontAwesomeIcon
                    icon={faPenToSquare}
                    onClick={(e) => { e.stopPropagation(); toggleEditMode(r.id, true); }}
                    style={{ fontSize: 16, cursor: 'pointer', color: '#000', flexShrink: 0 }}
                  />
                </div>
                <label className="form-label" style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: 7, marginTop: 7 }}>Item Name</label>
                <input
                  type="text"
                  value={r.name}
                  onChange={(e) => handleUpdateRecipeField(r.id, 'name', e.target.value)}
                  className="boxed-input itemmap-recipe-name-input"
                  style={{ fontSize: 16, marginBottom: 8 }}
                />
                <div style={{ display: 'flex', gap: '10px', marginBottom: 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label className="form-label" style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: 7 }}>Servings</label>
                    <input
                      type="number"
                      value={r.servings}
                      onChange={(e) => handleUpdateRecipeField(r.id, 'servings', parseInt(e.target.value))}
                      className="boxed-input itemmap-recipe-servings-input"
                      style={{ fontSize: 14, marginBottom: 0 }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label className="form-label" style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: 7 }}>Price (₹)</label>
                    <input
                      type="number"
                      value={r.price}
                      onChange={(e) => handleUpdateRecipeField(r.id, 'price', parseInt(e.target.value))}
                      className="boxed-input itemmap-recipe-price-input"
                      style={{ fontSize: 14, marginBottom: 0 }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: 16 }}>
                  <button
                    onClick={async (e) => { e.stopPropagation(); await deleteRecipe(r.id); }}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      background: '#EBEBEB',
                      color: '#000',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                  <button
                    onClick={async (e) => { e.stopPropagation(); await saveRecipeChanges(r.id); toggleEditMode(r.id); }}
                    disabled={savingRecipeId === r.id}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      background: savingRecipeId === r.id ? '#666' : '#000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: savingRecipeId === r.id ? 'not-allowed' : 'pointer',
                      opacity: savingRecipeId === r.id ? 0.7 : 1
                    }}
                  >
                    {savingRecipeId === r.id ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => {
                  setSelectedRecipe(withImageMeta(r));
                }}
              >
                <div className="card-title">{formatSummaryTitle(r.name)}</div>
                <div className="recipe-meta">
                  {r.servings} servings ・ {r.ingredientscount || r.ingredientsCount || 0} ingredients
                </div>
                {isPendingAddedFromSales(r) && (
                  <div className="recipe-added-from-sales">{addedFromSalesLabel(r.added_from_sales)}</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                  <div className={`recipe-status ${r.status === 'confirmed' ? 'confirmed' : r.status === 'draft' ? 'review' : 'pending'}`}>
                    {r.status === 'confirmed' ? (
                      <>
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '4px' }} />
                        Confirmed
                      </>
                    ) : r.status === 'draft' ? (
                      <>Under Review</>
                    ) : 'Status'}
                  </div>
                  <div 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      toggleEditMode(r.id); 
                    }}
                    style={{
                      color: '#000',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      transition: 'background 0.2s'
                    }}
                    >
                    ₹{r.price || 0}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* SP9 (A4): only ~30 cards render up front; scrolling to the sentinel loads more. */}
      {filteredRecipes.length > visibleCardCount && (
        <div ref={cardSentinelRef} style={{ height: 1 }} aria-hidden="true" />
      )}

      {recipes.length > 0 && (() => {
        // Spec D4: a dish added by Sales Name Check does not hold back Finish until its recipe is confirmed.
        const countable = recipes.filter(r => !isPendingAddedFromSales(r));
        const addedPending = recipes.length - countable.length;
        const total = countable.length;
        const confirmed = countable.filter(r => r.status === 'confirmed').length;
        const allConfirmed = total > 0 && confirmed === total;

        const handleFinishClick = async () => {
          if (!allConfirmed) return;
          try {
            const token = localStorage.getItem('token');
            const businessId = UnitMappingService.getBusinessId();
            const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';
            const res = await fetch(`${apiUrl}/master-categories/unmapped?businessId=${businessId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'X-Business-ID': businessId
              }
            });
            const data = await res.json();
            if (data.success && data.count > 0) {
              // Intercept: show category mapping gate
              setShowCategoryGate(true);
              return;
            }
          } catch (err) {
            console.warn('Unmapped check failed, proceeding:', err.message);
          }
          if (isOnboarding) {
            goTo && goTo('ingredient-reconciliation');
          } else {
            goTo && goTo('dashboard');
          }
        };

        return (
          <div style={{ marginTop: 16 }}>
            <div style={{ textAlign: 'center', color: '#666', fontSize: 12, marginBottom: 8 }}>
              {confirmed}/{total} items confirmed
              {addedPending > 0 && (
                <div style={{ color: '#ff9100', marginTop: 2 }}>
                  {addedPending} added from sales {addedPending === 1 ? 'is' : 'are'} not counted until confirmed
                </div>
              )}
            </div>
            <button
              disabled={!allConfirmed}
              onClick={handleFinishClick}
              style={{
                width: '100%',
                padding: 14,
                marginBottom:'4rem',
                background: allConfirmed ? '#111' : '#ccc',
                color: allConfirmed ? '#fff' : '#666',
                fontWeight: 'bold',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: allConfirmed ? 'pointer' : 'not-allowed'
              }}
            >
              Finish
            </button>
          </div>
        );
      })()}

      {/* Category Mapping Gate (onboarding intercept) */}
      {showCategoryGate && (
        <MenuOrganization
          isOpen={true}
          gateMode={true}
          onClose={() => {
            setShowCategoryGate(false);
            if (isOnboarding) {
              goTo && goTo('ingredient-reconciliation');
            }
          }}
          onComplete={() => {
            setShowCategoryGate(false);
            if (isOnboarding) {
              goTo && goTo('ingredient-reconciliation');
            } else {
              goTo && goTo('dashboard');
            }
          }}
        />
      )}

      {showDuplicateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 20000
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '0.5rem',
            maxWidth: '400px',
            width: '90%',
            padding: '28px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            textAlign: 'center'
          }}>
            <h3 style={{ 
              margin: '0 0 12px 0', 
              fontSize: '22px', 
              fontWeight: '700',
              color: '#111'
            }}>Duplicate Item Name</h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '15px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              Duplicate item name found please verify.
            </p>
            <button
              onClick={() => setShowDuplicateModal(false)}
              style={{
                width: '100%',
                padding: '12px 20px',
                fontSize: '15px',
                fontWeight: '600',
                background: '#000000',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Menu Options Modal */}
      {showMenuOptionsModal && (
        <div className="itemmap-modal-overlay">
          <div className="itemmap-modal-card" style={{ maxWidth: 400, position: 'relative' }}>
            <div className="itemmap-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: '#000', fontSize: '18px' }}>Add Item</h3>
              <button 
                type="button" 
                onClick={() => setShowMenuOptionsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#000',
                  padding: '0 4px',
                  lineHeight: '1',
                  fontWeight: '300'
                }}
              >
                ×
              </button>
            </div>

            <div>
              <p style={{ fontSize: 14, color: '#666', marginBottom: 24, textAlign: 'left' }}>
                Choose how you want to add your items
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                {/* Add Manually Button */}
                <button
                  onClick={() => {
                    setShowMenuOptionsModal(false);
                    setShowNewRecipeForm(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    background: '#fff',
                    color: '#000',
                    border: '2px solid #000',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12
                  }}
                >
                  <span>Add Manually</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu Item Selector */}
      {showMenuSelector && (
        <MenuItemSelector
          onSelectMenuItem={async (newRecipe) => {
            try {
              const refreshedRecipes = await RecipesService.getRecipes();
              setRecipes(applyLocalReviewDrafts(refreshedRecipes));
              setShowMenuSelector(false);
              showToast(`Recipe "${newRecipe.name}" created successfully with pre-mapped ingredients!`, 'success');
            } catch (error) {
              console.error('Failed to refresh recipes:', error);
              setShowMenuSelector(false);
            }
          }}
          onClose={() => setShowMenuSelector(false)}
        />
      )}

      {/* ===== SYNC FROM RECIPE LIBRARY — Selection Modal ===== */}
      {showSyncModal && (
        <div className="sync-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !syncLoading) setShowSyncModal(false); }}>
          <div className="sync-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="sync-modal-header">
              <h3 style={{ margin: 0, color: '#000', fontSize: '18px', fontWeight: 700 }}>Sync</h3>
              <button className="sync-modal-close" onClick={() => setShowSyncModal(false)} aria-label="Close">×</button>
            </div>

            {syncLoading ? (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <div className="sync-spinner"></div>
                <div style={{ marginTop: '16px', color: '#666', fontSize: '14px' }}>Checking recipe library...</div>
              </div>
            ) : (
              <>
                <div style={{ padding: '12px 20px 8px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, color: '#333' }}>
                    <input
                      type="checkbox"
                      checked={syncMatches.length > 0 && Object.keys(syncSelected).filter(k => syncSelected[k]).length === syncMatches.length}
                      onChange={(e) => handleSyncSelectAll(e.target.checked)}
                      className="sync-checkbox"
                    />
                    Select All
                  </label>
                  <span style={{ fontSize: '13px', color: '#888' }}>{syncMatches.length} matches found</span>
                </div>

                <div className="sync-items-list">
                  {syncMatches.map((match) => (
                    <label key={match.menuItemId} className={`sync-item-row ${syncSelected[match.menuItemId] ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={!!syncSelected[match.menuItemId]}
                        onChange={(e) => setSyncSelected(prev => ({ ...prev, [match.menuItemId]: e.target.checked }))}
                        className="sync-checkbox"
                      />
                      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <div style={{ fontWeight: 600, fontSize: '15px', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>{match.menuItemName}</div>
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '2px', textAlign: 'left' }}>
                          Library: {match.libraryIngredientCount} ingredients
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        {match.hasExistingIngredients ? (
                          <span className="sync-badge sync-badge--has">Has {match.existingIngredientCount}</span>
                        ) : (
                          <span className="sync-badge sync-badge--empty">Empty</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>

                <div className="sync-modal-footer">
                  <button
                    className="sync-action-btn"
                    onClick={handleSyncProceed}
                    disabled={Object.keys(syncSelected).filter(k => syncSelected[k]).length === 0}
                  >
                    Confirm
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== SYNC — Conflict Resolution Modal ===== */}
      {showConflictModal && (
        <div className="sync-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowConflictModal(false); }}>
          <div className="sync-modal-card sync-conflict-card" onClick={(e) => e.stopPropagation()}>
            <div className="sync-modal-header">
              <h3 style={{ margin: 0, color: '#000', fontSize: '18px', fontWeight: 700 }}>Ingredient Conflict</h3>
              <button className="sync-modal-close" onClick={() => setShowConflictModal(false)} aria-label="Close">×</button>
            </div>

            <div style={{ padding: '20px' }}>
              <div style={{ fontSize: '14px', color: '#555', marginBottom: '20px', lineHeight: '1.5' }}>
                <strong>{syncConflictItems.length}</strong> of your selected items already have ingredients. How do you want to handle them?
              </div>

              <button
                className="sync-strategy-btn"
                onClick={() => {
                  const selectedIds = Object.keys(syncSelected).filter(k => syncSelected[k]).map(Number);
                  const allSelected = syncMatches.filter(m => selectedIds.includes(m.menuItemId));
                  executeBulkSync(allSelected, 'replace');
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '22px' }}>🔄</span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: '#111' }}>Replace</div>
                    <div style={{ fontSize: '12px', color: '#777', marginTop: '2px' }}>Remove existing ingredients and pull fresh from library</div>
                  </div>
                </div>
              </button>

              <button
                className="sync-strategy-btn"
                style={{ marginTop: '10px' }}
                onClick={() => {
                  const selectedIds = Object.keys(syncSelected).filter(k => syncSelected[k]).map(Number);
                  const allSelected = syncMatches.filter(m => selectedIds.includes(m.menuItemId));
                  executeBulkSync(allSelected, 'merge');
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '22px' }}>🔀</span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: '#111' }}>Merge</div>
                    <div style={{ fontSize: '12px', color: '#777', marginTop: '2px' }}>Keep existing ingredients, add missing ones from library</div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SYNC — Processing Overlay ===== */}
      {syncProcessing && (
        <div className="sync-modal-overlay" style={{ zIndex: 10001 }}>
          <div className="sync-processing-card">
            <div className="sync-spinner" style={{ width: '48px', height: '48px', margin: '0 auto 20px' }}></div>
            <div style={{ fontWeight: 700, fontSize: '18px', color: '#000', marginBottom: '12px' }}>Syncing Ingredients...</div>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>{syncProgress.message}</div>
            {syncProgress.total > 0 && (
              <div style={{ width: '100%' }}>
                <div style={{ width: '100%', height: '6px', backgroundColor: '#e0e0e0', borderRadius: '3px', overflow: 'hidden', marginBottom: '8px' }}>
                  <div style={{
                    width: `${Math.min((syncProgress.current / syncProgress.total) * 100, 100)}%`,
                    height: '100%',
                    backgroundColor: '#000',
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
                <div style={{ fontSize: '12px', color: '#888', textAlign: 'center' }}>
                  {syncProgress.current}/{syncProgress.total} items
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {/* 🆕 Multiplier Editor Modal */}
      {showMultiplierEditor && (
        <div className="itemmap-multiplier-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
            <h2>🍽️ Customize Portion Multipliers</h2>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              We detected variant groups in your menu. Review and adjust the portion multipliers before creating recipes.
              The <strong>base variant</strong> (×1.0) is highlighted in green.
            </p>

            {pendingVariantGroups.validGroups && pendingVariantGroups.validGroups.map((group, groupIdx) => (
              <div key={groupIdx} style={{ marginBottom: '30px', padding: '15px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                <h3 style={{ marginBottom: '15px', color: '#333' }}>📋 {group.baseName}</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e0e0e0' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Variant Name</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ccc' }}>Portion Size</th>
                      <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ccc', width: '150px' }}>Multiplier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.variants.map((variant, variantIdx) => {
                      const keyword = variant.detectedPortionSize.toLowerCase();
                      const isBase = variant.isBaseVariant;
                      return (
                        <tr key={variantIdx} style={{ backgroundColor: isBase ? '#d4edda' : 'white' }}>
                          <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{variant.name}</td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>
                            {variant.detectedPortionSize}
                            {isBase && <span style={{ marginLeft: '8px', color: '#28a745', fontWeight: 'bold' }}>⭐ BASE</span>}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'center' }}>
                            <input
                              type="number"
                              step="0.1"
                              min="0.1"
                              max="10"
                              value={editableMultipliers[keyword] || 1.0}
                              onChange={(e) => {
                                const newValue = parseFloat(e.target.value) || 1.0;
                                setEditableMultipliers(prev => ({
                                  ...prev,
                                  [keyword]: newValue
                                }));
                              }}
                              style={{
                                width: '80px',
                                padding: '5px',
                                textAlign: 'center',
                                border: isBase ? '2px solid #28a745' : '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                              disabled={isBase}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}

            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowMultiplierEditor(false);
                  setPendingVariantGroups([]);
                  setEditableMultipliers({});
                }}
                className="btn btn--secondary"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (isSavingMultipliers) return;
                  
                  // Validation
                  for (const [key, value] of Object.entries(editableMultipliers)) {
                    const numVal = parseFloat(value);
                    if (isNaN(numVal) || numVal <= 0 || numVal > 10) {
                      showToast(`Invalid multiplier for ${key}: Must be between 0.1 and 10`, 'error');
                      return;
                    }
                  }

                  setIsSavingMultipliers(true);
                  debugLog('🔘 Save & Create Recipes button clicked');
                  debugLog('📊 Current editableMultipliers:', editableMultipliers);
                  debugLog('📋 Current pendingVariantGroups:', pendingVariantGroups);
                  
                  try {
                    debugLog('💾 Saving custom multipliers...');
                    await saveCustomMultipliers(editableMultipliers);
                    debugLog(' Multipliers saved successfully');
                    
                    debugLog('🍽️ Processing variant groups...');
                    // Pass current state explicitly to robust processing function
                    await processVariantGroupsWithMultipliers(
                       pendingVariantGroups.validGroups, 
                       pendingVariantGroups.standalone, 
                       editableMultipliers
                    );
                    debugLog(' Variant groups processed successfully');
                    setShowMultiplierEditor(false);
                  } catch (error) {
                    console.error('❌ Error saving multipliers or processing variants:', error);
                    console.error('❌ Error stack:', error.stack);
                    showToast('❌ Error processing recipes: ' + (error.message || 'Unknown error'), 'error');
                  } finally {
                    setIsSavingMultipliers(false);
                  }
                }}
                className="btn btn--primary"
                disabled={isSavingMultipliers}
              >
                {isSavingMultipliers ? 'Processing...' : 'Save & Create Recipes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔄 Recipe Creation Loading Overlay */}
      {isCreatingRecipes && (
        <div
          className="itemmap-menuscan-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target.className === 'itemmap-menuscan-overlay') {
              // Prevent closing during recipe creation
            }
          }}
        >
          <div
            className="itemmap-recipe-creation-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center', padding: window.innerWidth <= 640 ? '10px 0' : '20px 0' }}>
              <div style={{ 
                width: '50px', 
                height: '50px', 
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #000',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 24px'
              }}></div>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
              
              <div style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 8, color: '#000' }}>
                Creating Recipes...
              </div>
              
              {recipeCreationProgress.message && (
                <div style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
                  {recipeCreationProgress.message}
                </div>
              )}
              
              {recipeCreationProgress.total > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ 
                    width: '100%', 
                    height: 8, 
                    background: '#ddd', 
                    borderRadius: 4, 
                    overflow: 'hidden',
                    marginBottom: 8
                  }}>
                    <div style={{ 
                      width: `${(recipeCreationProgress.current / recipeCreationProgress.total) * 100}%`, 
                      height: '100%', 
                      background: 'linear-gradient(90deg, #000, #666)',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <div style={{ fontSize: 14, color: '#666' }}>
                    {recipeCreationProgress.current} of {recipeCreationProgress.total} recipes processed
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            width: 'min(420px, 90vw)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            padding: '24px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '19px',
              fontWeight: '600',
              marginBottom: '12px',
              color: '#1f2937'
            }}>
              Are you sure you want to delete this item?
            </div>
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              Note: If this item has sales history, it will be deactivated instead of permanently deleted.
            </div>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'stretch',
              width: '100%'
            }}>
              <button 
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setRecipeToDelete(null);
                }}
              >
                Cancel
              </button>
              <button 
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  backgroundColor: '#000000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                onClick={handleConfirmDelete}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      

      {showDeleteSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            width: 'min(380px, 90vw)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            padding: '24px',
            textAlign: 'center'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                border: '3px solid #22c55e',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Check size={36} color="#22c55e" strokeWidth={3} />
              </div>
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '16px',
              color: '#1f2937'
            }}>
              {deleteModalMessage}
            </div>
            <button 
              style={{
                padding: '10px 24px',
                backgroundColor: '#000000',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              onClick={() => setShowDeleteSuccessModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
      
      {duplicateRecipeError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2147483010
        }}>
          <div style={{
            width: 'min(380px, 90vw)',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            padding: '24px',
            textAlign: 'center'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '36px', color: '#eab308' }} />
            </div>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#1f2937'
            }}>
              Item Already Exists
            </div>
            <div style={{
              fontSize: '14px',
              color: '#4b5563',
              marginBottom: '20px',
              lineHeight: '1.4'
            }}>
              The item "{duplicateRecipeError.name}" already exists. Please choose a different name.
            </div>
            <button 
              style={{
                padding: '10px 24px',
                backgroundColor: '#000000',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '120px'
              }}
              onClick={() => setDuplicateRecipeError(null)}
            >
              OK
            </button>
          </div>
        </div>
      )}
      <button
        type="button"
        className={`itemmap-scroll-top-btn ${showScrollTop ? 'visible' : ''}`}
        onClick={() => scrollToTop()}
        aria-label="Scroll to top"
        title="Scroll to top"
      >
        <FontAwesomeIcon icon={faArrowUp} />
      </button>

      {/* SP9: the row-level cross-family conversion popup ("Popup 1") is retired — unit
          changes on recipe rows always apply with the number unchanged, and family
          mismatches surface as an inline note + the save gate. CrossFamilyConversionModal
          still serves the inventory-item pencil edit, where that decision belongs. */}

      {/* Popup 2: Impact Verification (read-only) */}
      {showUnitChangeModal && unitChangePreview && (
        <UnitChangeConfirmationModal
          isOpen={showUnitChangeModal}
          previewData={unitChangePreview}
          onConfirm={handleConfirmUnitChange}
          onCancel={() => {
            setShowUnitChangeModal(false);
            setPendingUnitChange(null);
            setUnitChangePreview(null);
          }}
          isLoading={isApplyingUnitChange}
        />
      )}

      {/* New CrossFamilyConversionModal for Strict 5 Unit Enforcement */}
      {/* DISABLED IN ITEMMAP: Cross-family conversions should be done in InventoryOverview */}
      {/* Recipe ingredients can freely change units - conversion happens at inventory level */}
      {false && showNewConversionModal && newConversionModalProps && (
        <CrossFamilyConversionModal
          isOpen={showNewConversionModal}
          itemId={newConversionModalProps.itemId}
          itemName={newConversionModalProps.itemName}
          currentStock={newConversionModalProps.currentStock}
          currentUnit={newConversionModalProps.currentUnit}
          newUnit={newConversionModalProps.newUnit}
          newUnitId={newConversionModalProps.newUnitId}
          onConfirm={newConversionModalProps.onConfirm}
          onCancel={newConversionModalProps.onCancel}
        />
      )}
    </div>
  );
};

// Lightweight suggestion item component
const OCRLibrarySuggestion = ({ name, onImport }) => (
  <div className="modal-list-item">
    <div style={{ fontWeight: 600 }}>{name}</div>
    <button onClick={() => onImport(name)} className="btn btn--primary">Use Suggested</button>
  </div>
);

export default RecipeListScreen;
